import { useState, useEffect, useRef } from 'react';
/* eslint-disable react-hooks/exhaustive-deps -- data loaders and scanner lifecycle use explicit state keys */
import { supabase } from '../../lib/supabase';
import { useSucursal } from '../../utils/sucursalContext';
import AdminLayout from '../../components/layout/AdminLayout';
import EvaluacionesAgendaModal from './EvaluacionesAgendaModal';
import EvaluacionInicialPanel from './EvaluacionInicialPanel';
import { useAuth } from '../../contexts/AuthContext';
import Rutinas from '../rutinas/page';

const SUPABASE_URL = import.meta.env.VITE_PUBLIC_SUPABASE_URL as string;
const SUPABASE_ANON_KEY = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY as string;

type Usuario = {
  id: string;
  nombre: string;
  email: string;
  tipo_membresia: string;
  estado: 'activo' | 'inactivo' | 'vencido';
  fecha_nacimiento?: string | null;
  fecha_cambio_rutina?: string | null;
  profesor_admin_id?: string | null;
  declaracion_salud?: {
    estado?: string;
    detalle?: string;
    restricciones?: string;
  } | null;
  contacto_emergencia?: {
    nombre?: string;
    telefono?: string;
  } | null;
  avatar?: string;
};

type ReglamentoEstado = {
  aceptado: boolean;
  version: number | null;
  fecha: string | null;
};

type FitMaoExtracted = {
  fecha_medicion: string | null;
  nombre: string | null;
  peso: number | null;
  grasa_corporal: number | null;
  masa_muscular: number | null;
  imc: number | null;
  pbf: number | null;
  grasa_visceral: number | null;
  tmb: number | null;
};

type CoachOption = {
  id: string;
  nombre: string;
  email: string;
  role: string;
  activo: boolean;
};

type ProgresoData = {
  id: string;
  usuario_id: string;
  fecha: string;
  peso: number;
  grasa_corporal: number;
  masa_muscular: number;
  notas: string;
};

type ProgresoReporte = {
  id: string;
  usuario_id: string;
  progreso_id: string | null;
  fecha_medicion: string;
  fuente: 'fitmao' | 'manual' | 'otro' | string;
  fuente_url: string;
  mime_type: string | null;
  file_size: number | null;
  resumen: Record<string, unknown> | null;
  meta: Record<string, unknown> | null;
  created_at: string;
};

type CorreccionEvaluacion = {
  id: string;
  motivo: string;
  valores_antes: Record<string, unknown>;
  valores_despues: Record<string, unknown>;
  created_at: string;
};

type EntrenamientoEjercicio = {
  id: string;
  entrenamiento_id: string;
  ejercicio_id: number | null;
  nombre: string;
  categoria: string | null;
  series: number | null;
  repeticiones: number | null;
  descanso: number | null;
  peso_plan: number | null;
  peso_usado: number | null;
  completado: boolean;
};

type Entrenamiento = {
  id: string;
  usuario_id: string;
  fecha: string; // YYYY-MM-DD
  sesion_id: string;
  rutina_id: string | null;
  entrenamiento_ejercicios?: EntrenamientoEjercicio[];
};

type RutinaPersonalizada = {
  id: string;
  nombre: string;
  descripcion: string | null;
  activa: boolean;
  ejercicios: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
};

type RutinaAsignada = {
  id: number;
  nombre: string;
  descripcion: string | null;
  activa: boolean;
  creada_por_usuario: boolean;
  updated_at: string;
};

type RutinaDisponible = {
  id: string;
  nombre: string;
  descripcion: string | null;
};

type RutinaHistorial = {
  id: string;
  usuario_id: string;
  rutina_id: string;
  rutina_origen_id: string | null;
  accion: 'asignada' | 'desasignada' | 'reemplazada';
  admin_auth_id: string | null;
  metadata: Record<string, unknown> | null;
  created_at: string;
};

type NutricionCalculo = {
  id: string;
  usuario_id: string;
  sexo: 'hombre' | 'mujer';
  peso_kg: number;
  altura_cm: number;
  edad: number;
  actividad: 'sedentario' | 'ligero' | 'moderado' | 'activo' | 'muy_activo';
  factor_actividad: number;
  tmb: number;
  get_kcal: number;
  formula?: 'harris_benedict' | 'mifflin_st_jeor';
  ajuste_porcentaje?: number;
  calorias_objetivo?: number | null;
  proteinas_g?: number | null;
  grasas_g?: number | null;
  carbohidratos_g?: number | null;
  created_at: string;
};
type EvaluacionProxima = {
  id: string;
  usuario_id: string;
  fecha_inicio: string;
  estado: 'solicitada' | 'confirmada';
  usuario: { nombre: string; email: string } | null;
};
type RecomendacionRutinaIA = {
  id: string;
  resumen: string;
  motivo: Record<string, unknown> | null;
  estado: 'pendiente' | 'aprobada' | 'rechazada' | 'cancelada';
  created_at: string;
  revisada_at: string | null;
  comentario_revision: string | null;
  acoma_execution_id: string | null;
  ultima_edicion_at: string | null;
  cantidad_ediciones: number;
  evaluacion_inicial_id: string | null;
  rutina_propuesta: { id: string; nombre: string } | null;
};
type EvaluacionInicialAcoma = {
  id: string;
  estado: 'completa' | 'requiere_revision';
  fecha_evaluacion: string;
};
type RutinaPropuestaSnapshot = {
  nombre?: string | null;
  descripcion?: string | null;
  dias_semana?: string[] | null;
  ejercicios?: {
    sesiones?: Array<{
      id?: string;
      codigo?: string;
      nombre?: string;
      ejercicios?: Array<{
        ejercicioId?: number | string;
        nombre?: string;
        series?: number;
        repeticiones?: number | string;
        descanso?: number;
        descansoSegundos?: number;
        rir?: number | null;
        tempo?: string | null;
      }>;
    }>;
  } | null;
};
type RevisionRecomendacionIA = {
  id: string;
  propuesta_antes: RutinaPropuestaSnapshot;
  propuesta_despues: RutinaPropuestaSnapshot;
  comentario: string | null;
  created_at: string;
  admin: { nombre: string; email: string } | null;
};
type PlanReferencia = { nombre: string; max_sesiones_periodo: number | null; duracion_dias: number | null };
type FamiliaDisponible = { id: string; nombre: string; descripcion: string | null; etapas: Array<{ id: string }> };
type FamiliaAsignada = {
  id: string; estado: string; etapa_iniciada_at: string;
  familia_nombre_snapshot: string | null; familia_descripcion_snapshot: string | null;
  avance_automatico_snapshot: boolean | null; dias_aprobacion_snapshot: number | null;
  familia: { id: string; nombre: string; dias_aprobacion: number } | null;
  etapa: { id: string; orden: number; duracion_dias: number; rutina: { nombre: string } | null } | null;
  etapa_snapshot: { id: string; orden: number; duracion_dias: number; nombre_rutina_snapshot: string } | null;
  etapas_snapshot: Array<{
    id: string; orden: number; duracion_dias: number; sesiones_minimas: number | null; nombre_rutina_snapshot: string;
    rutina_copia: { id: string; nombre: string; activa: boolean } | null;
  }>;
};
type FamiliaAsignadaEditDraft = {
  nombre: string; descripcion: string; avanceAutomatico: boolean; diasAprobacion: number;
  etapas: Array<{ id: string; orden: number; duracionDias: number; sesionesMinimas: string; rutinaId: string; rutinaNombre: string }>;
};
type CambioFamilia = {
  id: string; motivo: string; fecha_limite: string; estado: string;
  destino: { orden: number; rutina: { nombre: string } | null } | null;
  destino_snapshot: { orden: number; nombre_rutina_snapshot: string } | null;
};

const HISTORIAL_IA_POR_PAGINA = 5;
const RECOMENDACION_IA_SELECT = 'id,resumen,motivo,estado,created_at,revisada_at,comentario_revision,acoma_execution_id,ultima_edicion_at,cantidad_ediciones,evaluacion_inicial_id,rutina_propuesta:rutinas!rutina_recomendaciones_ia_rutina_propuesta_id_fkey(id,nombre)';

function RoutineSnapshotPanel({ title, snapshot, tone }: { title: string; snapshot: RutinaPropuestaSnapshot; tone: 'before' | 'after' }) {
  const sessions = snapshot.ejercicios?.sesiones || [];
  const exerciseCount = sessions.reduce((total, session) => total + (session.ejercicios?.length || 0), 0);
  const toneClass = tone === 'before' ? 'border-slate-200 bg-slate-50' : 'border-emerald-200 bg-emerald-50';
  return (
    <div className={`rounded-xl border p-4 ${toneClass}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-wide font-bold text-gray-500">{title}</p>
          <p className="font-semibold text-gray-900 mt-1">{snapshot.nombre || 'Rutina sin nombre'}</p>
        </div>
        <span className="text-xs font-semibold text-gray-600 whitespace-nowrap">{sessions.length} sesiones · {exerciseCount} ejercicios</span>
      </div>
      {snapshot.descripcion && <p className="text-xs text-gray-600 mt-2">{snapshot.descripcion}</p>}
      <div className="mt-3 space-y-2">
        {sessions.map((session, sessionIndex) => (
          <div key={`${session.id || session.codigo || sessionIndex}-${sessionIndex}`} className="rounded-lg border border-white/80 bg-white/80 p-3">
            <p className="text-sm font-semibold text-gray-800">{session.nombre || `Sesión ${sessionIndex + 1}`}</p>
            <div className="mt-2 space-y-1.5">
              {(session.ejercicios || []).map((exercise, exerciseIndex) => (
                <div key={`${exercise.ejercicioId || exercise.nombre || exerciseIndex}-${exerciseIndex}`} className="text-xs text-gray-600 flex flex-wrap justify-between gap-x-3 gap-y-1">
                  <span className="font-medium text-gray-800">{exercise.nombre || `Ejercicio ${exerciseIndex + 1}`}</span>
                  <span>{exercise.series || '-'} series · {exercise.repeticiones || '-'} reps · {exercise.descanso ?? exercise.descansoSegundos ?? '-'} s{exercise.rir !== null && exercise.rir !== undefined ? ` · RIR ${exercise.rir}` : ''}{exercise.tempo ? ` · ${exercise.tempo}` : ''}</span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function ProgresoPage() {
  const { profile } = useAuth();
  const isStaff = profile?.role === 'staff';
  const isInternalCoach = isStaff && profile?.tipoStaff === 'coach_interno';
  const isExternalCoach = isStaff && profile?.tipoStaff === 'coach_externo';
  const isCoach = isStaff && ['coach_interno', 'coach_externo'].includes(profile?.tipoStaff || '');
  const canViewInitialEvaluation = !isStaff || ['coach_interno', 'coach_externo', 'nutricionista'].includes(profile?.tipoStaff || '');
  const { sucursalActiva } = useSucursal();
  const [usuarios, setUsuarios] = useState<Usuario[]>([]);
  const [planesReferencia, setPlanesReferencia] = useState<PlanReferencia[]>([]);
  const [selectedUsuario, setSelectedUsuario] = useState<Usuario | null>(null);
  const [progreso, setProgreso] = useState<ProgresoData[]>([]);
  const [reportes, setReportes] = useState<ProgresoReporte[]>([]);
  const [entrenamientos, setEntrenamientos] = useState<Entrenamiento[]>([]);
  const [rutinasPersonalizadas, setRutinasPersonalizadas] = useState<RutinaPersonalizada[]>([]);
  const [rutinasAsignadas, setRutinasAsignadas] = useState<RutinaAsignada[]>([]);
  const [rutinasDisponibles, setRutinasDisponibles] = useState<RutinaDisponible[]>([]);
  const [showRutinaAssignModal, setShowRutinaAssignModal] = useState(false);
  const [rutinaSeleccionadaId, setRutinaSeleccionadaId] = useState('');
  const [rutinaAssignSearch, setRutinaAssignSearch] = useState('');
  const [loadingRutinasDisponibles, setLoadingRutinasDisponibles] = useState(false);
  const [asignandoRutina, setAsignandoRutina] = useState(false);
  const [rutinasHistorial, setRutinasHistorial] = useState<RutinaHistorial[]>([]);
  const [familiasDisponibles, setFamiliasDisponibles] = useState<FamiliaDisponible[]>([]);
  const [familiaAsignada, setFamiliaAsignada] = useState<FamiliaAsignada | null>(null);
  const [cambiosFamilia, setCambiosFamilia] = useState<CambioFamilia[]>([]);
  const [familiaSeleccionadaId, setFamiliaSeleccionadaId] = useState('');
  const [procesandoFamilia, setProcesandoFamilia] = useState(false);
  const [editandoFamiliaAsignada, setEditandoFamiliaAsignada] = useState(false);
  const [familiaAsignadaDraft, setFamiliaAsignadaDraft] = useState<FamiliaAsignadaEditDraft | null>(null);
  const [loadingRutinasAsignadas, setLoadingRutinasAsignadas] = useState(false);
  const [loadingRutinasHistorial, setLoadingRutinasHistorial] = useState(false);
  const [rutinaNombreById, setRutinaNombreById] = useState<Record<string, string>>({});
  const [rutinaHistNombreById, setRutinaHistNombreById] = useState<Record<string, string>>({});
  const [loadingEntrenamientos, setLoadingEntrenamientos] = useState(false);
  const [loadingRutinasPersonalizadas, setLoadingRutinasPersonalizadas] = useState(false);
  const [loadingReportes, setLoadingReportes] = useState(false);
  const [expandedDay, setExpandedDay] = useState<string | null>(null);
  const [entrenamientosPage, setEntrenamientosPage] = useState(1);
  const ENTRENAMIENTOS_DIAS_POR_PAGINA = 7;
  const [, setLoading] = useState(true);
  const [evaluacionesProximas, setEvaluacionesProximas] = useState<EvaluacionProxima[]>([]);
  const [recomendacionesIA, setRecomendacionesIA] = useState<RecomendacionRutinaIA[]>([]);
  const [historialRecomendacionesIA, setHistorialRecomendacionesIA] = useState<RecomendacionRutinaIA[]>([]);
  const [loadingRecomendacionesIA, setLoadingRecomendacionesIA] = useState(false);
  const [loadingHistorialIA, setLoadingHistorialIA] = useState(false);
  const [historialIAExpandido, setHistorialIAExpandido] = useState(false);
  const [historialIAPagina, setHistorialIAPagina] = useState(1);
  const [historialIATotal, setHistorialIATotal] = useState(0);
  const [historialIASeleccionado, setHistorialIASeleccionado] = useState<RecomendacionRutinaIA | null>(null);
  const [revisionesIA, setRevisionesIA] = useState<RevisionRecomendacionIA[]>([]);
  const [loadingRevisionesIA, setLoadingRevisionesIA] = useState(false);
  const [resolviendoRecomendacionId, setResolviendoRecomendacionId] = useState<string | null>(null);
  const [showAcomaSuggestionModal, setShowAcomaSuggestionModal] = useState(false);
  const [generandoRecomendacionIA, setGenerandoRecomendacionIA] = useState(false);
  const [evaluacionInicialAcoma, setEvaluacionInicialAcoma] = useState<EvaluacionInicialAcoma | null>(null);
  const [showFamilySuggestionModal, setShowFamilySuggestionModal] = useState(false);
  const [requestingFamilySuggestion, setRequestingFamilySuggestion] = useState(false);
  const [familyForm, setFamilyForm] = useState({
    instrucciones: '', nivel: 'Intermedio', frecuencia: 3, duracion_minutos: 60, cantidad_etapas: 3,
  });
  const [acomaForm, setAcomaForm] = useState({
    tipo_sugerencia: 'nueva' as 'nueva' | 'renovacion',
    objetivo: '',
    nivel: 'intermedio',
    frecuencia_semanal: 3,
    duracion_minutos: 60,
    observaciones: '',
  });
  const [showModal, setShowModal] = useState(false);
  const [showEvaluacionesAgenda, setShowEvaluacionesAgenda] = useState(false);
  const [showUpcomingSummary, setShowUpcomingSummary] = useState(false);
  const [rutinaEmbedRequest, setRutinaEmbedRequest] = useState<{
    rutinaId: number | string;
    action: 'detail' | 'edit';
    recommendationId?: string;
  } | null>(null);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showQrScannerModal, setShowQrScannerModal] = useState(false);
  const [qrScanError, setQrScanError] = useState<string | null>(null);
  const [qrScanSuccess, setQrScanSuccess] = useState<string | null>(null);
  const [scanningQr, setScanningQr] = useState(false);
  const [readingQrImage, setReadingQrImage] = useState(false);
  const [importingReporte, setImportingReporte] = useState(false);
  const [extractingFitmao, setExtractingFitmao] = useState(false);
  const [openingReporteId, setOpeningReporteId] = useState<string | null>(null);
  const [correctionReport, setCorrectionReport] = useState<ProgresoReporte | null>(null);
  const [correctionHistory, setCorrectionHistory] = useState<CorreccionEvaluacion[]>([]);
  const [loadingCorrectionHistory, setLoadingCorrectionHistory] = useState(false);
  const [savingCorrection, setSavingCorrection] = useState(false);
  const [correctionForm, setCorrectionForm] = useState({
    peso: '',
    grasa_corporal: '',
    masa_muscular: '',
    imc: '',
    pbf: '',
    grasa_visceral: '',
    tmb: '',
    motivo: '',
  });
  const [showUserSelector, setShowUserSelector] = useState(false);
  const [showRutinaChangeModal, setShowRutinaChangeModal] = useState(false);
  const [showCoachModal, setShowCoachModal] = useState(false);
  const [reglamentoEstado, setReglamentoEstado] = useState<ReglamentoEstado | null>(null);
  const [savingCoach, setSavingCoach] = useState(false);
  const [coachOptions, setCoachOptions] = useState<CoachOption[]>([]);
  const [selectedCoachId, setSelectedCoachId] = useState<string>('');
  const [savingRutinaChange, setSavingRutinaChange] = useState(false);
  const [rutinaChangeDate, setRutinaChangeDate] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedEjercicioProgreso, setSelectedEjercicioProgreso] = useState<string>('todos');
  const [selectedRangeDays, setSelectedRangeDays] = useState<7 | 30 | 90>(30);
  const [reportesError, setReportesError] = useState<string | null>(null);
  const [reportesNotice, setReportesNotice] = useState<string | null>(null);
  const [uploadingPauta, setUploadingPauta] = useState(false);
  const [guardandoNutri, setGuardandoNutri] = useState(false);
  const [nutriHistorial, setNutriHistorial] = useState<NutricionCalculo[]>([]);
  const [loadingNutriHistorial, setLoadingNutriHistorial] = useState(false);
  const pautaFileInputRef = useRef<HTMLInputElement | null>(null);
  const deepLinkUsuarioHandledRef = useRef<string | null>(null);
  const qrVideoRef = useRef<HTMLVideoElement | null>(null);
  const qrStreamRef = useRef<MediaStream | null>(null);
  const qrRafRef = useRef<number | null>(null);
  const qrDetectorRef = useRef<any>(null);
  const qrImageInputRef = useRef<HTMLInputElement | null>(null);
  const [nutriSexo, setNutriSexo] = useState<'hombre' | 'mujer'>('hombre');
  const [nutriPesoKg, setNutriPesoKg] = useState('');
  const [nutriAlturaCm, setNutriAlturaCm] = useState('170');
  const [nutriEdad, setNutriEdad] = useState('30');
  const [nutriActividad, setNutriActividad] = useState<'sedentario' | 'ligero' | 'moderado' | 'activo' | 'muy_activo'>('moderado');
  const [nutriFormula, setNutriFormula] = useState<'harris_benedict' | 'mifflin_st_jeor'>('harris_benedict');
  const [nutriAjuste, setNutriAjuste] = useState(-15);

  const [formData, setFormData] = useState({
    fecha: new Date().toISOString().split('T')[0],
    peso: '',
    grasa_corporal: '',
    masa_muscular: '',
    notas: '',
  });

  const [importForm, setImportForm] = useState({
    source_url: '',
    fecha_medicion: new Date().toISOString().split('T')[0],
    peso: '',
    grasa_corporal: '',
    masa_muscular: '',
    imc: '',
    pbf: '',
    grasa_visceral: '',
    tmb: '',
    notas: '',
  });
  const [importPreviewReady, setImportPreviewReady] = useState(false);
  const [importIdentityConfirmed, setImportIdentityConfirmed] = useState(false);
  const [importSourceIdentity, setImportSourceIdentity] = useState<{ usuarioId: string; email: string; nombre: string }>({ usuarioId: '', email: '', nombre: '' });

  const parseISODateLocal = (iso: string) => new Date(`${iso}T00:00:00`);
  const getAgeYears = (isoDate?: string | null) => {
    if (!isoDate) return null;
    const birth = parseISODateLocal(isoDate);
    if (Number.isNaN(birth.getTime())) return null;
    const today = new Date();
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) age--;
    return age > 0 ? age : null;
  };
  const parseOptionalNumber = (value: string) => {
    const trimmed = value.trim();
    if (!trimmed) return null;
    const parsed = Number(trimmed.replace(',', '.'));
    return Number.isFinite(parsed) ? parsed : null;
  };

  const getValidAccessToken = async () => {
    const {
      data: { session },
    } = await supabase.auth.getSession();

    if (!session?.access_token) {
      throw new Error('Sesion expirada. Vuelve a iniciar sesion.');
    }

    const expiresAtMs = (session.expires_at || 0) * 1000;
    if (expiresAtMs > 0 && expiresAtMs - Date.now() < 60_000) {
      const { data: refreshed, error: refreshError } = await supabase.auth.refreshSession();
      if (refreshError || !refreshed.session?.access_token) {
        throw new Error('Sesion expirada. Vuelve a iniciar sesion.');
      }
      return refreshed.session.access_token;
    }

    return session.access_token;
  };

  const callEdgeFunction = async <T,>(functionName: string, payload: Record<string, unknown>): Promise<T> => {
    if (!SUPABASE_URL || !SUPABASE_ANON_KEY) {
      throw new Error('Configuracion de Supabase incompleta en frontend.');
    }

    const accessToken = await getValidAccessToken();
    const response = await fetch(`${SUPABASE_URL}/functions/v1/${functionName}`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        apikey: SUPABASE_ANON_KEY,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    const body = await response.json().catch(() => ({} as any));
    if (!response.ok) {
      if (body?.error && body?.details) {
        throw new Error(`${body.error}: ${body.details}`);
      }
      if (body?.error) {
        throw new Error(String(body.error));
      }
      throw new Error(`Edge Function error (${response.status})`);
    }

    return body as T;
  };

  const openUrlInNewTab = (url: string) => {
    const a = document.createElement('a');
    a.href = url;
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const openRutinaInlineModal = (rutinaId: number | string, action: 'detail' | 'edit', recommendationId?: string) => {
    setRutinaEmbedRequest({ rutinaId, action, recommendationId });
  };

  useEffect(() => {
    fetchUsuarios();
    void fetchCoachOptions();
  }, [sucursalActiva]);

  useEffect(() => {
    const usuarioId = new URLSearchParams(window.location.search).get('usuario');
    if (!usuarioId || usuarios.length === 0 || deepLinkUsuarioHandledRef.current === usuarioId) return;
    const usuario = usuarios.find((item) => item.id === usuarioId);
    if (!usuario) return;
    deepLinkUsuarioHandledRef.current = usuarioId;
    setSelectedUsuario(usuario);
  }, [usuarios]);

  useEffect(() => {
    const proposalId = new URLSearchParams(window.location.search).get('propuesta');
    if (!proposalId || !recomendacionesIA.some((item) => item.id === proposalId)) return;
    window.setTimeout(() => document.getElementById('recomendaciones-acoma')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
  }, [recomendacionesIA]);

  useEffect(() => {
    if (!sucursalActiva?.id) {
      setEvaluacionesProximas([]);
      return;
    }
    const hasta = new Date(Date.now() + 60 * 86400000);
    let query = supabase.from('progreso_evaluacion_citas')
      .select('id,usuario_id,fecha_inicio,estado,usuario:usuarios(nombre,email)')
      .eq('sucursal_id', sucursalActiva.id)
      .in('estado', ['solicitada', 'confirmada'])
      .gte('fecha_inicio', new Date().toISOString())
      .lte('fecha_inicio', hasta.toISOString())
      .order('fecha_inicio')
      .limit(20);
    if (isCoach) {
      const ownIds = usuarios.map((usuario) => usuario.id);
      if (ownIds.length === 0) {
        setEvaluacionesProximas([]);
        return;
      }
      query = query.in('usuario_id', ownIds);
    }
    void query
      .then(({ data, error }) => {
        if (error) console.error('Error cargando evaluaciones próximas:', error);
        setEvaluacionesProximas((data || []) as unknown as EvaluacionProxima[]);
      });
  }, [sucursalActiva?.id, isCoach, usuarios]);

  useEffect(() => {
    setEntrenamientosPage(1);
    setExpandedDay(null);
    setHistorialIAExpandido(false);
    setHistorialIAPagina(1);
  }, [selectedUsuario?.id]);

  useEffect(() => {
    const gimnasioId = sucursalActiva?.gimnasio_id;
    if (!gimnasioId) {
      setPlanesReferencia([]);
      return;
    }
    void supabase
      .from('planes')
      .select('nombre,max_sesiones_periodo,duracion_dias')
      .eq('gimnasio_id', gimnasioId)
      .eq('activo', true)
      .then(({ data, error }) => {
        if (error) console.error('Error cargando referencia de planes:', error);
        setPlanesReferencia((data || []) as PlanReferencia[]);
      });
  }, [sucursalActiva?.gimnasio_id]);

  useEffect(() => {
    setEditandoFamiliaAsignada(false);
    setFamiliaAsignadaDraft(null);
    if (selectedUsuario) {
      setReportesError(null);
      setReportesNotice(null);
      fetchProgreso(selectedUsuario.id);
      fetchProgresoReportes(selectedUsuario.id);
      fetchNutricionCalculos(selectedUsuario.id);
      fetchEntrenamientos(selectedUsuario.id);
      fetchRutinasPersonalizadas(selectedUsuario.id);
      fetchRutinasAsignadas(selectedUsuario.id);
      fetchRutinasHistorial(selectedUsuario.id);
      fetchReglamentoEstado(selectedUsuario.id);
      fetchRecomendacionesIA(selectedUsuario.id);
      fetchFamiliaUsuario(selectedUsuario.id);
    } else {
      setReportes([]);
      setNutriHistorial([]);
      setRutinasPersonalizadas([]);
      setRutinasAsignadas([]);
      setRutinasHistorial([]);
      setRecomendacionesIA([]);
      setHistorialRecomendacionesIA([]);
      setHistorialIATotal(0);
      setHistorialIAExpandido(false);
      setHistorialIAPagina(1);
      setHistorialIASeleccionado(null);
      setRevisionesIA([]);
      setReglamentoEstado(null);
      setFamiliaAsignada(null);
      setCambiosFamilia([]);
    }
  }, [selectedUsuario]);

  useEffect(() => {
    const gimnasioId = sucursalActiva?.gimnasio_id;
    if (!gimnasioId) return setFamiliasDisponibles([]);
    void supabase.from('rutina_familias').select('id,nombre,descripcion,rutina_familia_etapas(id)').eq('gimnasio_id', gimnasioId).eq('activa', true).then(({ data, error }) => {
      if (error) return console.error('Error cargando familias:', error);
      setFamiliasDisponibles((data || []).map((row: any) => ({ ...row, etapas: row.rutina_familia_etapas || [] })));
    });
  }, [sucursalActiva?.gimnasio_id]);

  const fetchFamiliaUsuario = async (usuarioId: string) => {
    const { data, error } = await supabase.from('usuario_familia_rutina')
      .select('id,estado,etapa_iniciada_at,familia_nombre_snapshot,familia_descripcion_snapshot,avance_automatico_snapshot,dias_aprobacion_snapshot,familia:rutina_familias(id,nombre,dias_aprobacion),etapa:rutina_familia_etapas(id,orden,duracion_dias,rutina:rutinas(nombre)),etapa_snapshot:usuario_familia_rutina_etapas!usuario_familia_rutina_etapa_snapshot_actual_id_fkey(id,orden,duracion_dias,nombre_rutina_snapshot),etapas_snapshot:usuario_familia_rutina_etapas!usuario_familia_rutina_etapas_asignacion_id_fkey(id,orden,duracion_dias,sesiones_minimas,nombre_rutina_snapshot,rutina_copia:rutinas!usuario_familia_rutina_etapas_rutina_copia_id_fkey(id,nombre,activa))')
      .eq('usuario_id', usuarioId).eq('estado', 'activa').maybeSingle();
    if (error) { console.error('Error cargando familia del socio:', error); return; }
    const current = data ? {
      ...(data as unknown as FamiliaAsignada),
      etapas_snapshot: [...(((data as any).etapas_snapshot || []) as FamiliaAsignada['etapas_snapshot'])].sort((a, b) => a.orden - b.orden),
    } : null;
    setFamiliaAsignada(current);
    if (!current) return setCambiosFamilia([]);
    await supabase.rpc('evaluar_cambios_familias');
    const { data: cambios, error: cambiosError } = await supabase.from('rutina_familia_cambios')
      .select('id,motivo,fecha_limite,estado,destino:rutina_familia_etapas!rutina_familia_cambios_etapa_destino_id_fkey(orden,rutina:rutinas(nombre)),destino_snapshot:usuario_familia_rutina_etapas!rutina_familia_cambios_etapa_snapshot_destino_id_fkey(orden,nombre_rutina_snapshot)')
      .eq('asignacion_id', current.id).eq('estado', 'pendiente').order('created_at', { ascending: false });
    if (cambiosError) console.error('Error cargando cambios de familia:', cambiosError);
    setCambiosFamilia((cambios || []) as unknown as CambioFamilia[]);
  };

  const asignarFamilia = async () => {
    if (!selectedUsuario || !familiaSeleccionadaId) return;
    if (!window.confirm('Se asignará la primera etapa de esta familia al socio. ¿Continuar?')) return;
    try {
      setProcesandoFamilia(true);
      const { error } = await supabase.rpc('asignar_familia_rutina', { p_usuario_id: selectedUsuario.id, p_familia_id: familiaSeleccionadaId });
      if (error) throw error;
      setFamiliaSeleccionadaId('');
      await Promise.all([fetchFamiliaUsuario(selectedUsuario.id), fetchRutinasAsignadas(selectedUsuario.id), fetchRutinasHistorial(selectedUsuario.id)]);
      setReportesNotice('Familia asignada correctamente. La primera etapa ya está activa.');
    } catch (err: any) { setReportesError(err?.message || 'No se pudo asignar la familia.'); }
    finally { setProcesandoFamilia(false); }
  };

  const abrirEdicionFamiliaAsignada = () => {
    if (!familiaAsignada) return;
    setFamiliaAsignadaDraft({
      nombre: familiaAsignada.familia_nombre_snapshot || familiaAsignada.familia?.nombre || '',
      descripcion: familiaAsignada.familia_descripcion_snapshot || '',
      avanceAutomatico: familiaAsignada.avance_automatico_snapshot ?? true,
      diasAprobacion: familiaAsignada.dias_aprobacion_snapshot || familiaAsignada.familia?.dias_aprobacion || 5,
      etapas: familiaAsignada.etapas_snapshot.map((etapa) => ({
        id: etapa.id,
        orden: etapa.orden,
        duracionDias: etapa.duracion_dias,
        sesionesMinimas: etapa.sesiones_minimas == null ? '' : String(etapa.sesiones_minimas),
        rutinaId: etapa.rutina_copia?.id || '',
        rutinaNombre: etapa.rutina_copia?.nombre || etapa.nombre_rutina_snapshot,
      })),
    });
    setEditandoFamiliaAsignada(true);
  };

  const guardarFamiliaAsignada = async () => {
    if (!selectedUsuario || !familiaAsignada || !familiaAsignadaDraft?.nombre.trim()) return;
    try {
      setProcesandoFamilia(true);
      const { error } = await supabase.rpc('actualizar_familia_asignada', {
        p_asignacion_id: familiaAsignada.id,
        p_nombre: familiaAsignadaDraft.nombre.trim(),
        p_descripcion: familiaAsignadaDraft.descripcion.trim() || null,
        p_avance_automatico: familiaAsignadaDraft.avanceAutomatico,
        p_dias_aprobacion: familiaAsignadaDraft.diasAprobacion,
        p_etapas: familiaAsignadaDraft.etapas.map((etapa) => ({
          id: etapa.id,
          duracion_dias: etapa.duracionDias,
          sesiones_minimas: etapa.sesionesMinimas.trim() ? Number(etapa.sesionesMinimas) : null,
        })),
      });
      if (error) throw error;
      setEditandoFamiliaAsignada(false);
      setFamiliaAsignadaDraft(null);
      await fetchFamiliaUsuario(selectedUsuario.id);
      setReportesNotice('La familia personalizada del socio quedó actualizada.');
    } catch (err: any) {
      setReportesError(err?.message || 'No se pudo actualizar la familia del socio.');
    } finally {
      setProcesandoFamilia(false);
    }
  };

  const resolverCambioFamilia = async (cambio: CambioFamilia, aprobar: boolean) => {
    if (!selectedUsuario || !window.confirm(aprobar ? '¿Aprobar y aplicar la siguiente rutina?' : '¿Rechazar este cambio?')) return;
    try {
      setProcesandoFamilia(true);
      const { error } = await supabase.rpc('resolver_cambio_familia', { p_cambio_id: cambio.id, p_aprobar: aprobar, p_comentario: null, p_automatico: false });
      if (error) throw error;
      await Promise.all([fetchFamiliaUsuario(selectedUsuario.id), fetchRutinasAsignadas(selectedUsuario.id), fetchRutinasHistorial(selectedUsuario.id)]);
      setReportesNotice(aprobar ? 'Cambio de etapa aprobado y aplicado.' : 'Cambio de etapa rechazado.');
    } catch (err: any) { setReportesError(err?.message || 'No se pudo resolver el cambio.'); }
    finally { setProcesandoFamilia(false); }
  };

  const fetchReglamentoEstado = async (usuarioId: string) => {
    if (!sucursalActiva?.gimnasio_id) {
      setReglamentoEstado(null);
      return;
    }
    try {
      const { data: gym } = await supabase
        .from('gimnasios')
        .select('reglamento_habilitado,reglamento_version')
        .eq('id', sucursalActiva.gimnasio_id)
        .maybeSingle();

      const version = (gym as any)?.reglamento_version ? Number((gym as any).reglamento_version) : null;
      const habilitado = (gym as any)?.reglamento_habilitado === true;
      if (!habilitado || !version) {
        setReglamentoEstado({ aceptado: false, version, fecha: null });
        return;
      }

      let { data } = await supabase
        .from('usuarios_reglamento_aceptaciones')
        .select('aceptado,accepted_at,rejected_at,created_at,gimnasio_id')
        .eq('usuario_id', usuarioId)
        .eq('gimnasio_id', sucursalActiva.gimnasio_id)
        .eq('reglamento_version', version)
        .order('created_at', { ascending: false })
        .limit(1);

      // Fallback defensivo: si por histórico el registro quedó con gimnasio_id distinto,
      // igual reflejamos el último estado de la versión vigente para el usuario.
      if (!Array.isArray(data) || data.length === 0) {
        const fallback = await supabase
          .from('usuarios_reglamento_aceptaciones')
          .select('aceptado,accepted_at,rejected_at,created_at,gimnasio_id')
          .eq('usuario_id', usuarioId)
          .eq('reglamento_version', version)
          .order('created_at', { ascending: false })
          .limit(1);
        data = fallback.data ?? [];
      }

      const latest = Array.isArray(data) && data.length > 0 ? data[0] as any : null;
      setReglamentoEstado({
        aceptado: !!latest?.aceptado,
        version,
        fecha: (latest?.accepted_at || latest?.rejected_at || null) as string | null,
      });
    } catch (err) {
      console.error('Error fetching reglamento estado:', err);
      setReglamentoEstado(null);
    }
  };

  const fetchUsuarios = async () => {
    if (!sucursalActiva?.id) {
      setUsuarios([]);
      setSelectedUsuario(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      let query = supabase
        .from('usuarios')
        .select('id, nombre, email, tipo_membresia, estado, fecha_nacimiento, fecha_cambio_rutina, profesor_admin_id, declaracion_salud, contacto_emergencia')
        .eq('sucursal_id', sucursalActiva.id)
        .order('nombre', { ascending: true });

      if (isStaff && !isInternalCoach && !isExternalCoach) {
        if (!profile?.adminId) throw new Error('No se pudo identificar al coach.');
        query = query.eq('profesor_admin_id', profile.adminId);
      }

      const { data, error } = await query;

      if (error) throw error;

      const usuariosConAvatar = (data || []).map(u => ({
        ...u,
        avatar: u.nombre.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
      }));

      setUsuarios(usuariosConAvatar);

      // Al cambiar de sucursal no conservar un usuario perteneciente a la
      // sucursal anterior. Esto dejaba Mowe mostrando datos de Maximum.
      setSelectedUsuario((current) => {
        if (usuariosConAvatar.length === 0) return null;
        return usuariosConAvatar.find((usuario) => usuario.id === current?.id)
          || usuariosConAvatar[0];
      });
    } catch (err) {
      console.error('Error fetching usuarios:', err);
    } finally {
      setLoading(false);
    }
  };

  const openRutinaChangeModal = () => {
    if (!selectedUsuario) return;
    setRutinaChangeDate(selectedUsuario.fecha_cambio_rutina || '');
    setShowRutinaChangeModal(true);
  };

  const openRutinaAssignModal = async () => {
    if (!selectedUsuario || !sucursalActiva?.gimnasio_id) return;
    setRutinaSeleccionadaId('');
    setRutinaAssignSearch('');
    setReportesError(null);
    setLoadingRutinasDisponibles(true);
    setShowRutinaAssignModal(true);
    try {
      const { data, error } = await supabase
        .from('rutinas')
        .select('id,nombre,descripcion')
        .eq('gimnasio_id', sucursalActiva.gimnasio_id)
        .eq('activa', true)
        .eq('creada_por_usuario', false)
        .or('es_plantilla.is.null,es_plantilla.eq.false')
        .or('es_copia_usuario.is.null,es_copia_usuario.eq.false')
        .order('nombre', { ascending: true });
      if (error) throw error;
      setRutinasDisponibles((data || []) as RutinaDisponible[]);
    } catch (err: any) {
      setRutinasDisponibles([]);
      setReportesError(err?.message || 'No se pudieron cargar las rutinas disponibles.');
    } finally {
      setLoadingRutinasDisponibles(false);
    }
  };

  const handleAssignRutina = async () => {
    if (!selectedUsuario || !rutinaSeleccionadaId) return;
    if (selectedUsuario.estado.toLocaleLowerCase('es-CL') !== 'activo') {
      setReportesError('Solo se pueden asignar rutinas a usuarios activos.');
      return;
    }
    try {
      setAsignandoRutina(true);
      setReportesError(null);
      const { error } = await supabase.rpc('asignar_rutina_por_copia', {
        p_rutina_origen_id: rutinaSeleccionadaId,
        p_usuario_ids: [selectedUsuario.id],
      });
      if (error) throw error;
      const rutina = rutinasDisponibles.find((item) => item.id === rutinaSeleccionadaId);
      await Promise.all([
        fetchRutinasAsignadas(selectedUsuario.id),
        fetchRutinasHistorial(selectedUsuario.id),
      ]);
      setShowRutinaAssignModal(false);
      setReportesNotice(`Rutina ${rutina?.nombre ? `“${rutina.nombre}” ` : ''}asignada correctamente.`);
    } catch (err: any) {
      setReportesError(err?.message || 'No se pudo asignar la rutina.');
    } finally {
      setAsignandoRutina(false);
    }
  };

  const fetchCoachOptions = async () => {
    if (!sucursalActiva?.gimnasio_id) {
      setCoachOptions([]);
      return;
    }
    try {
      const { data, error } = await supabase
        .from('usuarios_admin')
        .select('id,nombre,email,role,activo')
        .eq('gimnasio_id', sucursalActiva.gimnasio_id)
        .eq('activo', true)
        .in('role', ['staff', 'gym_admin'])
        .order('nombre', { ascending: true });
      if (error) throw error;
      setCoachOptions((data as CoachOption[]) || []);
    } catch (err) {
      console.error('Error cargando coaches:', err);
      setCoachOptions([]);
    }
  };

  const openCoachModal = async () => {
    if (!selectedUsuario) return;
    await fetchCoachOptions();
    setSelectedCoachId(selectedUsuario.profesor_admin_id || '');
    setShowCoachModal(true);
  };

  const handleSaveCoach = async () => {
    if (!selectedUsuario) return;
    try {
      setSavingCoach(true);
      const coachId = (selectedCoachId || '').trim();
      const { error } = await supabase
        .from('usuarios')
        .update({
          profesor_admin_id: coachId || null,
          updated_at: new Date().toISOString(),
        })
        .eq('id', selectedUsuario.id);
      if (error) throw error;

      setSelectedUsuario((prev) =>
        prev
          ? {
              ...prev,
              profesor_admin_id: coachId || null,
            }
          : prev
      );
      setUsuarios((prev) =>
        prev.map((u) =>
          u.id === selectedUsuario.id
            ? { ...u, profesor_admin_id: coachId || null }
            : u
        )
      );
      await fetchCoachOptions();
      setShowCoachModal(false);
    } catch (err) {
      console.error('Error guardando coach:', err);
      alert('No se pudo guardar el coach');
    } finally {
      setSavingCoach(false);
    }
  };

  const getCoachNombre = (coachId?: string | null) => {
    if (!coachId) return 'Sin asignar';
    const c = coachOptions.find((x) => x.id === coachId);
    return c?.nombre || 'Sin asignar';
  };

  const handleSaveRutinaChange = async () => {
    if (!selectedUsuario) return;
    if (selectedUsuario.estado.toLocaleLowerCase('es-CL') !== 'activo') {
      alert('No se puede programar un cambio de rutina para un usuario inactivo o vencido.');
      return;
    }
    try {
      setSavingRutinaChange(true);
      const payloadDate = (rutinaChangeDate || '').trim();
      const { error } = await supabase
        .from('usuarios')
        .update({
          fecha_cambio_rutina: payloadDate || null,
          updated_at: new Date().toISOString(),
        })
        .eq('id', selectedUsuario.id);
      if (error) throw error;

      setSelectedUsuario((prev) =>
        prev
          ? {
              ...prev,
              fecha_cambio_rutina: payloadDate || null,
            }
          : prev
      );
      setUsuarios((prev) =>
        prev.map((u) =>
          u.id === selectedUsuario.id
            ? { ...u, fecha_cambio_rutina: payloadDate || null }
            : u
        )
      );
      setShowRutinaChangeModal(false);
    } catch (err) {
      console.error('Error guardando fecha de cambio de rutina:', err);
      alert('No se pudo guardar la fecha de cambio de rutina');
    } finally {
      setSavingRutinaChange(false);
    }
  };

  const fetchProgreso = async (usuarioId: string) => {
    try {
      const { data, error } = await supabase
        .from('progreso')
        .select('*')
        .eq('usuario_id', usuarioId)
        .order('fecha', { ascending: false });

      if (error) throw error;

      setProgreso(data || []);
    } catch (err) {
      console.error('Error fetching progreso:', err);
    }
  };

  const fetchRecomendacionesIA = async (usuarioId: string) => {
    try {
      setLoadingRecomendacionesIA(true);
      const [pendientesResult, historialResult] = await Promise.all([
        supabase
          .from('rutina_recomendaciones_ia')
          .select(RECOMENDACION_IA_SELECT)
          .eq('usuario_id', usuarioId)
          .eq('estado', 'pendiente')
          .order('created_at', { ascending: false }),
        supabase
          .from('rutina_recomendaciones_ia')
          .select(RECOMENDACION_IA_SELECT, { count: 'exact' })
          .eq('usuario_id', usuarioId)
          .neq('estado', 'pendiente')
          .order('created_at', { ascending: false })
          .range(0, HISTORIAL_IA_POR_PAGINA - 1),
      ]);
      if (pendientesResult.error) throw pendientesResult.error;
      if (historialResult.error) throw historialResult.error;
      setRecomendacionesIA((pendientesResult.data || []) as unknown as RecomendacionRutinaIA[]);
      setHistorialRecomendacionesIA((historialResult.data || []) as unknown as RecomendacionRutinaIA[]);
      setHistorialIATotal(historialResult.count || 0);
      setHistorialIAPagina(1);
    } catch (err) {
      console.error('Error cargando recomendaciones IA:', err);
      setRecomendacionesIA([]);
      setHistorialRecomendacionesIA([]);
      setHistorialIATotal(0);
    } finally {
      setLoadingRecomendacionesIA(false);
    }
  };

  const fetchHistorialIAPagina = async (usuarioId: string, pagina: number) => {
    try {
      setLoadingHistorialIA(true);
      const desde = (pagina - 1) * HISTORIAL_IA_POR_PAGINA;
      const { data, error, count } = await supabase
        .from('rutina_recomendaciones_ia')
        .select(RECOMENDACION_IA_SELECT, { count: 'exact' })
        .eq('usuario_id', usuarioId)
        .neq('estado', 'pendiente')
        .order('created_at', { ascending: false })
        .range(desde, desde + HISTORIAL_IA_POR_PAGINA - 1);
      if (error) throw error;
      setHistorialRecomendacionesIA((data || []) as unknown as RecomendacionRutinaIA[]);
      setHistorialIATotal(count || 0);
      setHistorialIAPagina(pagina);
    } catch (err) {
      console.error('Error cargando página del historial IA:', err);
      setReportesError('No se pudo cargar esa página del historial de propuestas IA.');
    } finally {
      setLoadingHistorialIA(false);
    }
  };

  const abrirHistorialRecomendacionIA = async (recomendacion: RecomendacionRutinaIA) => {
    setHistorialIASeleccionado(recomendacion);
    setRevisionesIA([]);
    setLoadingRevisionesIA(true);
    try {
      const { data, error } = await supabase
        .from('rutina_recomendaciones_ia_revisiones')
        .select('id,admin_id,propuesta_antes,propuesta_despues,comentario,created_at')
        .eq('recomendacion_id', recomendacion.id)
        .order('created_at', { ascending: true });
      if (error) throw error;

      const raw = (data || []) as Array<Omit<RevisionRecomendacionIA, 'admin'> & { admin_id: string | null }>;
      const adminIds = [...new Set(raw.map((revision) => revision.admin_id).filter(Boolean))] as string[];
      let admins = new Map<string, { nombre: string; email: string }>();
      if (adminIds.length > 0) {
        const { data: adminData, error: adminError } = await supabase
          .from('usuarios_admin')
          .select('id,nombre,email')
          .in('id', adminIds);
        if (!adminError) {
          admins = new Map((adminData || []).map((admin) => [admin.id, { nombre: admin.nombre, email: admin.email }]));
        }
      }
      setRevisionesIA(raw.map((revision) => ({
        id: revision.id,
        propuesta_antes: revision.propuesta_antes,
        propuesta_despues: revision.propuesta_despues,
        comentario: revision.comentario,
        created_at: revision.created_at,
        admin: revision.admin_id ? admins.get(revision.admin_id) || null : null,
      })));
    } catch (err: any) {
      setReportesError(err?.message || 'No se pudo cargar la comparación de la propuesta.');
    } finally {
      setLoadingRevisionesIA(false);
    }
  };

  const resolverRecomendacionIA = async (recomendacion: RecomendacionRutinaIA, aprobar: boolean) => {
    if (!selectedUsuario) return;
    const action = aprobar ? 'aplicar esta rutina al socio' : 'rechazar esta recomendación';
    const comentario = window.prompt(
      aprobar ? 'Comentario de aprobación (opcional):' : 'Indica el motivo del rechazo:',
      '',
    );
    if (comentario === null) return;
    if (!aprobar && !comentario.trim()) {
      setReportesError('Debes indicar el motivo del rechazo.');
      return;
    }
    if (!window.confirm(`¿Confirmas que deseas ${action}?`)) return;
    try {
      setResolviendoRecomendacionId(recomendacion.id);
      const { error } = await supabase.rpc('resolver_recomendacion_rutina_ia', {
        p_recomendacion_id: recomendacion.id,
        p_aprobar: aprobar,
        p_comentario: comentario.trim() || null,
      });
      if (error) throw error;
      await Promise.all([
        fetchRecomendacionesIA(selectedUsuario.id),
        fetchRutinasAsignadas(selectedUsuario.id),
        fetchRutinasHistorial(selectedUsuario.id),
      ]);
      setReportesNotice(aprobar
        ? `La recomendación fue aprobada y la rutina “${recomendacion.rutina_propuesta?.nombre || 'propuesta'}” quedó asignada.`
        : 'La recomendación de ACOMA fue rechazada.');
    } catch (err: any) {
      setReportesError(err?.message || 'No se pudo resolver la recomendación de ACOMA.');
    } finally {
      setResolviendoRecomendacionId(null);
    }
  };

  const generarRecomendacionIA = async () => {
    if (!selectedUsuario || acomaFormInvalid()) return;
    try {
      setGenerandoRecomendacionIA(true);
      setReportesError(null);
      setReportesNotice(null);
      const { data, error } = await supabase.functions.invoke('acoma-routine-suggestion', {
        body: { usuario_id: selectedUsuario.id, ...acomaForm },
      });
      if (error || data?.error) {
        let message = String(data?.error || error?.message || 'No pudimos generar la propuesta con ACOMA.');
        const context = (error as { context?: Response } | null)?.context;
        if (context) {
          try {
            const payload = await context.clone().json();
            message = String(payload?.error || message);
          } catch { /* respuesta no JSON */ }
        }
        throw new Error(message);
      }
      await fetchRecomendacionesIA(selectedUsuario.id);
      setShowAcomaSuggestionModal(false);
      setReportesNotice(String(data?.message || 'ACOMA generó una propuesta pendiente de revisión.'));
    } catch (err: any) {
      setReportesError(err?.message || 'No pudimos generar la propuesta con ACOMA.');
    } finally {
      setGenerandoRecomendacionIA(false);
    }
  };

  const acomaFormInvalid = () => (
    acomaForm.objetivo.trim().length < 3
    || acomaForm.frecuencia_semanal < 1
    || acomaForm.frecuencia_semanal > 7
    || acomaForm.duracion_minutos < 20
    || acomaForm.duracion_minutos > 180
  );

  const openFamilySuggestionForUsuario = (usuario: Usuario) => {
    setSelectedUsuario(usuario);
    setShowUserSelector(false);
    setReportesError(null);
    setReportesNotice(null);
    if (usuario.estado.toLocaleLowerCase('es-CL') !== 'activo') {
      setReportesError('El usuario debe estar activo para generar una familia.');
      return;
    }
    setFamilyForm({ instrucciones: '', nivel: 'Intermedio', frecuencia: 3, duracion_minutos: 60, cantidad_etapas: 3 });
    setShowFamilySuggestionModal(true);
  };

  const requestFamilySuggestion = async () => {
    if (!selectedUsuario || !sucursalActiva?.gimnasio_id || familyForm.instrucciones.trim().length < 10) return;
    try {
      setRequestingFamilySuggestion(true);
      setReportesError(null);
      const result = await callEdgeFunction<{ message?: string }>('acoma-family-request', {
        usuario_id: selectedUsuario.id,
        gimnasio_id: sucursalActiva.gimnasio_id,
        sucursal_id: sucursalActiva.id,
        instrucciones: familyForm.instrucciones.trim(),
        nivel: familyForm.nivel,
        frecuencias: [familyForm.frecuencia],
        duracion_minutos: familyForm.duracion_minutos,
        cantidad_etapas: familyForm.cantidad_etapas,
      });
      setShowFamilySuggestionModal(false);
      setReportesNotice(result.message || 'La propuesta se está generando. Puedes seguir trabajando y revisarla en Propuestas IA.');
    } catch (err: any) {
      setReportesError(err?.message || 'No pudimos iniciar la propuesta de familia.');
    } finally {
      setRequestingFamilySuggestion(false);
    }
  };

  const fetchProgresoReportes = async (usuarioId: string) => {
    try {
      setLoadingReportes(true);
      const { data, error } = await supabase
        .from('progreso_reportes')
        .select('id,usuario_id,progreso_id,fecha_medicion,fuente,fuente_url,mime_type,file_size,resumen,meta,created_at')
        .eq('usuario_id', usuarioId)
        .order('fecha_medicion', { ascending: false })
        .order('created_at', { ascending: false })
        .limit(100);

      if (error) throw error;
      setReportes((data as ProgresoReporte[]) || []);
    } catch (err: any) {
      console.error('Error fetching progreso reportes:', err);
      setReportes([]);
      setReportesError(err?.message || 'No se pudieron cargar los reportes');
    } finally {
      setLoadingReportes(false);
    }
  };

  const fetchNutricionCalculos = async (usuarioId: string) => {
    try {
      setLoadingNutriHistorial(true);
      const { data, error } = await supabase
        .from('nutricion_calculos')
        .select('*')
        .eq('usuario_id', usuarioId)
        .order('created_at', { ascending: false })
        .limit(20);

      if (error) throw error;
      setNutriHistorial((data as NutricionCalculo[]) || []);
    } catch (err: any) {
      console.error('Error fetching nutricion calculos:', err);
      setNutriHistorial([]);
    } finally {
      setLoadingNutriHistorial(false);
    }
  };

  const fetchEntrenamientos = async (usuarioId: string) => {
    try {
      setLoadingEntrenamientos(true);
      setExpandedDay(null);

      const { data, error } = await supabase
        .from('entrenamientos')
        .select(
          `
          id,
          usuario_id,
          fecha,
          sesion_id,
          rutina_id,
          entrenamiento_ejercicios (
            id,
            entrenamiento_id,
            ejercicio_id,
            nombre,
            categoria,
            series,
            repeticiones,
            descanso,
            peso_plan,
            peso_usado,
            completado
          )
        `
        )
        .eq('usuario_id', usuarioId)
        .order('fecha', { ascending: false })
        .limit(200);

      if (error) throw error;

      const rows = (data || []) as Entrenamiento[];
      setEntrenamientos(rows);

      const rutinaIds = Array.from(
        new Set(rows.map((r) => r.rutina_id).filter((id): id is string => typeof id === 'string' && id.length > 0))
      );

      if (rutinaIds.length > 0) {
          const { data: rutData, error: rutErr } = await supabase
            .from('rutinas')
            .select('id, nombre')
            .in('id', rutinaIds as any);

        if (!rutErr) {
          const map: Record<string, string> = {};
          for (const r of rutData || []) {
            map[String((r as any).id)] = String((r as any).nombre || '');
          }
          setRutinaNombreById(map);
        }
      } else {
        setRutinaNombreById({});
      }
    } catch (err: any) {
      // If the DB isn't migrated yet, keep this section empty but don't break the page.
      console.error('Error fetching entrenamientos:', err);
      setEntrenamientos([]);
      setRutinaNombreById({});
    } finally {
      setLoadingEntrenamientos(false);
    }
  };

  const fetchRutinasPersonalizadas = async (usuarioId: string) => {
    try {
      setLoadingRutinasPersonalizadas(true);
      const { data, error } = await supabase
        .from('rutinas')
        .select('id,nombre,descripcion,activa,ejercicios,created_at,updated_at')
        .eq('creada_por_usuario', true)
        .contains('usuarios_asignados', [usuarioId])
        .order('updated_at', { ascending: false })
        .limit(30);

      if (error) throw error;
      setRutinasPersonalizadas((data as RutinaPersonalizada[]) || []);
    } catch (err) {
      console.error('Error fetching rutinas personalizadas:', err);
      setRutinasPersonalizadas([]);
    } finally {
      setLoadingRutinasPersonalizadas(false);
    }
  };

  const fetchRutinasAsignadas = async (usuarioId: string) => {
    if (!sucursalActiva?.gimnasio_id) {
      setRutinasAsignadas([]);
      return;
    }
    try {
      setLoadingRutinasAsignadas(true);
      const { data, error } = await supabase
        .from('rutinas')
        .select('id,nombre,descripcion,activa,creada_por_usuario,updated_at')
        .eq('gimnasio_id', sucursalActiva.gimnasio_id)
        .contains('usuarios_asignados', [usuarioId])
        .or('es_plantilla.is.null,es_plantilla.eq.false')
        .order('updated_at', { ascending: false })
        .limit(30);

      if (error) throw error;
      setRutinasAsignadas((data as RutinaAsignada[]) || []);
    } catch (err) {
      console.error('Error fetching rutinas asignadas:', err);
      setRutinasAsignadas([]);
    } finally {
      setLoadingRutinasAsignadas(false);
    }
  };

  const fetchRutinasHistorial = async (usuarioId: string) => {
    try {
      setLoadingRutinasHistorial(true);
      const { data, error } = await supabase
        .from('rutinas_asignaciones_historial')
        .select('id,usuario_id,rutina_id,rutina_origen_id,accion,admin_auth_id,metadata,created_at')
        .eq('usuario_id', usuarioId)
        .order('created_at', { ascending: false })
        .limit(100);

      if (error) throw error;
      const rows = (data as RutinaHistorial[]) || [];
      setRutinasHistorial(rows);

      const ids = Array.from(
        new Set(
          rows
            .flatMap((r) => [r.rutina_id, r.rutina_origen_id])
            .filter((id): id is string => typeof id === 'string' && id.length > 0)
        )
      );
      if (ids.length > 0) {
        const { data: rutinasData, error: rutinasErr } = await supabase
          .from('rutinas')
          .select('id,nombre')
          .in('id', ids as any);
        if (!rutinasErr) {
          const map: Record<string, string> = {};
          for (const rr of rutinasData || []) {
            map[String((rr as any).id)] = String((rr as any).nombre || '');
          }
          setRutinaHistNombreById(map);
        }
      } else {
        setRutinaHistNombreById({});
      }
    } catch (err) {
      console.error('Error fetching rutinas historial:', err);
      setRutinasHistorial([]);
      setRutinaHistNombreById({});
    } finally {
      setLoadingRutinasHistorial(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedUsuario) return;

    try {
      const { error } = await supabase
        .from('progreso')
        .insert([{
          usuario_id: selectedUsuario.id,
          fecha: formData.fecha,
          peso: parseFloat(formData.peso),
          grasa_corporal: parseFloat(formData.grasa_corporal),
          masa_muscular: parseFloat(formData.masa_muscular),
          notas: formData.notas,
        }]);

      if (error) throw error;

      setShowModal(false);
      resetForm();
      fetchProgreso(selectedUsuario.id);
    } catch (err) {
      console.error('Error saving progreso:', err);
    }
  };

  const resetImportForm = () => {
    setImportForm({
      source_url: '',
      fecha_medicion: new Date().toISOString().split('T')[0],
      peso: '',
      grasa_corporal: '',
      masa_muscular: '',
      imc: '',
      pbf: '',
      grasa_visceral: '',
      tmb: '',
      notas: '',
    });
    setImportPreviewReady(false);
    setImportIdentityConfirmed(false);
    setImportSourceIdentity({ usuarioId: '', email: '', nombre: '' });
  };

  const parseQrTextIntoImportForm = (rawText: string) => {
    const text = rawText.trim();
    if (!text) return;

    const isHttp = /^https?:\/\//i.test(text);
    if (isHttp) {
      setImportForm((prev) => ({ ...prev, source_url: text }));
      try {
        const u = new URL(text);
        const q = (keys: string[]) => {
          for (const k of keys) {
            const v = u.searchParams.get(k);
            if (v && v.trim()) return v.trim();
          }
          return '';
        };
        const fecha = q(['fecha', 'date', 'fecha_medicion', 'measurement_date']);
        const peso = q(['peso', 'weight', 'weight_kg']);
        const grasa = q(['grasa_corporal', 'body_fat', 'fat', 'pbf']);
        const musculo = q(['masa_muscular', 'muscle', 'muscle_mass']);
        const imc = q(['imc', 'bmi']);
        const visceral = q(['grasa_visceral', 'visceral_fat']);
        const tmb = q(['tmb', 'bmr']);
        const sourceUsuarioId = q(['usuario_id', 'user_id', 'member_id', 'client_id']);
        const sourceEmail = q(['email', 'correo', 'member_email']);
        const sourceNombre = q(['nombre', 'name', 'member_name']);

        setImportForm((prev) => ({
          ...prev,
          source_url: text,
          fecha_medicion: /^\d{4}-\d{2}-\d{2}$/.test(fecha) ? fecha : prev.fecha_medicion,
          peso: peso || prev.peso,
          grasa_corporal: grasa || prev.grasa_corporal,
          masa_muscular: musculo || prev.masa_muscular,
          imc: imc || prev.imc,
          grasa_visceral: visceral || prev.grasa_visceral,
          tmb: tmb || prev.tmb,
        }));
        setImportSourceIdentity({ usuarioId: sourceUsuarioId, email: sourceEmail, nombre: sourceNombre });
        setImportIdentityConfirmed(false);
      } catch {
        // URL inválida parcial: igual dejamos source_url cargada.
      }
      return;
    }

    setImportForm((prev) => ({
      ...prev,
      notas: prev.notas
        ? `${prev.notas}\nQR: ${text}`
        : `QR: ${text}`,
    }));
  };

  const loadFitMaoMeasurements = async (sourceUrl: string) => {
    if (!selectedUsuario) throw new Error('Selecciona un usuario antes de leer el informe.');
    setExtractingFitmao(true);
    try {
      const response = await callEdgeFunction<{ extracted?: FitMaoExtracted; message?: string }>('progreso-import-fitmao', {
        action: 'preview',
        usuario_id: selectedUsuario.id,
        source_url: sourceUrl,
        fuente: 'fitmao',
      });
      const extracted = response.extracted;
      if (!extracted) throw new Error('FitMao no devolvió mediciones reconocibles.');
      const textValue = (value: number | null) => value === null ? '' : String(value);
      setImportForm((prev) => ({
        ...prev,
        source_url: sourceUrl,
        fecha_medicion: extracted.fecha_medicion || prev.fecha_medicion,
        peso: textValue(extracted.peso),
        grasa_corporal: textValue(extracted.grasa_corporal),
        masa_muscular: textValue(extracted.masa_muscular),
        imc: textValue(extracted.imc),
        pbf: textValue(extracted.pbf),
        grasa_visceral: textValue(extracted.grasa_visceral),
        tmb: textValue(extracted.tmb),
      }));
      setImportSourceIdentity((prev) => ({
        ...prev,
        nombre: extracted.nombre || prev.nombre,
      }));
      setImportIdentityConfirmed(false);
      setImportPreviewReady(true);
      setReportesNotice('Datos recuperados desde FitMao. Revisa los valores antes de confirmar.');
      return extracted;
    } finally {
      setExtractingFitmao(false);
    }
  };

  const stopQrScanner = () => {
    if (qrRafRef.current !== null) {
      cancelAnimationFrame(qrRafRef.current);
      qrRafRef.current = null;
    }
    if (qrStreamRef.current) {
      qrStreamRef.current.getTracks().forEach((t) => t.stop());
      qrStreamRef.current = null;
    }
    if (qrVideoRef.current) {
      qrVideoRef.current.srcObject = null;
    }
    setScanningQr(false);
  };

  const decodeQrFromImageFile = async (file: File) => {
    const BarcodeDetectorCtor = (window as any).BarcodeDetector;
    if (!BarcodeDetectorCtor) {
      throw new Error('Tu navegador no soporta lectura QR automática por imagen. Usa Chrome/Edge moderno.');
    }
    const detector = new BarcodeDetectorCtor({ formats: ['qr_code'] });
    const bitmap = await createImageBitmap(file);
    try {
      const codes = await detector.detect(bitmap);
      const value = String(codes?.[0]?.rawValue || '').trim();
      if (!value) throw new Error('No se detectó un QR válido en la imagen.');
      return value;
    } finally {
      bitmap.close();
    }
  };

  const handleQrImagePick = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      setReadingQrImage(true);
      setQrScanError(null);
      setQrScanSuccess(null);
      const value = await decodeQrFromImageFile(file);
      parseQrTextIntoImportForm(value);
      setQrScanSuccess('QR leído correctamente. Se cargó la información en el formulario.');
      stopQrScanner();
      setTimeout(() => {
        setShowQrScannerModal(false);
      }, 900);
    } catch (err: any) {
      setQrScanError(err?.message || 'No se pudo leer el QR desde la imagen.');
    } finally {
      setReadingQrImage(false);
      if (qrImageInputRef.current) qrImageInputRef.current.value = '';
    }
  };

  const startQrScanner = async () => {
    try {
      setQrScanError(null);
      setQrScanSuccess(null);
      const BarcodeDetectorCtor = (window as any).BarcodeDetector;
      if (!BarcodeDetectorCtor) {
        throw new Error('Este navegador no soporta lectura QR por cámara. Usa Chrome/Edge actual.');
      }
      qrDetectorRef.current = new BarcodeDetectorCtor({ formats: ['qr_code'] });
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' },
        audio: false,
      });
      qrStreamRef.current = stream;
      if (qrVideoRef.current) {
        qrVideoRef.current.srcObject = stream;
        await qrVideoRef.current.play();
      }
      setScanningQr(true);

      const tick = async () => {
        if (!qrVideoRef.current || !qrDetectorRef.current) return;
        try {
          const barcodes = await qrDetectorRef.current.detect(qrVideoRef.current);
          if (Array.isArray(barcodes) && barcodes.length > 0) {
            const rawValue = String(barcodes[0]?.rawValue || '').trim();
            if (rawValue) {
              parseQrTextIntoImportForm(rawValue);
              setQrScanSuccess('QR leído correctamente. Se cargó la información en el formulario.');
              stopQrScanner();
              setTimeout(() => {
                setShowQrScannerModal(false);
              }, 900);
              return;
            }
          }
        } catch {
          // ignoramos frames con error y seguimos escaneando
        }
        qrRafRef.current = requestAnimationFrame(() => {
          void tick();
        });
      };

      void tick();
    } catch (err: any) {
      setQrScanError(err?.message || 'No se pudo iniciar la cámara para escanear QR.');
      stopQrScanner();
    }
  };

  const handleImportFitmao = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedUsuario) return;

    try {
      setImportingReporte(true);
      setReportesError(null);
      setReportesNotice(null);

      const sourceUrl = importForm.source_url.trim();
      if (!sourceUrl) {
        throw new Error('Debes ingresar la URL del informe.');
      }

      if (!importPreviewReady) {
        await loadFitMaoMeasurements(sourceUrl);
        return;
      }

      const peso = parseOptionalNumber(importForm.peso);
      const grasa = parseOptionalNumber(importForm.grasa_corporal);
      const musculo = parseOptionalNumber(importForm.masa_muscular);
      const validations: Array<[string, number | null, number, number]> = [
        ['Peso', peso, 20, 350],
        ['Grasa corporal', grasa, 2, 75],
        ['Masa muscular', musculo, 5, 150],
        ['IMC', parseOptionalNumber(importForm.imc), 8, 80],
        ['PBF', parseOptionalNumber(importForm.pbf), 2, 75],
        ['Grasa visceral', parseOptionalNumber(importForm.grasa_visceral), 0, 60],
        ['TMB', parseOptionalNumber(importForm.tmb), 500, 5000],
      ];
      if (peso === null || grasa === null || musculo === null) {
        throw new Error('Completa peso, grasa corporal y masa muscular.');
      }
      const invalidRange = validations.find(([, value, min, max]) => value !== null && (value < min || value > max));
      if (invalidRange) throw new Error(`${invalidRange[0]} debe estar entre ${invalidRange[2]} y ${invalidRange[3]}.`);
      if (importForm.fecha_medicion > new Date().toISOString().split('T')[0]) {
        throw new Error('La fecha de medición no puede estar en el futuro.');
      }
      const idMismatch = importSourceIdentity.usuarioId && importSourceIdentity.usuarioId !== selectedUsuario.id;
      const emailMismatch = importSourceIdentity.email && importSourceIdentity.email.toLocaleLowerCase('es-CL') !== selectedUsuario.email.toLocaleLowerCase('es-CL');
      if (idMismatch || emailMismatch) {
        throw new Error('La identidad incluida en el informe no coincide con el usuario seleccionado.');
      }
      if (!importIdentityConfirmed) {
        throw new Error('Confirma que los datos corresponden al usuario seleccionado.');
      }

      const resumen: Record<string, unknown> = {};
      const imc = parseOptionalNumber(importForm.imc);
      const pbf = parseOptionalNumber(importForm.pbf);
      const visceral = parseOptionalNumber(importForm.grasa_visceral);
      const tmb = parseOptionalNumber(importForm.tmb);

      if (imc !== null) resumen.imc = imc;
      if (pbf !== null) resumen.pbf = pbf;
      if (visceral !== null) resumen.grasa_visceral = visceral;
      if (tmb !== null) resumen.tmb = tmb;

      const progresoPayload: Record<string, unknown> = {};
      const notas = importForm.notas.trim();

      if (peso !== null) progresoPayload.peso = peso;
      if (grasa !== null) progresoPayload.grasa_corporal = grasa;
      if (musculo !== null) progresoPayload.masa_muscular = musculo;
      if (notas) progresoPayload.notas = notas;

      const body: Record<string, unknown> = {
        action: 'import',
        usuario_id: selectedUsuario.id,
        source_url: sourceUrl,
        fecha_medicion: importForm.fecha_medicion,
        fuente: 'fitmao',
        identidad_confirmada: true,
        meta: {
          identidad_origen: importSourceIdentity.usuarioId || importSourceIdentity.email || importSourceIdentity.nombre ? 'qr_fitmao' : 'confirmacion_manual',
          identidad_reportada: importSourceIdentity,
          usuario_confirmado: { id: selectedUsuario.id, email: selectedUsuario.email, nombre: selectedUsuario.nombre },
        },
      };
      if (Object.keys(resumen).length > 0) body.resumen = resumen;
      if (Object.keys(progresoPayload).length > 0) body.progreso = progresoPayload;

      await callEdgeFunction('progreso-import-fitmao', body);

      setShowImportModal(false);
      resetImportForm();
      setReportesNotice('Informe importado correctamente.');
      await Promise.all([fetchProgreso(selectedUsuario.id), fetchProgresoReportes(selectedUsuario.id)]);
    } catch (err: any) {
      setReportesError(err?.message || 'No se pudo importar el informe');
    } finally {
      setImportingReporte(false);
    }
  };

  useEffect(() => {
    if (showQrScannerModal) {
      void startQrScanner();
    } else {
      stopQrScanner();
    }
    return () => {
      stopQrScanner();
    };
  }, [showQrScannerModal]);

  const openReporte = async (reporte: ProgresoReporte) => {
    try {
      setOpeningReporteId(reporte.id);
      setReportesError(null);
      setReportesNotice(null);

      const mime = String(reporte.mime_type || '').toLowerCase();
      const isHtmlLike = mime.includes('text/html') || mime.includes('text/plain');
      if ((reporte.fuente === 'fitmao' || isHtmlLike) && reporte.fuente_url) {
        openUrlInNewTab(reporte.fuente_url);
        return;
      }

      const data = await callEdgeFunction<{ url?: string }>('progreso-reportes-signed-url', {
          reporte_id: reporte.id,
          expires_seconds: 3600,
      });
      const signedUrl = String(data?.url || '');
      if (!signedUrl) {
        throw new Error('No se pudo generar URL firmada del informe');
      }

      openUrlInNewTab(signedUrl);
    } catch (err: any) {
      setReportesError(err?.message || 'No se pudo abrir el informe');
    } finally {
      setOpeningReporteId(null);
    }
  };

  const openCorrectionModal = async (reporte: ProgresoReporte) => {
    if (!reporte.progreso_id) {
      setReportesError('Este informe no tiene una medición estructurada asociada.');
      return;
    }

    const medicion = progreso.find((item) => item.id === reporte.progreso_id);
    if (!medicion) {
      setReportesError('No se encontró la medición asociada. Actualiza la pantalla e inténtalo nuevamente.');
      return;
    }

    const resumen = reporte.resumen || {};
    const asInput = (value: unknown) => (
      typeof value === 'number' && Number.isFinite(value) ? String(value) : ''
    );
    setCorrectionForm({
      peso: asInput(medicion.peso),
      grasa_corporal: asInput(medicion.grasa_corporal),
      masa_muscular: asInput(medicion.masa_muscular),
      imc: asInput(resumen.imc),
      pbf: asInput(resumen.pbf),
      grasa_visceral: asInput(resumen.grasa_visceral),
      tmb: asInput(resumen.tmb),
      motivo: '',
    });
    setCorrectionReport(reporte);
    setCorrectionHistory([]);
    setLoadingCorrectionHistory(true);
    setReportesError(null);

    const { data, error } = await supabase
      .from('progreso_reportes_correcciones')
      .select('id,motivo,valores_antes,valores_despues,created_at')
      .eq('reporte_id', reporte.id)
      .order('created_at', { ascending: false });
    if (error) {
      setReportesError(error.message || 'No se pudo cargar el historial de correcciones.');
    } else {
      setCorrectionHistory((data as CorreccionEvaluacion[]) || []);
    }
    setLoadingCorrectionHistory(false);
  };

  const handleSaveCorrection = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!correctionReport || !selectedUsuario) return;

    try {
      setSavingCorrection(true);
      setReportesError(null);
      setReportesNotice(null);

      const peso = parseOptionalNumber(correctionForm.peso);
      const grasa = parseOptionalNumber(correctionForm.grasa_corporal);
      const musculo = parseOptionalNumber(correctionForm.masa_muscular);
      const validations: Array<[string, number | null, number, number]> = [
        ['Peso', peso, 20, 350],
        ['Grasa corporal', grasa, 2, 75],
        ['Masa muscular', musculo, 5, 150],
        ['IMC', parseOptionalNumber(correctionForm.imc), 8, 80],
        ['PBF', parseOptionalNumber(correctionForm.pbf), 2, 75],
        ['Grasa visceral', parseOptionalNumber(correctionForm.grasa_visceral), 0, 60],
        ['TMB', parseOptionalNumber(correctionForm.tmb), 500, 5000],
      ];
      if (peso === null || grasa === null || musculo === null) {
        throw new Error('Completa peso, grasa corporal y masa muscular.');
      }
      const invalid = validations.find(([, value, min, max]) => value !== null && (value < min || value > max));
      if (invalid) throw new Error(`${invalid[0]} debe estar entre ${invalid[2]} y ${invalid[3]}.`);
      if (correctionForm.motivo.trim().length < 5) {
        throw new Error('Explica brevemente el motivo de la corrección.');
      }

      const resumen: Record<string, unknown> = { ...(correctionReport.resumen || {}) };
      const optionalValues: Array<[string, string]> = [
        ['imc', correctionForm.imc],
        ['pbf', correctionForm.pbf],
        ['grasa_visceral', correctionForm.grasa_visceral],
        ['tmb', correctionForm.tmb],
      ];
      optionalValues.forEach(([key, raw]) => {
        const parsed = parseOptionalNumber(raw);
        if (parsed === null) delete resumen[key];
        else resumen[key] = parsed;
      });

      const { error } = await supabase.rpc('corregir_evaluacion_corporal', {
        p_reporte_id: correctionReport.id,
        p_peso: peso,
        p_grasa_corporal: grasa,
        p_masa_muscular: musculo,
        p_resumen: resumen,
        p_motivo: correctionForm.motivo.trim(),
      });
      if (error) throw error;

      setCorrectionReport(null);
      setReportesNotice('Evaluación corregida. El cambio quedó registrado en la auditoría.');
      await Promise.all([fetchProgreso(selectedUsuario.id), fetchProgresoReportes(selectedUsuario.id)]);
    } catch (err: any) {
      setReportesError(err?.message || 'No se pudo guardar la corrección.');
    } finally {
      setSavingCorrection(false);
    }
  };

  const resetForm = () => {
    setFormData({
      fecha: new Date().toISOString().split('T')[0],
      peso: '',
      grasa_corporal: '',
      masa_muscular: '',
      notas: '',
    });
  };

  const filteredUsuarios = usuarios.filter((usuario) =>
    usuario.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
    usuario.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const entrenamientosByDay = entrenamientos.reduce((acc, e) => {
    const day = e.fecha;
    if (!acc[day]) acc[day] = [];
    acc[day].push(e);
    return acc;
  }, {} as Record<string, Entrenamiento[]>);

  const daysEntrenados = Object.keys(entrenamientosByDay).sort((a, b) => (a < b ? 1 : -1));
  const totalEntrenamientosPages = Math.max(1, Math.ceil(daysEntrenados.length / ENTRENAMIENTOS_DIAS_POR_PAGINA));
  const entrenamientosPageSafe = Math.min(entrenamientosPage, totalEntrenamientosPages);
  const entrenamientosStart = (entrenamientosPageSafe - 1) * ENTRENAMIENTOS_DIAS_POR_PAGINA;
  const entrenamientosEnd = entrenamientosStart + ENTRENAMIENTOS_DIAS_POR_PAGINA;
  const daysEntrenadosPage = daysEntrenados.slice(entrenamientosStart, entrenamientosEnd);

  const getDayStats = (day: string) => {
    const sessions = entrenamientosByDay[day] || [];
    const ejercicios = sessions.flatMap((s) => s.entrenamiento_ejercicios || []);
    const completados = ejercicios.filter((x) => x.completado);
    const tonnage = completados.reduce((sum, ex) => {
      const peso = Number(ex.peso_usado ?? ex.peso_plan ?? 0) || 0;
      const series = Number(ex.series ?? 0) || 0;
      const reps = Number(ex.repeticiones ?? 0) || 0;
      return sum + peso * series * reps;
    }, 0);
    return {
      sesiones: sessions.length,
      ejercicios: completados.length,
      tonnage,
    };
  };

  const ejerciciosCompletados = entrenamientos.flatMap((s) =>
    (s.entrenamiento_ejercicios || []).filter((x) => x.completado)
  );
  const endDate = new Date();
  endDate.setHours(23, 59, 59, 999);
  const startDate = new Date(endDate);
  startDate.setDate(startDate.getDate() - (selectedRangeDays - 1));
  startDate.setHours(0, 0, 0, 0);
  const ejerciciosDisponibles = Array.from(
    new Set(
      ejerciciosCompletados
        .map((e) => String(e.nombre || '').trim())
        .filter(Boolean)
    )
  ).sort((a, b) => a.localeCompare(b, 'es'));

  const ejercicioProgresoSeries = daysEntrenados
    .slice()
    .reverse()
    .filter((day) => {
      const d = parseISODateLocal(day);
      return d >= startDate && d <= endDate;
    })
    .map((day) => {
      const sessions = entrenamientosByDay[day] || [];
      const ejerciciosDia = sessions
        .flatMap((s) => s.entrenamiento_ejercicios || [])
        .filter((x) => x.completado)
        .filter((x) =>
          selectedEjercicioProgreso === 'todos'
            ? true
            : String(x.nombre || '').trim() === selectedEjercicioProgreso
        );

      const volumen = ejerciciosDia.reduce((sum, ex) => {
        const peso = Number(ex.peso_usado ?? ex.peso_plan ?? 0) || 0;
        const series = Number(ex.series ?? 0) || 0;
        const reps = Number(ex.repeticiones ?? 0) || 0;
        return sum + peso * series * reps;
      }, 0);

      const maxPeso = ejerciciosDia.reduce((max, ex) => {
        const peso = Number(ex.peso_usado ?? ex.peso_plan ?? 0) || 0;
        return Math.max(max, peso);
      }, 0);

      const repsTotales = ejerciciosDia.reduce((sum, ex) => {
        const series = Number(ex.series ?? 0) || 0;
        const reps = Number(ex.repeticiones ?? 0) || 0;
        return sum + series * reps;
      }, 0);

      return {
        day,
        volumen,
        maxPeso,
        repsTotales,
        ejercicios: ejerciciosDia.length,
      };
    });

  const ejercicioProgresoSeriesRango = ejercicioProgresoSeries;
  const ejercicioProgresoSeriesRecientes = ejercicioProgresoSeriesRango;
  const maxVolumenEjercicio = Math.max(1, ...ejercicioProgresoSeriesRecientes.map((r) => r.volumen));
  const maxPesoEjercicio = Math.max(1, ...ejercicioProgresoSeriesRecientes.map((r) => r.maxPeso));
  const rangeDaysSet = new Set(ejercicioProgresoSeriesRango.map((r) => r.day));
  const resumenMap = new Map<string, number>();
  for (const day of rangeDaysSet) {
    const sessions = entrenamientosByDay[day] || [];
    const ejercicios = sessions.flatMap((s) => s.entrenamiento_ejercicios || []).filter((x) => x.completado);
    for (const ex of ejercicios) {
      const nombre = String(ex.nombre || '').trim();
      if (!nombre) continue;
      const peso = Number(ex.peso_usado ?? ex.peso_plan ?? 0) || 0;
      const series = Number(ex.series ?? 0) || 0;
      const reps = Number(ex.repeticiones ?? 0) || 0;
      const volumen = peso * series * reps;
      resumenMap.set(nombre, (resumenMap.get(nombre) || 0) + volumen);
    }
  }
  const resumenPorEjercicio = Array.from(resumenMap.entries())
    .map(([nombre, volumen]) => ({ nombre, volumen }))
    .sort((a, b) => b.volumen - a.volumen)
    .slice(0, 8);
  const maxResumenEj = Math.max(1, ...resumenPorEjercicio.map((r) => r.volumen));

  // Calcular estadísticas desde datos reales
  const entrenamientosEsteMes = (() => {
    const hoy = new Date();
    if (daysEntrenados.length > 0) {
      return daysEntrenados.filter((d) => {
        const fecha = parseISODateLocal(d);
        return fecha.getMonth() === hoy.getMonth() && fecha.getFullYear() === hoy.getFullYear();
      }).length;
    }
    // Fallback (legacy): use "progreso" entries as proxy.
    return progreso.filter((p) => {
      const fecha = parseISODateLocal(p.fecha);
      return fecha.getMonth() === hoy.getMonth() && fecha.getFullYear() === hoy.getFullYear();
    }).length;
  })();

  const entrenamientosMesAnterior = (() => {
    const inicioMesActual = new Date();
    inicioMesActual.setDate(1);
    inicioMesActual.setHours(0, 0, 0, 0);
    const inicioMesAnterior = new Date(inicioMesActual);
    inicioMesAnterior.setMonth(inicioMesAnterior.getMonth() - 1);
    return daysEntrenados.filter((day) => {
      const fecha = parseISODateLocal(day);
      return fecha >= inicioMesAnterior && fecha < inicioMesActual;
    }).length;
  })();
  const diferenciaEntrenamientosMes = entrenamientosEsteMes - entrenamientosMesAnterior;

  const calcularRachas = () => {
    const uniqueDays = [...new Set(daysEntrenados)].sort();
    if (!uniqueDays.length) return { actual: 0, mejor: 0 };
    let mejor = 1;
    let seguida = 1;
    for (let index = 1; index < uniqueDays.length; index += 1) {
      const previous = parseISODateLocal(uniqueDays[index - 1]);
      const current = parseISODateLocal(uniqueDays[index]);
      const diferencia = Math.round((current.getTime() - previous.getTime()) / 86400000);
      seguida = diferencia === 1 ? seguida + 1 : 1;
      mejor = Math.max(mejor, seguida);
    }
    const hoy = new Date(); hoy.setHours(0, 0, 0, 0);
    const ultimo = parseISODateLocal(uniqueDays[uniqueDays.length - 1]);
    const diasDesdeUltimo = Math.round((hoy.getTime() - ultimo.getTime()) / 86400000);
    let actual = 0;
    if (diasDesdeUltimo === 0 || diasDesdeUltimo === 1) {
      actual = 1;
      for (let index = uniqueDays.length - 1; index > 0; index -= 1) {
        const current = parseISODateLocal(uniqueDays[index]);
        const previous = parseISODateLocal(uniqueDays[index - 1]);
        if (Math.round((current.getTime() - previous.getTime()) / 86400000) !== 1) break;
        actual += 1;
      }
    }
    return { actual, mejor };
  };
  const rachasEntrenamiento = calcularRachas();

  useEffect(() => {
    const suggestedPeso = progreso.length > 0 && Number.isFinite(Number(progreso[0].peso))
      ? String(Number(progreso[0].peso))
      : '';
    setNutriPesoKg(suggestedPeso);
    const edad = getAgeYears(selectedUsuario?.fecha_nacimiento);
    if (edad) setNutriEdad(String(edad));
  }, [selectedUsuario?.id, progreso]);

  const ACTIVITY_FACTORS: Record<'sedentario' | 'ligero' | 'moderado' | 'activo' | 'muy_activo', { label: string; desc: string; factor: number }> = {
    sedentario: { label: 'Sedentario', desc: 'Poco o ningun ejercicio', factor: 1.2 },
    ligero: { label: 'Ligero', desc: '1 a 3 dias por semana', factor: 1.375 },
    moderado: { label: 'Moderado', desc: '3 a 5 dias por semana', factor: 1.55 },
    activo: { label: 'Activo', desc: '6 a 7 dias por semana', factor: 1.725 },
    muy_activo: { label: 'Muy activo', desc: 'Entrenamientos intensos o trabajo fisico', factor: 1.9 },
  };

  const toNum = (v: string) => Number(v.replace(',', '.'));
  const pesoNum = toNum(nutriPesoKg);
  const alturaNum = toNum(nutriAlturaCm);
  const edadNum = toNum(nutriEdad);
  const factorActividad = ACTIVITY_FACTORS[nutriActividad].factor;
  const canComputeNutri = [pesoNum, alturaNum, edadNum].every((n) => Number.isFinite(n) && n > 0);
  const tmbCalculada = !canComputeNutri
    ? null
    : nutriFormula === 'mifflin_st_jeor'
      ? (10 * pesoNum) + (6.25 * alturaNum) - (5 * edadNum) + (nutriSexo === 'hombre' ? 5 : -161)
      : nutriSexo === 'hombre'
        ? 88.362 + (13.397 * pesoNum) + (4.799 * alturaNum) - (5.677 * edadNum)
        : 447.593 + (9.247 * pesoNum) + (3.098 * alturaNum) - (4.33 * edadNum);
  const getCalculado = tmbCalculada === null ? null : tmbCalculada * factorActividad;
  const caloriasObjetivo = getCalculado === null ? null : getCalculado * (1 + nutriAjuste / 100);
  const macrosCalculados = caloriasObjetivo === null ? null : {
    proteinas: (caloriasObjetivo * 0.30) / 4,
    grasas: (caloriasObjetivo * 0.30) / 9,
    carbohidratos: (caloriasObjetivo * 0.40) / 4,
  };

  const sumarVolumenRango = (desde: Date, hasta: Date) => daysEntrenados.reduce((sum, day) => {
    const fecha = parseISODateLocal(day);
    return fecha >= desde && fecha <= hasta ? sum + getDayStats(day).tonnage : sum;
  }, 0);
  const finSemanaActual = new Date(); finSemanaActual.setHours(23, 59, 59, 999);
  const inicioSemanaActual = new Date(finSemanaActual); inicioSemanaActual.setDate(inicioSemanaActual.getDate() - 6); inicioSemanaActual.setHours(0, 0, 0, 0);
  const finSemanaAnterior = new Date(inicioSemanaActual); finSemanaAnterior.setMilliseconds(-1);
  const inicioSemanaAnterior = new Date(finSemanaAnterior); inicioSemanaAnterior.setDate(inicioSemanaAnterior.getDate() - 6); inicioSemanaAnterior.setHours(0, 0, 0, 0);
  const volumenTotal = Math.round(sumarVolumenRango(inicioSemanaActual, finSemanaActual));
  const volumenSemanaAnterior = Math.round(sumarVolumenRango(inicioSemanaAnterior, finSemanaAnterior));
  const variacionVolumen = volumenSemanaAnterior > 0
    ? Math.round(((volumenTotal - volumenSemanaAnterior) / volumenSemanaAnterior) * 100)
    : null;

  const planSeleccionado = planesReferencia.find((plan) =>
    plan.nombre.trim().toLocaleLowerCase('es-CL') === selectedUsuario?.tipo_membresia?.trim().toLocaleLowerCase('es-CL')
  );
  const metaSesionesPlan = Number(planSeleccionado?.max_sesiones_periodo || 0);
  const progresoRutina = metaSesionesPlan > 0
    ? Math.min((entrenamientosEsteMes / metaSesionesPlan) * 100, 100)
    : 0;

  // Datos para gráficos (agrupados por semana del mes de referencia).
  const weekOfMonth = (date: Date) => Math.floor((date.getDate() - 1) / 7) + 1;
  const getLatestDataDate = () => {
    const latestEntrenamiento = daysEntrenados.length > 0 ? parseISODateLocal(daysEntrenados[0]) : null;
    const latestProgreso = progreso.length > 0 ? parseISODateLocal(progreso[0].fecha) : null;
    if (!latestEntrenamiento && !latestProgreso) return new Date();
    if (!latestEntrenamiento) return latestProgreso as Date;
    if (!latestProgreso) return latestEntrenamiento;
    return latestEntrenamiento > latestProgreso ? latestEntrenamiento : latestProgreso;
  };

  const referenceDate = getLatestDataDate();
  const referenceMonth = referenceDate.getMonth();
  const referenceYear = referenceDate.getFullYear();

  const volumenByWeek = new Map<number, number>();
  for (const day of daysEntrenados) {
    const fecha = parseISODateLocal(day);
    if (fecha.getMonth() !== referenceMonth || fecha.getFullYear() !== referenceYear) continue;
    const week = weekOfMonth(fecha);
    const current = volumenByWeek.get(week) || 0;
    volumenByWeek.set(week, current + getDayStats(day).tonnage);
  }

  const pesoByWeek = new Map<number, number>();
  const progresoMesOrdenado = [...progreso]
    .filter((p) => {
      const fecha = parseISODateLocal(p.fecha);
      return fecha.getMonth() === referenceMonth && fecha.getFullYear() === referenceYear;
    })
    .sort((a, b) => (a.fecha > b.fecha ? 1 : -1));
  for (const row of progresoMesOrdenado) {
    const week = weekOfMonth(parseISODateLocal(row.fecha));
    // Conserva la última medición de esa semana.
    pesoByWeek.set(week, row.peso);
  }

  const weeklyKeys = Array.from(new Set([...volumenByWeek.keys(), ...pesoByWeek.keys()])).sort((a, b) => a - b);
  const weeklySeries = weeklyKeys.map((week) => ({
    week,
    volumen: volumenByWeek.get(week) || 0,
    peso: pesoByWeek.has(week) ? (pesoByWeek.get(week) as number) : null,
  }));

  const maxVolumen = Math.max(1, ...weeklySeries.map((w) => w.volumen));
  const pesosSemanales = weeklySeries
    .map((w) => w.peso)
    .filter((v): v is number => typeof v === 'number' && Number.isFinite(v));
  const minPeso = pesosSemanales.length > 0 ? Math.min(...pesosSemanales) - 5 : 0;
  const maxPeso = pesosSemanales.length > 0 ? Math.max(...pesosSemanales) + 5 : 1;
  const rangoPeso = Math.max(1, maxPeso - minPeso);

  const getResumenLabel = (resumen: Record<string, unknown> | null) => {
    if (!resumen || typeof resumen !== 'object') return 'Sin resumen';
    const piezas: string[] = [];
    const pick = (key: string) => parseOptionalNumber(String((resumen as Record<string, unknown>)[key] ?? ''));
    const imc = pick('imc');
    const pbf = pick('pbf');
    const visceral = pick('grasa_visceral');
    const tmb = pick('tmb');

    if (imc !== null) piezas.push(`IMC ${imc.toFixed(1)}`);
    if (pbf !== null) piezas.push(`PBF ${pbf.toFixed(1)}%`);
    if (visceral !== null) piezas.push(`Visceral ${visceral.toFixed(1)}`);
    if (tmb !== null) piezas.push(`TMB ${Math.round(tmb)} kcal`);
    return piezas.length > 0 ? piezas.join(' · ') : 'Sin resumen';
  };

  const formatBytes = (value: number | null) => {
    if (!value || value <= 0) return '-';
    if (value < 1024) return `${value} B`;
    if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KB`;
    return `${(value / (1024 * 1024)).toFixed(2)} MB`;
  };

  const getArchivoLabel = (reporte: ProgresoReporte) => {
    const mime = String(reporte.mime_type || '').toLowerCase();
    if (reporte.fuente === 'fitmao' || mime.includes('text/html') || mime.includes('text/plain')) {
      return 'URL externa (FitMao)';
    }
    if (!mime) return 'Archivo';
    return mime;
  };

  const getSesionesRutina = (rutina: RutinaPersonalizada) => {
    const ejercicios = rutina.ejercicios;
    if (!ejercicios || typeof ejercicios !== 'object') return 0;
    const sesiones = (ejercicios as Record<string, unknown>).sesiones;
    return Array.isArray(sesiones) ? sesiones.length : 0;
  };

  const isPautaNutricional = (reporte: ProgresoReporte) => {
    const tipo = String((reporte.meta as any)?.tipo || '').toLowerCase();
    return tipo === 'pauta_nutricional';
  };

  const reportesNutricion = reportes.filter(isPautaNutricional);
  const reportesComposicion = reportes.filter((r) => !isPautaNutricional(r));

  const guardarCalculoNutricional = async () => {
    if (!selectedUsuario) {
      alert('Selecciona un usuario');
      return;
    }
    if (!canComputeNutri || tmbCalculada === null || getCalculado === null) {
      alert('Completa peso, altura y edad con valores válidos');
      return;
    }

    try {
      setGuardandoNutri(true);
      setReportesError(null);
      setReportesNotice(null);

      const { error } = await supabase.from('nutricion_calculos').insert({
        usuario_id: selectedUsuario.id,
        sexo: nutriSexo,
        peso_kg: pesoNum,
        altura_cm: alturaNum,
        edad: edadNum,
        actividad: nutriActividad,
        factor_actividad: factorActividad,
        tmb: Number(tmbCalculada.toFixed(2)),
        get_kcal: Number(getCalculado.toFixed(2)),
        formula: nutriFormula,
        ajuste_porcentaje: nutriAjuste,
        calorias_objetivo: caloriasObjetivo === null ? null : Number(caloriasObjetivo.toFixed(2)),
        proteinas_g: macrosCalculados === null ? null : Number(macrosCalculados.proteinas.toFixed(2)),
        grasas_g: macrosCalculados === null ? null : Number(macrosCalculados.grasas.toFixed(2)),
        carbohidratos_g: macrosCalculados === null ? null : Number(macrosCalculados.carbohidratos.toFixed(2)),
      });

      if (error) throw error;
      setReportesNotice('Cálculo nutricional guardado correctamente.');
      await fetchNutricionCalculos(selectedUsuario.id);
    } catch (err: any) {
      console.error('Error guardando calculo nutricional:', err);
      setReportesError(err?.message || 'No se pudo guardar el cálculo nutricional');
    } finally {
      setGuardandoNutri(false);
    }
  };

  const handleUploadPautaNutricional = async (file: File) => {
    if (!selectedUsuario) return;
    if (!sucursalActiva?.gimnasio_id) {
      setReportesError('No hay gimnasio activo para subir la pauta.');
      return;
    }
    const isPdf = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
    if (!isPdf) {
      setReportesError('Solo se permite archivo PDF para pauta nutricional.');
      return;
    }
    if (file.size > 15 * 1024 * 1024) {
      setReportesError('El PDF supera el límite de 15MB.');
      return;
    }

    try {
      setUploadingPauta(true);
      setReportesError(null);
      setReportesNotice(null);

      const now = Date.now();
      const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
      const storagePath = `${sucursalActiva.gimnasio_id}/${selectedUsuario.id}/pauta-${now}-${safeName}`;

      const { error: uploadError } = await supabase.storage
        .from('progreso-reportes')
        .upload(storagePath, file, {
          cacheControl: '3600',
          upsert: false,
          contentType: 'application/pdf',
        });
      if (uploadError) throw uploadError;

      const today = new Date().toISOString().split('T')[0];
      const { error: insertError } = await supabase.from('progreso_reportes').insert({
        usuario_id: selectedUsuario.id,
        fecha_medicion: today,
        fuente: 'manual',
        fuente_url: '',
        storage_bucket: 'progreso-reportes',
        storage_path: storagePath,
        mime_type: 'application/pdf',
        file_size: file.size,
        resumen: {},
        meta: {
          tipo: 'pauta_nutricional',
          nombre_archivo: file.name,
          uploaded_from: 'web_progreso',
        },
      });
      if (insertError) throw insertError;

      setReportesNotice('Pauta nutricional subida correctamente.');
      await fetchProgresoReportes(selectedUsuario.id);
    } catch (err: any) {
      setReportesError(err?.message || 'No se pudo subir la pauta nutricional');
    } finally {
      setUploadingPauta(false);
    }
  };

  const cambiosRutinaProximos = usuarios
    .filter((usuario) => usuario.estado.toLocaleLowerCase('es-CL') === 'activo' && !!usuario.fecha_cambio_rutina)
    .map((usuario) => {
      const fecha = new Date(`${usuario.fecha_cambio_rutina}T12:00:00`);
      const today = new Date(); today.setHours(12, 0, 0, 0);
      return { usuario, fecha, dias: Math.ceil((fecha.getTime() - today.getTime()) / 86400000) };
    })
    .filter((item) => !Number.isNaN(item.fecha.getTime()) && item.dias >= -7 && item.dias <= 60)
    .sort((a, b) => a.fecha.getTime() - b.fecha.getTime());

  const selectUsuarioById = (usuarioId: string) => {
    const usuario = usuarios.find((item) => item.id === usuarioId);
    if (usuario) setSelectedUsuario(usuario);
  };

  const openAcomaSuggestionForUsuario = async (usuario: Usuario, tipo: 'nueva' | 'renovacion' = 'nueva') => {
    setSelectedUsuario(usuario);
    setShowUserSelector(false);
    setReportesError(null);
    setReportesNotice(null);

    if (usuario.estado.toLocaleLowerCase('es-CL') !== 'activo') {
      setReportesError('El usuario debe estar activo para solicitar una propuesta de rutina.');
      return;
    }

    try {
      setLoadingRecomendacionesIA(true);
      setEvaluacionInicialAcoma(null);
      const [pendingResult, evaluationResult] = await Promise.all([
        supabase
          .from('rutina_recomendaciones_ia')
          .select(RECOMENDACION_IA_SELECT)
          .eq('usuario_id', usuario.id)
          .eq('estado', 'pendiente')
          .order('created_at', { ascending: false }),
        supabase
          .from('evaluaciones_iniciales')
          .select('id,estado,fecha_evaluacion')
          .eq('usuario_id', usuario.id)
          .in('estado', ['completa', 'requiere_revision'])
          .order('fecha_evaluacion', { ascending: false })
          .order('finalizada_at', { ascending: false })
          .limit(1)
          .maybeSingle(),
      ]);
      if (pendingResult.error) throw pendingResult.error;
      if (evaluationResult.error) throw evaluationResult.error;

      const pendientes = (pendingResult.data || []) as unknown as RecomendacionRutinaIA[];
      setEvaluacionInicialAcoma(evaluationResult.data as EvaluacionInicialAcoma | null);
      setRecomendacionesIA(pendientes);
      if (pendientes.length > 0) {
        setReportesNotice('Este cliente ya tiene una propuesta de ACOMA pendiente. Revísala antes de solicitar otra.');
        return;
      }

      if (tipo === 'renovacion' && rutinasAsignadas.length === 0) {
        setReportesError('Este usuario todavía no tiene una rutina activa para renovar.');
        return;
      }
      setAcomaForm({
        tipo_sugerencia: tipo,
        objetivo: tipo === 'renovacion' ? 'Renovar la rutina actual según su adherencia, progreso y limitaciones vigentes.' : '',
        nivel: 'intermedio',
        frecuencia_semanal: 3,
        duracion_minutos: 60,
        observaciones: '',
      });
      setShowAcomaSuggestionModal(true);
    } catch (err: any) {
      setReportesError(err?.message || 'No pudimos comprobar las propuestas pendientes de ACOMA.');
    } finally {
      setLoadingRecomendacionesIA(false);
    }
  };

  const rutinasDisponiblesFiltradas = rutinasDisponibles.filter((rutina) => {
    const term = rutinaAssignSearch.trim().toLocaleLowerCase('es-CL');
    return !term
      || rutina.nombre.toLocaleLowerCase('es-CL').includes(term)
      || (rutina.descripcion || '').toLocaleLowerCase('es-CL').includes(term);
  });
  const historialIATotalPaginas = Math.max(1, Math.ceil(historialIATotal / HISTORIAL_IA_POR_PAGINA));

  return (
    <AdminLayout>
      <div className="p-8">
        <div className="flex flex-col xl:flex-row xl:justify-between xl:items-start gap-5 mb-6">
          <div className="flex items-center gap-4">
            {isStaff && <div className="w-14 h-14 rounded-full bg-emerald-50 text-green-900 flex items-center justify-center"><i className="ri-boxing-line text-3xl"></i></div>}
            <div>
            <h1 className="text-3xl font-bold text-gray-900">{isStaff ? 'Panel del Entrenador' : 'Progreso de Usuarios'}</h1>
            <p className="text-gray-600 mt-1">{isStaff ? 'Seguimiento, planificación y progreso de tus usuarios.' : 'Seguimiento de evolución y métricas'}</p>
            </div>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <button
              onClick={() => setShowEvaluacionesAgenda(true)}
              disabled={!sucursalActiva?.id}
              className="bg-amber-500 text-white px-5 py-3 rounded-lg hover:bg-amber-600 disabled:opacity-50 transition-colors flex items-center gap-2 whitespace-nowrap"
            >
              <i className="ri-calendar-check-line"></i>
              Agenda evaluaciones
            </button>
            <button
              onClick={() => {
                if (!selectedUsuario) return;
                setReportesError(null);
                setReportesNotice(null);
                setShowImportModal(true);
              }}
              disabled={!selectedUsuario}
              className="bg-blue-500 text-white px-5 py-3 rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2 whitespace-nowrap"
            >
              <i className="ri-download-cloud-2-line"></i>
              Importar FitMao
            </button>
            <button
              onClick={() => setShowModal(true)}
              disabled={!selectedUsuario}
              className="bg-teal-500 text-white px-6 py-3 rounded-lg hover:bg-teal-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2 whitespace-nowrap"
            >
              <i className="ri-add-line"></i>
              Registrar Progreso
            </button>
          </div>
        </div>

        {(cambiosRutinaProximos.length > 0 || evaluacionesProximas.length > 0) && (
          <button onClick={() => setShowUpcomingSummary(true)} className="mb-6 inline-flex items-center gap-3 px-4 py-3 rounded-lg border border-amber-200 bg-amber-50 text-amber-800 font-medium hover:bg-amber-100">
            <i className="ri-time-line text-lg"></i>
            Tienes {cambiosRutinaProximos.length} cambio{cambiosRutinaProximos.length === 1 ? '' : 's'} de rutina próximo{cambiosRutinaProximos.length === 1 ? '' : 's'} y {evaluacionesProximas.length} evaluación{evaluacionesProximas.length === 1 ? '' : 'es'} pendiente{evaluacionesProximas.length === 1 ? '' : 's'}.
            <span className="ml-3 text-gray-900">Ver todo →</span>
          </button>
        )}

        <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,2fr)_minmax(360px,1fr)] gap-6 items-start">
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 items-start gap-5">
            <div className="flex items-center gap-4 min-w-0">
              <div className="w-16 h-16 rounded-full bg-teal-100 flex items-center justify-center">
                <span className="text-xl font-bold text-teal-600">{selectedUsuario?.avatar || ''}</span>
              </div>
              <div className="min-w-0">
                <h1 className="text-xl font-bold text-gray-900">{selectedUsuario?.nombre || 'Sin usuario seleccionado'}</h1>
                <p className="text-sm text-gray-500">{selectedUsuario?.email || ''}</p>
                <div className="mt-2 space-y-1">
                  <p className="text-xs text-gray-700">
                    <span className="font-semibold">Coach:</span>{' '}
                    {getCoachNombre(selectedUsuario?.profesor_admin_id)}
                  </p>
                  <p className="text-xs text-gray-700">
                    <span className="font-semibold">Cambio de rutina:</span>{' '}
                    {selectedUsuario?.fecha_cambio_rutina
                      ? new Date(`${selectedUsuario.fecha_cambio_rutina}T00:00:00`).toLocaleDateString('es-ES')
                      : 'No programado'}
                  </p>
                  <p className="text-xs text-gray-700">
                    <span className="font-semibold">Aceptó reglamento:</span>{' '}
                    {reglamentoEstado
                      ? `${reglamentoEstado.aceptado ? 'Sí' : 'No'}${reglamentoEstado.version ? ` (v${reglamentoEstado.version})` : ''}${reglamentoEstado.fecha ? ` · ${new Date(reglamentoEstado.fecha).toLocaleDateString('es-ES')}` : ''}`
                      : 'No disponible'}
                  </p>
                </div>
                <div className="flex items-center gap-2 mt-1">
                  <span className="px-2 py-0.5 bg-teal-100 text-teal-700 rounded text-xs font-medium">
                    {selectedUsuario?.tipo_membresia || 'Sin plan'}
                  </span>
                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                    selectedUsuario?.estado === 'activo' ? 'bg-green-100 text-green-700' :
                    selectedUsuario?.estado === 'vencido' ? 'bg-red-100 text-red-700' :
                    'bg-gray-100 text-gray-700'
                  }`}>
                    {selectedUsuario?.estado.charAt(0).toUpperCase() + selectedUsuario?.estado.slice(1) || ''}
                  </span>
                </div>
              </div>
            </div>

            <div className={`rounded-xl border p-4 min-w-0 ${selectedUsuario?.declaracion_salud?.estado === 'con_condicion' ? 'border-amber-200 bg-amber-50' : 'border-emerald-200 bg-emerald-50'}`}>
              <div className="flex items-start gap-3">
                <div className={`w-10 h-10 shrink-0 rounded-full grid place-items-center ${selectedUsuario?.declaracion_salud?.estado === 'con_condicion' ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'}`}>
                  <i className="ri-heart-pulse-line text-xl" />
                </div>
                <div className="min-w-0">
                  <p className="font-bold text-gray-900">Condición física y salud</p>
                  {!selectedUsuario ? (
                    <p className="text-sm text-gray-500 mt-1">Selecciona un usuario.</p>
                  ) : selectedUsuario.declaracion_salud?.estado === 'con_condicion' ? (
                    <div className="mt-2 space-y-2 text-sm">
                      <p><span className="font-semibold text-amber-900">Antecedente:</span> <span className="text-amber-900">{selectedUsuario.declaracion_salud.detalle?.trim() || 'Condición informada sin detalle.'}</span></p>
                      <p><span className="font-semibold text-amber-900">Restricciones:</span> <span className="text-amber-900">{selectedUsuario.declaracion_salud.restricciones?.trim() || 'No informó restricciones específicas.'}</span></p>
                    </div>
                  ) : selectedUsuario.declaracion_salud?.estado === 'sin_condicion' ? (
                    <p className="text-sm text-emerald-800 mt-2">Declara no conocer una condición que le impida entrenar.</p>
                  ) : (
                    <p className="text-sm text-gray-600 mt-2">Sin declaración de salud registrada.</p>
                  )}
                  {(selectedUsuario?.contacto_emergencia?.nombre || selectedUsuario?.contacto_emergencia?.telefono) && (
                    <p className="text-xs text-gray-600 mt-3 pt-2 border-t border-black/10">
                      <span className="font-semibold">Emergencia:</span>{' '}
                      {[selectedUsuario.contacto_emergencia.nombre, selectedUsuario.contacto_emergencia.telefono].filter(Boolean).join(' · ')}
                    </p>
                  )}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-3 w-full lg:col-span-2 pt-5 border-t border-gray-100">
              {!isCoach && <button
                onClick={openCoachModal}
                disabled={!selectedUsuario}
                className="bg-indigo-500 text-white px-4 py-2 rounded-lg hover:bg-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2 whitespace-nowrap"
              >
                <i className="ri-user-star-line"></i>
                Asignar coach
              </button>}
              <button
                onClick={() => void openRutinaAssignModal()}
                disabled={!selectedUsuario || selectedUsuario.estado.toLocaleLowerCase('es-CL') !== 'activo'}
                className="bg-teal-600 text-white px-4 py-2 rounded-lg hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2 whitespace-nowrap"
              >
                <i className="ri-file-add-line"></i>
                Asignar rutina
              </button>
              <button
                onClick={() => selectedUsuario && void openAcomaSuggestionForUsuario(selectedUsuario)}
                disabled={!selectedUsuario || selectedUsuario.estado.toLocaleLowerCase('es-CL') !== 'activo' || recomendacionesIA.length > 0}
                className="bg-violet-600 text-white px-4 py-2 rounded-lg hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2 whitespace-nowrap"
                title={recomendacionesIA.length > 0 ? 'Primero resuelve la recomendación pendiente' : 'Crear una propuesta para revisión del coach'}
              >
                <i className="ri-sparkling-2-line"></i>
                Sugerir con IA
              </button>
              {(!isStaff || isCoach) && <button
                onClick={() => selectedUsuario && openFamilySuggestionForUsuario(selectedUsuario)}
                disabled={!selectedUsuario || selectedUsuario.estado.toLocaleLowerCase('es-CL') !== 'activo'}
                className="bg-teal-800 text-white px-4 py-2 rounded-lg hover:bg-teal-900 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2 whitespace-nowrap"
                title="ACOMA generará una familia completa en segundo plano"
              >
                <i className="ri-node-tree"></i>
                Crear familia IA
              </button>}
              <button
                onClick={() => selectedUsuario && void openAcomaSuggestionForUsuario(selectedUsuario, 'renovacion')}
                disabled={!selectedUsuario || selectedUsuario.estado.toLocaleLowerCase('es-CL') !== 'activo' || recomendacionesIA.length > 0 || rutinasAsignadas.length === 0}
                className="bg-sky-600 text-white px-4 py-2 rounded-lg hover:bg-sky-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2 whitespace-nowrap"
                title={rutinasAsignadas.length === 0 ? 'El usuario necesita una rutina activa' : recomendacionesIA.length > 0 ? 'Primero resuelve la recomendación pendiente' : 'Analizar la rutina actual y proponer su renovación'}
              >
                <i className="ri-refresh-line"></i>
                Renovar con IA
              </button>
              <button
                onClick={openRutinaChangeModal}
                disabled={!selectedUsuario}
                className="bg-amber-500 text-white px-4 py-2 rounded-lg hover:bg-amber-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2 whitespace-nowrap"
              >
                <i className="ri-calendar-event-line"></i>
                Cambio de rutina
              </button>
              <button
                onClick={() => setShowUserSelector(true)}
                className="bg-teal-500 text-white px-4 py-2 rounded-lg hover:bg-teal-600 transition-colors flex items-center justify-center gap-2 whitespace-nowrap"
              >
                <i className="ri-user-search-line"></i>
                Cambiar Usuario
              </button>
            </div>
          </div>
        </div>

        {selectedUsuario && canViewInitialEvaluation && <EvaluacionInicialPanel usuario={selectedUsuario} />}

        <section className="bg-white rounded-xl border border-teal-200 shadow-sm overflow-hidden xl:col-start-1">
          <div className="px-5 py-4 border-b border-teal-100 bg-teal-50">
            <h2 className="font-bold text-teal-950 flex items-center gap-2"><i className="ri-node-tree" />Familia de rutinas</h2>
            <p className="text-xs text-teal-700 mt-1">Secuencia de rutinas y cambios controlados por el coach.</p>
          </div>
          <div className="p-5">
            {familiaAsignada ? (
              <div className="space-y-4">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                  <div>
                    <p className="font-bold text-gray-900">{familiaAsignada.familia_nombre_snapshot || familiaAsignada.familia?.nombre || 'Familia asignada'}</p>
                    <p className="text-sm text-gray-600 mt-1">Etapa {familiaAsignada.etapa_snapshot?.orden || familiaAsignada.etapa?.orden || '-'} · {familiaAsignada.etapa_snapshot?.nombre_rutina_snapshot || familiaAsignada.etapa?.rutina?.nombre || 'Rutina actual'} · {familiaAsignada.etapa_snapshot?.duracion_dias || familiaAsignada.etapa?.duracion_dias || '-'} días</p>
                    <p className="text-xs text-gray-400 mt-1">Iniciada {new Date(familiaAsignada.etapa_iniciada_at).toLocaleDateString('es-CL')}</p>
                  </div>
                  <div className="flex items-center gap-2 self-start"><span className="px-3 py-1 rounded-full bg-green-100 text-green-700 text-xs font-bold">Activa</span><button onClick={abrirEdicionFamiliaAsignada} className="px-3 py-2 rounded-lg border border-teal-300 bg-white text-teal-800 text-sm font-semibold hover:bg-teal-50"><i className="ri-edit-line mr-1" />Editar familia</button></div>
                </div>
                {editandoFamiliaAsignada && familiaAsignadaDraft && <div className="rounded-xl border border-teal-200 bg-teal-50 p-4 space-y-4">
                  <div className="flex items-start justify-between gap-3"><div><p className="font-bold text-teal-950">Editar copia de {selectedUsuario?.nombre}</p><p className="text-xs text-teal-700 mt-1">Los cambios afectan solamente a este socio, no a la plantilla maestra.</p></div><button onClick={() => { setEditandoFamiliaAsignada(false); setFamiliaAsignadaDraft(null); }} className="w-9 h-9 rounded-lg hover:bg-white" aria-label="Cerrar edición"><i className="ri-close-line text-xl" /></button></div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <label className="text-xs font-semibold text-gray-700">Nombre<input value={familiaAsignadaDraft.nombre} onChange={(e) => setFamiliaAsignadaDraft((actual) => actual ? { ...actual, nombre: e.target.value } : actual)} className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg bg-white" /></label>
                    <label className="text-xs font-semibold text-gray-700">Plazo de aprobación (días)<input type="number" min={1} max={30} value={familiaAsignadaDraft.diasAprobacion} onChange={(e) => setFamiliaAsignadaDraft((actual) => actual ? { ...actual, diasAprobacion: Math.max(1, Math.min(30, Number(e.target.value) || 1)) } : actual)} className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg bg-white" /></label>
                    <label className="md:col-span-2 text-xs font-semibold text-gray-700">Descripción<textarea rows={2} value={familiaAsignadaDraft.descripcion} onChange={(e) => setFamiliaAsignadaDraft((actual) => actual ? { ...actual, descripcion: e.target.value } : actual)} className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg bg-white" /></label>
                    <label className="md:col-span-2 flex items-center gap-2 text-sm text-gray-700"><input type="checkbox" checked={familiaAsignadaDraft.avanceAutomatico} onChange={(e) => setFamiliaAsignadaDraft((actual) => actual ? { ...actual, avanceAutomatico: e.target.checked } : actual)} />Aplicar automáticamente el cambio si el coach no responde dentro del plazo</label>
                  </div>
                  <div className="space-y-2"><p className="text-sm font-bold text-gray-900">Etapas asignadas</p>{familiaAsignadaDraft.etapas.map((etapa, index) => (
                    <div key={etapa.id} className="rounded-lg border border-gray-200 bg-white p-3 grid grid-cols-1 lg:grid-cols-[3rem_minmax(0,1fr)_8rem_9rem_auto] gap-3 items-end">
                      <div className="lg:self-center"><span className="inline-grid w-8 h-8 place-items-center rounded-full bg-teal-700 text-white font-bold text-sm">{index + 1}</span></div>
                      <div><p className="text-xs font-semibold text-gray-500">Rutina</p><p className="font-semibold text-gray-900 mt-1">{etapa.rutinaNombre}</p></div>
                      <label className="text-xs font-semibold text-gray-600">Duración (días)<input type="number" min={1} value={etapa.duracionDias} onChange={(e) => setFamiliaAsignadaDraft((actual) => actual ? { ...actual, etapas: actual.etapas.map((item) => item.id === etapa.id ? { ...item, duracionDias: Math.max(1, Number(e.target.value) || 1) } : item) } : actual)} className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg" /></label>
                      <label className="text-xs font-semibold text-gray-600">Sesiones mínimas<input type="number" min={0} value={etapa.sesionesMinimas} onChange={(e) => setFamiliaAsignadaDraft((actual) => actual ? { ...actual, etapas: actual.etapas.map((item) => item.id === etapa.id ? { ...item, sesionesMinimas: e.target.value } : item) } : actual)} placeholder="Opcional" className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg" /></label>
                      <button disabled={!etapa.rutinaId} onClick={() => openRutinaInlineModal(etapa.rutinaId, 'edit')} className="px-3 py-2 text-sm border border-teal-300 text-teal-800 rounded-lg font-semibold disabled:opacity-40 whitespace-nowrap"><i className="ri-edit-line mr-1" />Editar rutina</button>
                    </div>
                  ))}</div>
                  <div className="flex justify-end gap-2"><button onClick={() => { setEditandoFamiliaAsignada(false); setFamiliaAsignadaDraft(null); }} className="px-4 py-2 border border-gray-300 bg-white rounded-lg text-sm">Cancelar</button><button disabled={procesandoFamilia || !familiaAsignadaDraft.nombre.trim()} onClick={() => void guardarFamiliaAsignada()} className="px-4 py-2 bg-teal-700 text-white rounded-lg text-sm font-semibold disabled:opacity-50">{procesandoFamilia ? 'Guardando…' : 'Guardar familia'}</button></div>
                </div>}
                {cambiosFamilia.map((cambio) => (
                  <div key={cambio.id} className="rounded-xl border border-amber-200 bg-amber-50 p-4 flex flex-col lg:flex-row lg:items-center gap-4">
                    <div className="flex-1"><p className="font-semibold text-amber-950">Cambio pendiente: etapa {cambio.destino_snapshot?.orden || cambio.destino?.orden} · {cambio.destino_snapshot?.nombre_rutina_snapshot || cambio.destino?.rutina?.nombre || 'Siguiente rutina'}</p><p className="text-sm text-amber-800 mt-1">{cambio.motivo}</p><p className="text-xs text-amber-700 mt-1">Se aplicará automáticamente si no se resuelve antes del {new Date(cambio.fecha_limite).toLocaleString('es-CL')}.</p></div>
                    <div className="flex gap-2"><button disabled={procesandoFamilia} onClick={() => resolverCambioFamilia(cambio, false)} className="px-3 py-2 border border-red-200 text-red-700 bg-white rounded-lg text-sm disabled:opacity-50">Rechazar</button><button disabled={procesandoFamilia} onClick={() => resolverCambioFamilia(cambio, true)} className="px-3 py-2 bg-teal-700 text-white rounded-lg text-sm disabled:opacity-50">Aprobar cambio</button></div>
                  </div>
                ))}
                {cambiosFamilia.length === 0 && <p className="text-sm text-gray-500">La etapa sigue en curso; todavía no hay un cambio pendiente.</p>}
              </div>
            ) : (
              <div className="flex flex-col md:flex-row md:items-end gap-3">
                <label className="flex-1 text-sm font-medium text-gray-700">Asignar una familia
                  <select value={familiaSeleccionadaId} onChange={(e) => setFamiliaSeleccionadaId(e.target.value)} className="mt-1 w-full px-3 py-2.5 border border-gray-300 rounded-lg bg-white"><option value="">Selecciona una familia...</option>{familiasDisponibles.filter((f) => f.etapas.length > 0).map((familia) => <option key={familia.id} value={familia.id}>{familia.nombre}</option>)}</select>
                </label>
                <button disabled={!familiaSeleccionadaId || procesandoFamilia || selectedUsuario?.estado !== 'activo'} onClick={asignarFamilia} className="px-4 py-2.5 bg-teal-600 text-white rounded-lg disabled:opacity-50 whitespace-nowrap">{procesandoFamilia ? 'Asignando...' : 'Asignar familia'}</button>
              </div>
            )}
          </div>
        </section>

        {(loadingRecomendacionesIA || recomendacionesIA.length > 0) && (
          <section id="recomendaciones-acoma" className="bg-white rounded-xl border border-violet-200 shadow-sm overflow-hidden xl:col-start-1 scroll-mt-24">
            <div className="px-5 py-4 border-b border-violet-100 bg-violet-50 flex items-center justify-between gap-3">
              <div>
                <h2 className="font-bold text-violet-950 flex items-center gap-2"><i className="ri-sparkling-2-line" />Recomendaciones de ACOMA</h2>
                <p className="text-xs text-violet-700 mt-1">La IA propone; el coach decide antes de modificar la rutina activa.</p>
              </div>
              {recomendacionesIA.length > 0 && <span className="px-2.5 py-1 rounded-full bg-violet-200 text-violet-900 text-xs font-bold">{recomendacionesIA.length} pendiente{recomendacionesIA.length === 1 ? '' : 's'}</span>}
            </div>
            {loadingRecomendacionesIA ? (
              <div className="p-5 text-sm text-gray-500"><i className="ri-loader-4-line animate-spin mr-2" />Analizando recomendaciones…</div>
            ) : (
              <div className="divide-y divide-gray-100">
                {recomendacionesIA.map((recomendacion) => (
                  <article key={recomendacion.id} className="p-5">
                    <div className="min-w-0">
                      <p className="font-semibold text-gray-900">{recomendacion.rutina_propuesta?.nombre || 'Rutina propuesta'}</p>
                      {String(recomendacion.motivo?.tipo_sugerencia || '') === 'renovacion' && <span className="inline-flex mt-1 px-2 py-0.5 rounded-full bg-sky-100 text-sky-700 text-xs font-bold"><i className="ri-refresh-line mr-1" />Renovación</span>}
                      <p className="text-sm text-gray-600 mt-1 leading-relaxed max-w-5xl">{recomendacion.resumen}</p>
                      {String(recomendacion.motivo?.tipo_sugerencia || '') === 'renovacion' && (
                        <div className="mt-2">
                          <div className="flex flex-wrap gap-2 text-xs">
                            <span className="px-2 py-1 rounded-md bg-slate-100 text-slate-700">Adherencia 60 días: {Number(recomendacion.motivo?.adherencia_ultimos_60_dias || 0)}%</span>
                            <span className="px-2 py-1 rounded-md bg-slate-100 text-slate-700">{Number(recomendacion.motivo?.sesiones_ultimos_60_dias || 0)} sesiones analizadas</span>
                            <span className="px-2 py-1 rounded-md bg-slate-100 text-slate-700">{Number(recomendacion.motivo?.ejercicios_con_registro || 0)} ejercicios con carga</span>
                          </div>
                          {Boolean(recomendacion.motivo?.progresion_sugerida) && <p className="mt-2 text-xs font-medium text-sky-800"><i className="ri-line-chart-line mr-1" />Incluye {Number(recomendacion.motivo?.semanas_progresion || 0)} semanas de progresión · {Number(recomendacion.motivo?.ejercicios_con_progresion || 0)} ejercicios con evidencia suficiente.</p>}
                        </div>
                      )}
                      {Boolean(recomendacion.motivo?.evaluacion_corporal_considerada) && (
                        <p className="mt-2 text-xs font-medium text-emerald-800">
                          <i className="ri-heart-pulse-line mr-1" />
                          Consideró la evaluación corporal del {new Date(`${String((recomendacion.motivo?.evaluacion_corporal_considerada as Record<string, unknown>)?.fecha || '')}T12:00:00`).toLocaleDateString('es-CL')}.
                        </p>
                      )}
                      {Boolean(recomendacion.motivo?.evaluacion_inicial_considerada) && (
                        <p className="mt-2 text-xs font-medium text-violet-800">
                          <i className="ri-clipboard-line mr-1" />
                          Consideró la evaluación inicial del {new Date(`${String((recomendacion.motivo?.evaluacion_inicial_considerada as Record<string, unknown>)?.fecha || '')}T12:00:00`).toLocaleDateString('es-CL')}
                          {' · '}{String((recomendacion.motivo?.evaluacion_inicial_considerada as Record<string, unknown>)?.estado || '').replaceAll('_', ' ')}.
                        </p>
                      )}
                      {Array.isArray(recomendacion.motivo?.notas_revision) && recomendacion.motivo.notas_revision.length > 0 && (
                        <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900 max-w-4xl">
                          <p className="font-semibold mb-1"><i className="ri-alert-line mr-1" />Puntos que debe revisar el coach</p>
                          <ul className="list-disc pl-4 space-y-1">{recomendacion.motivo.notas_revision.map((nota, index) => <li key={index}>{String(nota)}</li>)}</ul>
                        </div>
                      )}
                      <p className="text-xs text-gray-400 mt-2">Generada {new Date(recomendacion.created_at).toLocaleString('es-CL')}</p>
                      {recomendacion.cantidad_ediciones > 0 && <p className="text-xs text-emerald-700 font-medium mt-1"><i className="ri-user-settings-line mr-1" />Revisada por el equipo · {recomendacion.cantidad_ediciones} edición{recomendacion.cantidad_ediciones === 1 ? '' : 'es'}</p>}
                    </div>
                    <div className="mt-4 pt-4 border-t border-gray-100 flex flex-wrap gap-2 justify-start lg:justify-end">
                      {recomendacion.rutina_propuesta?.id && <button disabled={resolviendoRecomendacionId === recomendacion.id} onClick={() => openRutinaInlineModal(recomendacion.rutina_propuesta!.id, 'detail')} className="px-4 py-2 border border-violet-200 text-violet-700 rounded-lg text-sm font-medium hover:bg-violet-50 disabled:opacity-50"><i className="ri-eye-line mr-1" />Ver propuesta</button>}
                      {recomendacion.rutina_propuesta?.id && <button disabled={resolviendoRecomendacionId === recomendacion.id} onClick={() => openRutinaInlineModal(recomendacion.rutina_propuesta!.id, 'edit', recomendacion.id)} className="px-4 py-2 border border-emerald-300 bg-emerald-50 text-emerald-800 rounded-lg text-sm font-semibold hover:bg-emerald-100 disabled:opacity-50"><i className="ri-edit-line mr-1" />Revisar y editar</button>}
                      <button disabled={resolviendoRecomendacionId === recomendacion.id} onClick={() => void resolverRecomendacionIA(recomendacion, false)} className="px-4 py-2 border border-red-200 text-red-700 rounded-lg text-sm font-medium hover:bg-red-50 disabled:opacity-50">Rechazar</button>
                      <button disabled={resolviendoRecomendacionId === recomendacion.id} onClick={() => void resolverRecomendacionIA(recomendacion, true)} className="px-4 py-2 bg-violet-700 text-white rounded-lg text-sm font-medium hover:bg-violet-800 disabled:opacity-50">{resolviendoRecomendacionId === recomendacion.id ? 'Procesando…' : 'Aprobar y publicar'}</button>
                    </div>
                  </article>
                ))}
              </div>
            )}
          </section>
        )}

        {historialIATotal > 0 && (
          <section className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden xl:col-start-1">
            <button
              type="button"
              onClick={() => setHistorialIAExpandido((value) => !value)}
              className="w-full px-5 py-4 bg-slate-50 flex items-center justify-between gap-3 text-left hover:bg-slate-100 transition-colors"
              aria-expanded={historialIAExpandido}
            >
              <div>
                <h2 className="font-bold text-slate-950 flex items-center gap-2"><i className="ri-history-line" />Historial de propuestas IA</h2>
                <p className="text-xs text-slate-600 mt-1">{historialIATotal} propuesta{historialIATotal === 1 ? '' : 's'} resuelta{historialIATotal === 1 ? '' : 's'} · despliega para revisar el detalle.</p>
              </div>
              <span className="flex items-center gap-2 shrink-0">
                <span className="px-2.5 py-1 rounded-full bg-slate-200 text-slate-700 text-xs font-bold">{historialIATotal}</span>
                <i className={`${historialIAExpandido ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-xl text-slate-600`} />
              </span>
            </button>
            {historialIAExpandido && (
              <div className="border-t border-slate-200">
                {loadingHistorialIA ? (
                  <div className="p-5 text-sm text-slate-500"><i className="ri-loader-4-line animate-spin mr-2" />Cargando historial…</div>
                ) : (
                  <div className="divide-y divide-gray-100">
                    {historialRecomendacionesIA.map((recomendacion) => {
                      const approved = recomendacion.estado === 'aprobada';
                      return (
                        <article key={recomendacion.id} className="p-5 flex flex-col lg:flex-row lg:items-center gap-4">
                          <div className="flex-1 min-w-0">
                            <div className="flex flex-wrap items-center gap-2">
                              <p className="font-semibold text-gray-900">{recomendacion.rutina_propuesta?.nombre || 'Propuesta de rutina'}</p>
                              {String(recomendacion.motivo?.tipo_sugerencia || '') === 'renovacion' && <span className="px-2 py-0.5 rounded-full bg-sky-100 text-sky-700 text-xs font-bold">Renovación</span>}
                              <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${approved ? 'bg-emerald-100 text-emerald-700' : recomendacion.estado === 'rechazada' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-600'}`}>
                                {approved ? 'Aprobada' : recomendacion.estado === 'rechazada' ? 'Rechazada' : 'Cancelada'}
                              </span>
                            </div>
                            <p className="text-sm text-gray-600 mt-1 line-clamp-2">{recomendacion.resumen}</p>
                            <p className="text-xs text-gray-400 mt-2">
                              Generada {new Date(recomendacion.created_at).toLocaleString('es-CL')}
                              {recomendacion.revisada_at ? ` · Resuelta ${new Date(recomendacion.revisada_at).toLocaleString('es-CL')}` : ''}
                              {` · ${recomendacion.cantidad_ediciones} edición${recomendacion.cantidad_ediciones === 1 ? '' : 'es'}`}
                            </p>
                          </div>
                          <button onClick={() => void abrirHistorialRecomendacionIA(recomendacion)} className="px-4 py-2 border border-slate-300 text-slate-700 rounded-lg text-sm font-semibold hover:bg-slate-50 shrink-0">
                            <i className="ri-git-commit-line mr-1" />Ver revisión y cambios
                          </button>
                        </article>
                      );
                    })}
                  </div>
                )}
                <div className="px-5 py-3 border-t border-slate-200 bg-slate-50 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <p className="text-xs text-slate-600">
                    Página {historialIAPagina} de {historialIATotalPaginas} · máximo {HISTORIAL_IA_POR_PAGINA} por página
                  </p>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      disabled={loadingHistorialIA || historialIAPagina <= 1}
                      onClick={() => selectedUsuario && void fetchHistorialIAPagina(selectedUsuario.id, historialIAPagina - 1)}
                      className="px-3 py-2 rounded-lg border border-slate-300 bg-white text-sm font-semibold text-slate-700 disabled:opacity-40"
                    >
                      <i className="ri-arrow-left-s-line mr-1" />Anterior
                    </button>
                    <button
                      type="button"
                      disabled={loadingHistorialIA || historialIAPagina >= historialIATotalPaginas}
                      onClick={() => selectedUsuario && void fetchHistorialIAPagina(selectedUsuario.id, historialIAPagina + 1)}
                      className="px-3 py-2 rounded-lg border border-slate-300 bg-white text-sm font-semibold text-slate-700 disabled:opacity-40"
                    >
                      Siguiente<i className="ri-arrow-right-s-line ml-1" />
                    </button>
                  </div>
                </div>
              </div>
            )}
          </section>
        )}

        <aside className="space-y-5 xl:row-start-1 xl:row-span-3 xl:col-start-2">
          <CoachUpcomingCard
            title="Próximos cambios de rutina"
            icon="ri-calendar-event-line"
            tone="amber"
            emptyText="No hay cambios de rutina programados."
            footer="Ver todos los usuarios"
            onFooter={() => setShowUserSelector(true)}
          >
            {cambiosRutinaProximos.slice(0, 5).map(({ usuario, fecha, dias }) => (
              <button key={usuario.id} onClick={() => selectUsuarioById(usuario.id)} className="w-full grid grid-cols-[2.25rem_minmax(0,1fr)_4.5rem_6rem_1rem] items-center gap-3 px-4 py-3 border-t first:border-t-0 hover:bg-gray-50 text-left">
                <span className="w-9 h-9 rounded-full bg-emerald-50 text-green-800 flex items-center justify-center font-bold text-xs">{usuario.avatar}</span>
                <span className="min-w-0"><span className="block font-semibold text-sm text-gray-900 truncate">{usuario.nombre}</span></span>
                <span className="text-xs text-gray-500 text-right whitespace-nowrap">{fecha.toLocaleDateString('es-CL', { day: '2-digit', month: 'short' })}</span>
                <span className={`text-xs rounded-full px-2 py-1 text-center whitespace-nowrap ${dias < 0 ? 'bg-red-100 text-red-700' : dias === 0 ? 'bg-rose-100 text-rose-700' : dias <= 3 ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'}`}>{dias < 0 ? `Vencida ${Math.abs(dias)}d` : dias === 0 ? 'Hoy' : `${dias} días`}</span>
                <i className="ri-arrow-right-s-line text-gray-400"></i>
              </button>
            ))}
          </CoachUpcomingCard>

          <CoachUpcomingCard
            title="Próximas evaluaciones"
            icon="ri-calendar-check-line"
            tone="purple"
            emptyText="No hay evaluaciones próximas."
            footer="Ver todas las evaluaciones"
            onFooter={() => setShowEvaluacionesAgenda(true)}
          >
            {evaluacionesProximas.slice(0, 5).map((cita) => {
              const fecha = new Date(cita.fecha_inicio);
              const dias = Math.max(0, Math.ceil((fecha.getTime() - Date.now()) / 86400000));
              return <button key={cita.id} onClick={() => selectUsuarioById(cita.usuario_id)} className="w-full grid grid-cols-[2.25rem_minmax(0,1fr)_4.5rem_6rem_1rem] items-center gap-3 px-4 py-3 border-t first:border-t-0 hover:bg-gray-50 text-left">
                <span className="w-9 h-9 rounded-full bg-purple-50 text-purple-700 flex items-center justify-center font-bold text-xs">{cita.usuario?.nombre?.charAt(0) || 'U'}</span>
                <span className="min-w-0"><span className="block font-semibold text-sm text-gray-900 truncate">{cita.usuario?.nombre || 'Usuario'}</span></span>
                <span className="text-xs text-gray-500 text-right whitespace-nowrap">{fecha.toLocaleDateString('es-CL', { day: '2-digit', month: 'short' })}</span>
                <span className="text-xs rounded-full px-2 py-1 bg-purple-100 text-purple-700 text-center whitespace-nowrap">{dias === 0 ? 'Hoy' : `En ${dias} días`}</span>
                <i className="ri-arrow-right-s-line text-gray-400"></i>
              </button>;
            })}
          </CoachUpcomingCard>
        </aside>

        <div className="grid grid-cols-1 sm:grid-cols-2 2xl:grid-cols-4 gap-4 xl:col-start-1">
          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm">Entrenamientos este Mes</p>
                <p className="text-3xl font-bold text-gray-900 mt-1">{entrenamientosEsteMes}</p>
                <p className={`text-xs mt-1 ${diferenciaEntrenamientosMes > 0 ? 'text-green-700' : diferenciaEntrenamientosMes < 0 ? 'text-red-600' : 'text-gray-500'}`}>
                  {diferenciaEntrenamientosMes > 0 ? '+' : ''}{diferenciaEntrenamientosMes} vs. mes anterior
                </p>
              </div>
              <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center">
                <i className="ri-calendar-check-line text-2xl text-teal-600"></i>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm">Racha Actual</p>
                <p className="text-3xl font-bold text-orange-600 mt-1">{rachasEntrenamiento.actual} días</p>
                <p className="text-xs text-orange-600 mt-1">Mejor racha: {rachasEntrenamiento.mejor} días</p>
              </div>
              <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                <i className="ri-fire-line text-2xl text-orange-600"></i>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm">Volumen últimos 7 días</p>
                <p className="text-3xl font-bold text-blue-600 mt-1">{volumenTotal.toLocaleString()} kg</p>
                <p className={`text-xs mt-1 ${variacionVolumen === null ? 'text-gray-500' : variacionVolumen >= 0 ? 'text-blue-600' : 'text-red-600'}`}>
                  {variacionVolumen === null ? 'Sin semana anterior comparable' : `${variacionVolumen > 0 ? '+' : ''}${variacionVolumen}% vs. 7 días anteriores`}
                </p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <i className="ri-bar-chart-box-line text-2xl text-blue-600"></i>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm">Uso del plan</p>
                <p className="text-3xl font-bold text-green-600 mt-1">{metaSesionesPlan > 0 ? `${Math.round(progresoRutina)}%` : entrenamientosEsteMes}</p>
                <p className="text-xs text-green-600 mt-1">{metaSesionesPlan > 0 ? `${entrenamientosEsteMes} de ${metaSesionesPlan} sesiones del período` : 'Sesiones realizadas este mes · plan sin límite'}</p>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <i className="ri-pie-chart-line text-2xl text-green-600"></i>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 xl:col-start-1">
          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Volumen de Entrenamiento Semanal</h3>
            {weeklySeries.length === 0 ? (
              <div className="text-sm text-gray-500">No hay datos semanales para el mes seleccionado.</div>
            ) : (
              <div className="space-y-4">
                {weeklySeries.map((row) => {
                  const altura = Math.min((row.volumen / maxVolumen) * 100, 100);
                  return (
                    <div key={`vol-${row.week}`}>
                      <div className="flex justify-between text-sm mb-2">
                        <span className="text-gray-600">Semana {row.week}</span>
                        <span className="font-semibold text-gray-900">{Math.round(row.volumen).toLocaleString()} kg</span>
                      </div>
                      <div className="w-full bg-gray-100 rounded-full h-3 overflow-hidden">
                        <div
                          className="bg-gradient-to-r from-teal-500 to-teal-600 h-full rounded-full transition-all duration-500"
                          style={{ width: `${altura}%`, maxHeight: '12rem' }}
                        ></div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Evolución del Peso</h3>
            {weeklySeries.length === 0 ? (
              <div className="text-sm text-gray-500">No hay datos semanales para el mes seleccionado.</div>
            ) : (
              <div className="space-y-4">
                {weeklySeries.map((row) => {
                  const altura = row.peso !== null ? Math.min(((row.peso - minPeso) / rangoPeso) * 100, 100) : 0;
                  return (
                    <div key={`peso-${row.week}`}>
                      <div className="flex justify-between text-sm mb-2">
                        <span className="text-gray-600">Semana {row.week}</span>
                        <span className="font-semibold text-gray-900">{row.peso !== null ? `${row.peso} kg` : '-'}</span>
                      </div>
                      <div className="w-full bg-gray-100 rounded-full h-3 overflow-hidden">
                        <div
                          className="bg-gradient-to-r from-blue-500 to-blue-600 h-full rounded-full transition-all duration-500"
                          style={{ width: `${altura}%`, maxHeight: '12rem' }}
                        ></div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 mt-8 overflow-hidden">
          <div className="p-6 border-b border-gray-200 flex items-center justify-between gap-4 flex-wrap">
            <div>
              <h3 className="text-lg font-bold text-gray-900">Evolución de Ejercicios</h3>
              <p className="text-sm text-gray-500 mt-1">
                Tendencia de volumen total movido, carga máxima y repeticiones por día.
              </p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <div className="inline-flex rounded-lg border border-gray-300 overflow-hidden">
                {[7, 30, 90].map((d) => (
                  <button
                    key={d}
                    onClick={() => setSelectedRangeDays(d as 7 | 30 | 90)}
                    className={`px-3 py-2 text-xs font-medium ${
                      selectedRangeDays === d ? 'bg-teal-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    {d}d
                  </button>
                ))}
              </div>
              <select
                value={selectedEjercicioProgreso}
                onChange={(e) => setSelectedEjercicioProgreso(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
              >
                <option value="todos">Todos los ejercicios</option>
                {ejerciciosDisponibles.map((nombre) => (
                  <option key={nombre} value={nombre}>
                    {nombre}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {ejercicioProgresoSeriesRecientes.length === 0 ? (
            <div className="text-center py-10">
              <i className="ri-line-chart-line text-5xl text-gray-300"></i>
              <p className="text-gray-500 mt-3">Sin datos de ejercicios completados</p>
            </div>
          ) : (
            <div className="p-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div>
                <div className="h-12 mb-3">
                  <h4 className="text-sm font-semibold text-gray-700">Volumen total movido (kg)</h4>
                  <p className="text-xs text-gray-500 mt-1">
                    Fórmula: peso × series × repeticiones (suma de ejercicios completados del día).
                  </p>
                </div>
                <div className="h-56 border border-gray-100 rounded-lg p-3 flex items-end gap-2 bg-gray-50">
                  {ejercicioProgresoSeriesRecientes.map((row) => {
                    const height = 16 + ((row.volumen / maxVolumenEjercicio) * 140);
                    return (
                      <div key={`vol-ej-${row.day}`} className="flex-1 min-w-0 flex flex-col items-center justify-end gap-1">
                        <div className="text-[10px] text-gray-500 truncate max-w-full">
                          {Math.round(row.volumen).toLocaleString('es-CL')}
                        </div>
                        <div
                          className="w-full rounded-t-md bg-teal-500"
                          style={{ height: `${Math.max(10, height)}px` }}
                          title={`${parseISODateLocal(row.day).toLocaleDateString('es-CL')}: ${Math.round(row.volumen).toLocaleString('es-CL')} kg`}
                        ></div>
                        <div className="text-[10px] text-gray-500">
                          {parseISODateLocal(row.day).toLocaleDateString('es-CL', { day: '2-digit', month: '2-digit' })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div>
                <div className="h-12 mb-3">
                  <h4 className="text-sm font-semibold text-gray-700">Carga máxima por día (kg)</h4>
                </div>
                <div className="h-56 border border-gray-100 rounded-lg p-3 flex items-end gap-2 bg-gray-50">
                  {ejercicioProgresoSeriesRecientes.map((row) => {
                    const height = 16 + ((row.maxPeso / maxPesoEjercicio) * 140);
                    return (
                      <div key={`max-ej-${row.day}`} className="flex-1 min-w-0 flex flex-col items-center justify-end gap-1">
                        <div className="text-[10px] text-gray-500 truncate max-w-full">
                          {row.maxPeso.toLocaleString('es-CL')}
                        </div>
                        <div
                          className="w-full rounded-t-md bg-blue-500"
                          style={{ height: `${Math.max(10, height)}px` }}
                          title={`${parseISODateLocal(row.day).toLocaleDateString('es-CL')}: ${row.maxPeso.toLocaleString('es-CL')} kg · ${row.repsTotales} reps`}
                        ></div>
                        <div className="text-[10px] text-gray-500">
                          {parseISODateLocal(row.day).toLocaleDateString('es-CL', { day: '2-digit', month: '2-digit' })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
          {selectedEjercicioProgreso === 'todos' && resumenPorEjercicio.length > 0 && (
            <div className="px-6 pb-6">
              <h4 className="text-sm font-semibold text-gray-700 mb-3">Sección por ejercicio (volumen en rango)</h4>
              <div className="space-y-2">
                {resumenPorEjercicio.map((r) => {
                  const width = Math.min((r.volumen / maxResumenEj) * 100, 100);
                  return (
                    <button
                      key={r.nombre}
                      onClick={() => setSelectedEjercicioProgreso(r.nombre)}
                      className="w-full text-left"
                    >
                      <div className="flex justify-between text-xs text-gray-500 mb-1">
                        <span className="truncate pr-3">{r.nombre}</span>
                        <span>{Math.round(r.volumen).toLocaleString('es-CL')} kg</span>
                      </div>
                      <div className="w-full h-2.5 rounded-full bg-gray-100 overflow-hidden">
                        <div className="h-full bg-amber-500 rounded-full" style={{ width: `${width}%` }}></div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 mt-8 overflow-hidden">
          <div className="p-6 border-b border-gray-200 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <h3 className="text-lg font-bold text-gray-900">Histórico de rutinas</h3>
              <p className="text-sm text-gray-500 mt-1">
                Registro de asignaciones y desasignaciones del usuario seleccionado.
              </p>
            </div>
            <div className="text-sm text-gray-500">
              {rutinasHistorial.length} evento{rutinasHistorial.length !== 1 ? 's' : ''}
            </div>
          </div>

          {loadingRutinasHistorial ? (
            <div className="p-6">
              <div className="flex items-center gap-3 text-gray-600">
                <div className="w-5 h-5 border-2 border-teal-500 border-t-transparent rounded-full animate-spin"></div>
                Cargando histórico...
              </div>
            </div>
          ) : rutinasHistorial.length === 0 ? (
            <div className="text-center py-10">
              <i className="ri-history-line text-5xl text-gray-300"></i>
              <p className="text-gray-500 mt-3">Sin movimientos de rutinas registrados</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Fecha</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Acción</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Rutina</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Origen</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {rutinasHistorial.map((row) => {
                    const meta = row.metadata || {};
                    const nombreOrigen = String(
                      (meta['rutina_origen_nombre'] as string) ||
                      (meta['rutina_nombre'] as string) ||
                      (row.rutina_origen_id ? rutinaHistNombreById[row.rutina_origen_id] : '') ||
                      '-'
                    );
                    return (
                      <tr key={row.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-6 py-3 text-sm text-gray-900">
                          {new Date(row.created_at).toLocaleString('es-CL')}
                        </td>
                        <td className="px-6 py-3 text-sm">
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                            row.accion === 'asignada'
                              ? 'bg-green-100 text-green-700'
                              : row.accion === 'desasignada'
                                ? 'bg-red-100 text-red-700'
                                : 'bg-amber-100 text-amber-700'
                          }`}>
                            {row.accion}
                          </span>
                        </td>
                        <td className="px-6 py-3 text-sm text-gray-900">{rutinaHistNombreById[row.rutina_id] || row.rutina_id}</td>
                        <td className="px-6 py-3 text-sm text-gray-600">{nombreOrigen}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 mt-8 overflow-hidden">
          <div className="p-6 border-b border-gray-200 flex items-center justify-between gap-4">
            <div>
              <h3 className="text-lg font-bold text-gray-900">Rutinas asignadas</h3>
              <p className="text-sm text-gray-500 mt-1">
                Rutinas actualmente asignadas al usuario seleccionado.
              </p>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-sm text-gray-500">
                {rutinasAsignadas.length} rutina{rutinasAsignadas.length !== 1 ? 's' : ''}
              </span>
              <button
                type="button"
                onClick={() => void openRutinaAssignModal()}
                disabled={!selectedUsuario || selectedUsuario.estado.toLocaleLowerCase('es-CL') !== 'activo'}
                className="px-3 py-2 text-sm font-semibold bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              >
                <i className="ri-user-add-line mr-1"></i>
                Asignar rutina
              </button>
            </div>
          </div>

          {loadingRutinasAsignadas ? (
            <div className="p-6">
              <div className="flex items-center gap-3 text-gray-600">
                <div className="w-5 h-5 border-2 border-teal-500 border-t-transparent rounded-full animate-spin"></div>
                Cargando rutinas asignadas...
              </div>
            </div>
          ) : rutinasAsignadas.length === 0 ? (
            <div className="text-center py-10">
              <i className="ri-file-list-3-line text-5xl text-gray-300"></i>
              <p className="text-gray-500 mt-3">No hay rutinas asignadas para este usuario</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {rutinasAsignadas.map((rutina) => (
                <div key={rutina.id} className="p-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-semibold text-gray-900">{rutina.nombre}</p>
                      <span
                        className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          rutina.activa ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-700'
                        }`}
                      >
                        {rutina.activa ? 'Activa' : 'Inactiva'}
                      </span>
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-700">
                        {rutina.creada_por_usuario ? 'Personalizada' : 'Oficial'}
                      </span>
                    </div>
                    {rutina.descripcion && <p className="text-sm text-gray-600 mt-1">{rutina.descripcion}</p>}
                    <div className="text-xs text-gray-500 mt-2">
                      Actualizada {new Date(rutina.updated_at).toLocaleString('es-CL')}
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => openRutinaInlineModal(rutina.id, 'detail')}
                      className="px-3 py-2 text-sm bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap inline-flex items-center gap-2"
                    >
                      <i className="ri-eye-line"></i>
                      Ver detalle
                    </button>
                    <button
                      onClick={() => openRutinaInlineModal(rutina.id, 'edit')}
                      className="px-3 py-2 text-sm bg-teal-500 text-white rounded-lg hover:bg-teal-600 transition-colors whitespace-nowrap inline-flex items-center gap-2"
                    >
                      <i className="ri-edit-line"></i>
                      Editar
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 mt-8 overflow-hidden">
          <div className="p-6 border-b border-gray-200">
            <h3 className="text-lg font-bold text-gray-900">Historial de Mediciones</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Fecha</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Peso</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Grasa Corporal</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Masa Muscular</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Notas</th>
                  <th className="px-6 py-4 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Evaluación</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {progreso.map((registro) => {
                  const reporteEvaluacion = reportesComposicion.find(
                    (reporte) =>
                      reporte.progreso_id === registro.id &&
                      reporte.fuente === 'fitmao' &&
                      Boolean(reporte.fuente_url),
                  );
                  return (
                    <tr key={registro.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {new Date(registro.fecha).toLocaleDateString('es-CL')}
                      </td>
                      <td className="px-6 py-4 text-sm font-medium text-gray-900">{registro.peso} kg</td>
                      <td className="px-6 py-4 text-sm text-gray-900">{registro.grasa_corporal}%</td>
                      <td className="px-6 py-4 text-sm text-gray-900">{registro.masa_muscular} kg</td>
                      <td className="px-6 py-4 text-sm text-gray-500">{registro.notas || '-'}</td>
                      <td className="px-6 py-4 text-right">
                        {reporteEvaluacion ? (
                          <button
                            type="button"
                            onClick={() => void openReporte(reporteEvaluacion)}
                            disabled={openingReporteId === reporteEvaluacion.id}
                            className="inline-flex items-center gap-2 whitespace-nowrap rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-sm font-semibold text-blue-700 transition-colors hover:bg-blue-100 disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            <i className={`ri-external-link-line ${openingReporteId === reporteEvaluacion.id ? 'animate-pulse' : ''}`}></i>
                            Reporte evaluación
                          </button>
                        ) : (
                          <span className="text-sm text-gray-400">-</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {progreso.length === 0 && (
            <div className="text-center py-12">
              <i className="ri-line-chart-line text-5xl text-gray-300"></i>
              <p className="text-gray-500 mt-4">No hay registros de progreso</p>
              <p className="text-sm text-gray-400 mt-2">Comienza registrando las primeras mediciones</p>
            </div>
          )}
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 mt-8 overflow-hidden">
          <div className="p-6 border-b border-gray-200 flex items-center justify-between gap-4">
            <div>
              <h3 className="text-lg font-bold text-gray-900">Rutinas personalizadas del usuario</h3>
              <p className="text-sm text-gray-500 mt-1">
                Rutinas creadas por el usuario en la app móvil para revisión del entrenador.
              </p>
            </div>
            <div className="text-sm text-gray-500">
              {rutinasPersonalizadas.length} rutina{rutinasPersonalizadas.length !== 1 ? 's' : ''}
            </div>
          </div>

          {loadingRutinasPersonalizadas ? (
            <div className="p-6">
              <div className="flex items-center gap-3 text-gray-600">
                <div className="w-5 h-5 border-2 border-amber-600 border-t-transparent rounded-full animate-spin"></div>
                Cargando rutinas personalizadas...
              </div>
            </div>
          ) : rutinasPersonalizadas.length === 0 ? (
            <div className="text-center py-10">
              <i className="ri-dumbbell-line text-5xl text-gray-300"></i>
              <p className="text-gray-500 mt-3">No hay rutinas personalizadas para este usuario</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {rutinasPersonalizadas.map((rutina) => (
                <div key={rutina.id} className="p-6 flex items-start justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-semibold text-gray-900">{rutina.nombre}</p>
                      <span
                        className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          rutina.activa ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-700'
                        }`}
                      >
                        {rutina.activa ? 'Activa' : 'Inactiva'}
                      </span>
                    </div>
                    {rutina.descripcion && <p className="text-sm text-gray-600 mt-1">{rutina.descripcion}</p>}
                    <div className="text-xs text-gray-500 mt-2">
                      {getSesionesRutina(rutina)} sesión{getSesionesRutina(rutina) !== 1 ? 'es' : ''} ·{' '}
                      Actualizada {new Date(rutina.updated_at).toLocaleString('es-CL')}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 mt-8 overflow-hidden">
          <div className="p-6 border-b border-gray-200 flex items-center justify-between gap-4">
            <div>
              <h3 className="text-lg font-bold text-gray-900">Pauta Nutricional (PDF)</h3>
              <p className="text-sm text-gray-500 mt-1">
                Sube la pauta nutricional del usuario para visualizarla desde la app móvil.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <input
                ref={pautaFileInputRef}
                type="file"
                accept="application/pdf,.pdf"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  void handleUploadPautaNutricional(file);
                  e.currentTarget.value = '';
                }}
              />
              <button
                onClick={() => pautaFileInputRef.current?.click()}
                disabled={!selectedUsuario || uploadingPauta}
                className="px-4 py-2 text-sm bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors whitespace-nowrap inline-flex items-center gap-2"
              >
                <i className={`ri-upload-2-line ${uploadingPauta ? 'animate-pulse' : ''}`}></i>
                {uploadingPauta ? 'Subiendo...' : 'Subir PDF'}
              </button>
            </div>
          </div>

          {reportesNutricion.length === 0 ? (
            <div className="text-center py-10">
              <i className="ri-file-pdf-line text-5xl text-gray-300"></i>
              <p className="text-gray-500 mt-3">No hay pauta nutricional cargada</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {reportesNutricion.slice(0, 3).map((reporte) => (
                <div key={reporte.id} className="p-6 flex items-center justify-between gap-4">
                  <div>
                    <p className="font-semibold text-gray-900">
                      {String((reporte.meta as any)?.nombre_archivo || 'Pauta nutricional')}
                    </p>
                    <p className="text-sm text-gray-500 mt-1">
                      Subida el {new Date(reporte.created_at).toLocaleString('es-CL')}
                    </p>
                  </div>
                  <button
                    onClick={() => openReporte(reporte)}
                    disabled={openingReporteId === reporte.id}
                    className="px-3 py-2 text-sm bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors whitespace-nowrap inline-flex items-center gap-2"
                  >
                    <i className={`ri-external-link-line ${openingReporteId === reporte.id ? 'animate-pulse' : ''}`}></i>
                    Ver PDF
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 mt-8 overflow-hidden">
          <div className="p-6 border-b border-gray-200">
            <h3 className="text-lg font-bold text-gray-900">Cálculo nutricional</h3>
            <p className="text-sm text-gray-500 mt-1">
              Estimación de TMB y GET según sexo, peso, altura, edad y nivel de actividad.
            </p>
          </div>

          <div className="p-6 space-y-6">
            <div className="inline-flex rounded-lg bg-gray-100 p-1">
              <button type="button" onClick={() => setNutriFormula('harris_benedict')} className={`px-4 py-2 rounded-md text-sm font-semibold ${nutriFormula === 'harris_benedict' ? 'bg-white text-teal-700 shadow-sm' : 'text-gray-600'}`}>Harris-Benedict</button>
              <button type="button" onClick={() => setNutriFormula('mifflin_st_jeor')} className={`px-4 py-2 rounded-md text-sm font-semibold ${nutriFormula === 'mifflin_st_jeor' ? 'bg-white text-teal-700 shadow-sm' : 'text-gray-600'}`}>Mifflin-St Jeor</button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Sexo</label>
                <select
                  value={nutriSexo}
                  onChange={(e) => setNutriSexo((e.target.value as 'hombre' | 'mujer') || 'hombre')}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                >
                  <option value="hombre">Hombre</option>
                  <option value="mujer">Mujer</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Peso (kg)</label>
                <input
                  type="text"
                  value={nutriPesoKg}
                  onChange={(e) => setNutriPesoKg(e.target.value)}
                  placeholder="Ej: 80"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Altura (cm)</label>
                <input
                  type="text"
                  value={nutriAlturaCm}
                  onChange={(e) => setNutriAlturaCm(e.target.value)}
                  placeholder="Ej: 175"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Edad</label>
                <input
                  type="text"
                  value={nutriEdad}
                  onChange={(e) => setNutriEdad(e.target.value)}
                  placeholder="Ej: 30"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Actividad</label>
                <select
                  value={nutriActividad}
                  onChange={(e) =>
                    setNutriActividad(
                      (e.target.value as 'sedentario' | 'ligero' | 'moderado' | 'activo' | 'muy_activo') || 'moderado'
                    )
                  }
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                >
                  {Object.entries(ACTIVITY_FACTORS).map(([key, value]) => (
                    <option key={key} value={key}>
                      {value.label} ({value.factor})
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="max-w-sm">
              <label className="block text-sm font-semibold text-gray-700 mb-1">Déficit / superávit</label>
              <select value={nutriAjuste} onChange={(e) => setNutriAjuste(Number(e.target.value))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                <option value={-20}>Déficit alto (-20%)</option>
                <option value={-15}>Déficit moderado (-15%)</option>
                <option value={-10}>Déficit suave (-10%)</option>
                <option value={0}>Mantención</option>
                <option value={10}>Superávit suave (+10%)</option>
                <option value={15}>Superávit moderado (+15%)</option>
              </select>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="rounded-lg border border-blue-100 bg-blue-50 p-4">
                <p className="text-xs uppercase tracking-wide text-blue-700 font-semibold">TMB estimada</p>
                <p className="mt-1 text-2xl font-bold text-blue-900">
                  {tmbCalculada === null ? '-' : `${Math.round(tmbCalculada).toLocaleString('es-CL')} kcal`}
                </p>
                <p className="text-xs text-blue-700 mt-2">
                  {nutriFormula === 'mifflin_st_jeor'
                    ? `Mifflin-St Jeor · ${nutriSexo === 'hombre' ? '+5' : '-161'}`
                    : nutriSexo === 'hombre'
                    ? '88.362 + (13.397 x peso) + (4.799 x altura) - (5.677 x edad)'
                    : '447.593 + (9.247 x peso) + (3.098 x altura) - (4.330 x edad)'}
                </p>
              </div>
              <div className="rounded-lg border border-green-100 bg-green-50 p-4">
                <p className="text-xs uppercase tracking-wide text-green-700 font-semibold">GET estimado</p>
                <p className="mt-1 text-2xl font-bold text-green-900">
                  {getCalculado === null ? '-' : `${Math.round(getCalculado).toLocaleString('es-CL')} kcal`}
                </p>
                <p className="text-xs text-green-700 mt-2">
                  {`Factor ${ACTIVITY_FACTORS[nutriActividad].label}: ${factorActividad}`}
                </p>
              </div>
              <div className="rounded-lg border border-teal-200 bg-teal-50 p-4">
                <p className="text-xs uppercase tracking-wide text-teal-700 font-semibold">Calorías objetivo</p>
                <p className="mt-1 text-2xl font-bold text-teal-900">{caloriasObjetivo === null ? '-' : `${Math.round(caloriasObjetivo).toLocaleString('es-CL')} kcal`}</p>
                <p className="text-xs text-teal-700 mt-2">Ajuste de {nutriAjuste > 0 ? '+' : ''}{nutriAjuste}% sobre el GET</p>
              </div>
            </div>

            {macrosCalculados && <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="rounded-lg bg-red-50 border border-red-100 p-4"><p className="text-sm font-semibold text-red-700">Proteínas · 30%</p><p className="text-2xl font-bold text-red-900 mt-1">{Math.round(macrosCalculados.proteinas)} g</p></div>
              <div className="rounded-lg bg-amber-50 border border-amber-100 p-4"><p className="text-sm font-semibold text-amber-700">Grasas · 30%</p><p className="text-2xl font-bold text-amber-900 mt-1">{Math.round(macrosCalculados.grasas)} g</p></div>
              <div className="rounded-lg bg-sky-50 border border-sky-100 p-4"><p className="text-sm font-semibold text-sky-700">Carbohidratos · 40%</p><p className="text-2xl font-bold text-sky-900 mt-1">{Math.round(macrosCalculados.carbohidratos)} g</p></div>
            </div>}

            <div className="rounded-lg border border-gray-200 overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left px-4 py-2 font-semibold text-gray-700">Nivel</th>
                    <th className="text-left px-4 py-2 font-semibold text-gray-700">Descripción</th>
                    <th className="text-left px-4 py-2 font-semibold text-gray-700">Factor</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {Object.values(ACTIVITY_FACTORS).map((item) => (
                    <tr key={item.label}>
                      <td className="px-4 py-2 text-gray-900">{item.label}</td>
                      <td className="px-4 py-2 text-gray-600">{item.desc}</td>
                      <td className="px-4 py-2 text-gray-900">{item.factor}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex items-center justify-end">
              <button
                onClick={() => void guardarCalculoNutricional()}
                disabled={!selectedUsuario || !canComputeNutri || guardandoNutri}
                className="px-4 py-2 text-sm bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors inline-flex items-center gap-2"
              >
                <i className={`ri-save-line ${guardandoNutri ? 'animate-pulse' : ''}`}></i>
                {guardandoNutri ? 'Guardando...' : 'Guardar cálculo'}
              </button>
            </div>

            <div className="rounded-lg border border-gray-200 overflow-hidden">
              <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
                <h4 className="font-semibold text-gray-800">Historial nutricional</h4>
              </div>
              {loadingNutriHistorial ? (
                <div className="p-4 text-sm text-gray-500">Cargando historial...</div>
              ) : nutriHistorial.length === 0 ? (
                <div className="p-4 text-sm text-gray-500">Sin cálculos guardados para este usuario.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="text-left px-4 py-2 font-semibold text-gray-700">Fecha</th>
                        <th className="text-left px-4 py-2 font-semibold text-gray-700">Sexo</th>
                        <th className="text-left px-4 py-2 font-semibold text-gray-700">Peso/Altura/Edad</th>
                        <th className="text-left px-4 py-2 font-semibold text-gray-700">Actividad</th>
                        <th className="text-left px-4 py-2 font-semibold text-gray-700">TMB</th>
                        <th className="text-left px-4 py-2 font-semibold text-gray-700">GET</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {nutriHistorial.map((row) => (
                        <tr key={row.id}>
                          <td className="px-4 py-2 text-gray-900">{new Date(row.created_at).toLocaleString('es-CL')}</td>
                          <td className="px-4 py-2 text-gray-900">{row.sexo === 'hombre' ? 'Hombre' : 'Mujer'}</td>
                          <td className="px-4 py-2 text-gray-700">
                            {`${row.peso_kg}kg / ${row.altura_cm}cm / ${row.edad} años`}
                          </td>
                          <td className="px-4 py-2 text-gray-700">
                            {`${ACTIVITY_FACTORS[row.actividad].label} (${row.factor_actividad})`}
                          </td>
                          <td className="px-4 py-2 text-gray-900">{`${Math.round(row.tmb).toLocaleString('es-CL')} kcal`}</td>
                          <td className="px-4 py-2 text-gray-900">{`${Math.round(row.get_kcal).toLocaleString('es-CL')} kcal`}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 mt-8 overflow-hidden">
          <div className="p-6 border-b border-gray-200 flex items-center justify-between gap-4">
            <div>
              <h3 className="text-lg font-bold text-gray-900">Reportes de Composición Corporal</h3>
              <p className="text-sm text-gray-500 mt-1">Importados desde FitMao y almacenados en Supabase.</p>
            </div>
            <button
              onClick={() => {
                if (!selectedUsuario) return;
                setReportesError(null);
                setReportesNotice(null);
                setShowImportModal(true);
              }}
              disabled={!selectedUsuario}
              className="px-4 py-2 text-sm bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors whitespace-nowrap flex items-center gap-2"
            >
              <i className="ri-file-add-line"></i>
              Importar reporte
            </button>
          </div>

          {reportesError && (
            <div className="mx-6 mt-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {reportesError}
            </div>
          )}
          {reportesNotice && (
            <div className="mx-6 mt-4 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">
              {reportesNotice}
            </div>
          )}

          {loadingReportes ? (
            <div className="p-6">
              <div className="flex items-center gap-3 text-gray-600">
                <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                Cargando reportes...
              </div>
            </div>
          ) : reportesComposicion.length === 0 ? (
            <div className="text-center py-12">
              <i className="ri-file-chart-line text-5xl text-gray-300"></i>
              <p className="text-gray-500 mt-4">No hay reportes importados para este usuario</p>
              <p className="text-sm text-gray-400 mt-2">Usa "Importar reporte" para guardar el primer informe FitMao.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-y border-gray-200">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Fecha medicion</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Fuente</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Resumen</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Archivo</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Cargado</th>
                    <th className="px-6 py-4 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {reportesComposicion.map((reporte) => (
                    <tr key={reporte.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {new Date(`${reporte.fecha_medicion}T00:00:00`).toLocaleDateString('es-CL')}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-700">
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                          {String(reporte.fuente || 'fitmao').toUpperCase()}
                        </span>
                        {Boolean(reporte.meta?.ultima_correccion_at) && (
                          <span className="ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                            Corregido
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-700">{getResumenLabel(reporte.resumen)}</td>
                      <td className="px-6 py-4 text-sm text-gray-700">
                        <div>{getArchivoLabel(reporte)}</div>
                        <div className="text-xs text-gray-500">{formatBytes(reporte.file_size)}</div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {new Date(reporte.created_at).toLocaleString('es-CL')}
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="inline-flex flex-wrap justify-end gap-2">
                        {reporte.progreso_id && (
                          <button
                            type="button"
                            onClick={() => void openCorrectionModal(reporte)}
                            className="px-3 py-2 text-sm bg-amber-50 border border-amber-200 text-amber-800 rounded-lg hover:bg-amber-100 transition-colors whitespace-nowrap inline-flex items-center gap-2"
                          >
                            <i className="ri-edit-2-line"></i>
                            Corregir
                          </button>
                        )}
                        <button
                          onClick={() => openReporte(reporte)}
                          disabled={openingReporteId === reporte.id}
                          className="px-3 py-2 text-sm bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors whitespace-nowrap inline-flex items-center gap-2"
                        >
                          <i className={`ri-external-link-line ${openingReporteId === reporte.id ? 'animate-pulse' : ''}`}></i>
                          Ver informe
                        </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 mt-8 overflow-hidden">
          <div className="p-6 border-b border-gray-200 flex items-center justify-between">
            <div>
              <h3 className="text-lg font-bold text-gray-900">Entrenamientos por día</h3>
              <p className="text-sm text-gray-500 mt-1">
                Sesiones y ejercicios completados (para control y análisis administrativo)
              </p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => {
                  if (selectedUsuario) {
                    setEntrenamientosPage(1);
                    setExpandedDay(null);
                    fetchEntrenamientos(selectedUsuario.id);
                  }
                }}
                className="px-3 py-2 text-sm bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap flex items-center gap-2"
                disabled={!selectedUsuario || loadingEntrenamientos}
              >
                <i className={`ri-refresh-line ${loadingEntrenamientos ? 'animate-spin' : ''}`}></i>
                Actualizar
              </button>
              <div className="text-sm text-gray-500">
                {daysEntrenados.length} día{daysEntrenados.length !== 1 ? 's' : ''}
              </div>
            </div>
          </div>

          {loadingEntrenamientos ? (
            <div className="p-6">
              <div className="flex items-center gap-3 text-gray-600">
                <div className="w-5 h-5 border-2 border-teal-500 border-t-transparent rounded-full animate-spin"></div>
                Cargando entrenamientos...
              </div>
            </div>
          ) : daysEntrenados.length === 0 ? (
            <div className="text-center py-12">
              <i className="ri-calendar-event-line text-5xl text-gray-300"></i>
              <p className="text-gray-500 mt-4">Aún no hay entrenamientos registrados</p>
              <p className="text-sm text-gray-400 mt-2">
                Esta sección se alimenta cuando el usuario marca ejercicios como completados en la app (con conexión a internet).
              </p>
              <p className="text-sm text-gray-400 mt-2">
                Si acabas de completar una sesión, presiona “Actualizar” o recarga la página.
              </p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {daysEntrenadosPage.map((day) => {
                const stats = getDayStats(day);
                const expanded = expandedDay === day;
                return (
                  <div key={day} className="p-6">
                    <button
                      onClick={() => setExpandedDay(expanded ? null : day)}
                      className="w-full flex items-center justify-between cursor-pointer"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center">
                          <i className="ri-calendar-check-line text-teal-600 text-xl"></i>
                        </div>
                        <div className="text-left">
                          <div className="font-semibold text-gray-900">
                            {parseISODateLocal(day).toLocaleDateString('es-CL', {
                              weekday: 'long',
                              year: 'numeric',
                              month: '2-digit',
                              day: '2-digit',
                            })}
                          </div>
                          <div className="text-sm text-gray-500">
                            {stats.sesiones} sesión{stats.sesiones !== 1 ? 'es' : ''} · {stats.ejercicios} ejercicio{stats.ejercicios !== 1 ? 's' : ''} ·{' '}
                            {Math.round(stats.tonnage).toLocaleString('es-CL')} kg
                          </div>
                        </div>
                      </div>
                      <div className="text-gray-400">
                        <i className={`ri-arrow-down-s-line text-2xl transition-transform ${expanded ? 'rotate-180' : ''}`}></i>
                      </div>
                    </button>

                    {expanded && (
                      <div className="mt-5 space-y-4">
                        {(entrenamientosByDay[day] || []).map((ses) => {
                          const ejercicios = (ses.entrenamiento_ejercicios || []).filter((x) => x.completado);
                          const rutinaName = ses.rutina_id ? rutinaNombreById[String(ses.rutina_id)] : '';
                          return (
                            <div key={ses.id} className="border border-gray-200 rounded-xl p-4">
                              <div className="flex items-center justify-between">
                                <div>
                                  <div className="font-semibold text-gray-900">
                                    Sesión {ses.sesion_id}
                                    {rutinaName ? ` · ${rutinaName}` : ''}
                                  </div>
                                  <div className="text-sm text-gray-500">
                                    {ejercicios.length} ejercicio{ejercicios.length !== 1 ? 's' : ''} completado{ejercicios.length !== 1 ? 's' : ''}
                                  </div>
                                </div>
                              </div>

                              {ejercicios.length === 0 ? (
                                <div className="text-sm text-gray-500 mt-3">Sin ejercicios completados.</div>
                              ) : (
                                <div className="mt-3 overflow-x-auto">
                                  <table className="w-full">
                                    <thead className="bg-gray-50 border border-gray-200">
                                      <tr>
                                        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Ejercicio</th>
                                        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Series</th>
                                        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Reps</th>
                                        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Peso plan</th>
                                        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Peso usado</th>
                                      </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-200 border border-gray-200">
                                      {ejercicios.map((ex) => (
                                        <tr key={ex.id} className="hover:bg-gray-50">
                                          <td className="px-3 py-2 text-sm text-gray-900">
                                            <div className="font-medium">{ex.nombre}</div>
                                            {ex.categoria && <div className="text-xs text-gray-500">{ex.categoria}</div>}
                                          </td>
                                          <td className="px-3 py-2 text-sm text-gray-900">{ex.series ?? '-'}</td>
                                          <td className="px-3 py-2 text-sm text-gray-900">{ex.repeticiones ?? '-'}</td>
                                          <td className="px-3 py-2 text-sm text-gray-900">{ex.peso_plan ?? '-'}</td>
                                          <td className="px-3 py-2 text-sm font-semibold text-teal-700">{ex.peso_usado ?? '-'}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}

              {daysEntrenados.length > ENTRENAMIENTOS_DIAS_POR_PAGINA && (
                <div className="px-6 py-4 border-t border-gray-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div className="text-sm text-gray-500">
                    Mostrando {entrenamientosStart + 1}-{Math.min(entrenamientosEnd, daysEntrenados.length)} de {daysEntrenados.length} días
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => {
                        setExpandedDay(null);
                        setEntrenamientosPage(1);
                      }}
                      disabled={entrenamientosPageSafe === 1}
                      className="w-9 h-9 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                      title="Primera"
                    >
                      <i className="ri-skip-back-mini-line"></i>
                    </button>
                    <button
                      onClick={() => {
                        setExpandedDay(null);
                        setEntrenamientosPage((p) => Math.max(1, p - 1));
                      }}
                      disabled={entrenamientosPageSafe === 1}
                      className="w-9 h-9 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                      title="Anterior"
                    >
                      <i className="ri-arrow-left-s-line"></i>
                    </button>
                    <span className="px-3 text-sm font-medium text-gray-700">
                      {entrenamientosPageSafe} / {totalEntrenamientosPages}
                    </span>
                    <button
                      onClick={() => {
                        setExpandedDay(null);
                        setEntrenamientosPage((p) => Math.min(totalEntrenamientosPages, p + 1));
                      }}
                      disabled={entrenamientosPageSafe >= totalEntrenamientosPages}
                      className="w-9 h-9 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                      title="Siguiente"
                    >
                      <i className="ri-arrow-right-s-line"></i>
                    </button>
                    <button
                      onClick={() => {
                        setExpandedDay(null);
                        setEntrenamientosPage(totalEntrenamientosPages);
                      }}
                      disabled={entrenamientosPageSafe >= totalEntrenamientosPages}
                      className="w-9 h-9 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                      title="Última"
                    >
                      <i className="ri-skip-forward-mini-line"></i>
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {showModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-200">
                <h2 className="text-2xl font-bold text-gray-900">Registrar Progreso</h2>
              </div>

              <form onSubmit={handleSubmit} className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Fecha *
                    </label>
                    <input
                      type="date"
                      required
                      value={formData.fecha}
                      onChange={(e) => setFormData({ ...formData, fecha: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Peso (kg) *
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      required
                      value={formData.peso}
                      onChange={(e) => setFormData({ ...formData, peso: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Grasa Corporal (%) *
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      required
                      value={formData.grasa_corporal}
                      onChange={(e) => setFormData({ ...formData, grasa_corporal: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Masa Muscular (kg) *
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      required
                      value={formData.masa_muscular}
                      onChange={(e) => setFormData({ ...formData, masa_muscular: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Notas
                    </label>
                    <textarea
                      value={formData.notas}
                      onChange={(e) => setFormData({ ...formData, notas: e.target.value })}
                      rows={3}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                      placeholder="Observaciones adicionales..."
                    />
                  </div>
                </div>

                <div className="flex gap-3 mt-6">
                  <button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      resetForm();
                    }}
                    className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-6 py-3 bg-teal-500 text-white rounded-lg hover:bg-teal-600 transition-colors whitespace-nowrap"
                  >
                    Guardar Progreso
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {showImportModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-200">
                <h2 className="text-2xl font-bold text-gray-900">Importar informe FitMao</h2>
                <p className="text-sm text-gray-500 mt-1">
                  Usuario: <span className="font-medium text-gray-800">{selectedUsuario?.nombre || '-'}</span>
                </p>
              </div>

              <form onSubmit={handleImportFitmao} className="p-6">
                <div className="space-y-5">
                  <div>
                    <div className="flex items-center justify-between gap-2 mb-2">
                      <label className="block text-sm font-medium text-gray-700">URL informe FitMao *</label>
                      <button
                        type="button"
                        onClick={() => setShowQrScannerModal(true)}
                        className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 transition-colors whitespace-nowrap"
                      >
                        Escanear QR
                      </button>
                    </div>
                    <input
                      type="url"
                      required
                      value={importForm.source_url}
                      onChange={(e) => {
                        setImportForm((prev) => ({ ...prev, source_url: e.target.value }));
                        setImportSourceIdentity({ usuarioId: '', email: '', nombre: '' });
                        setImportPreviewReady(false);
                        setImportIdentityConfirmed(false);
                      }}
                      placeholder="https://www.fitmao.com/..."
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Fecha medicion *</label>
                      <input
                        type="date"
                        required
                        value={importForm.fecha_medicion}
                        onChange={(e) => setImportForm((prev) => ({ ...prev, fecha_medicion: e.target.value }))}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      />
                    </div>
                    <div className="rounded-lg border border-blue-100 bg-blue-50 px-4 py-3 text-sm text-blue-700">
                      Si completas mediciones abajo, tambien se registra en el historial de progreso.
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Peso (kg) *</label>
                      <input
                        type="number"
                        step="0.1"
                        value={importForm.peso}
                        onChange={(e) => setImportForm((prev) => ({ ...prev, peso: e.target.value }))}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Grasa corporal (%) *</label>
                      <input
                        type="number"
                        step="0.1"
                        value={importForm.grasa_corporal}
                        onChange={(e) => setImportForm((prev) => ({ ...prev, grasa_corporal: e.target.value }))}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Masa muscular (kg) *</label>
                      <input
                        type="number"
                        step="0.1"
                        value={importForm.masa_muscular}
                        onChange={(e) => setImportForm((prev) => ({ ...prev, masa_muscular: e.target.value }))}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">IMC</label>
                      <input
                        type="number"
                        step="0.1"
                        value={importForm.imc}
                        onChange={(e) => setImportForm((prev) => ({ ...prev, imc: e.target.value }))}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">PBF (%)</label>
                      <input
                        type="number"
                        step="0.1"
                        value={importForm.pbf}
                        onChange={(e) => setImportForm((prev) => ({ ...prev, pbf: e.target.value }))}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Grasa visceral</label>
                      <input
                        type="number"
                        step="0.1"
                        value={importForm.grasa_visceral}
                        onChange={(e) => setImportForm((prev) => ({ ...prev, grasa_visceral: e.target.value }))}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">TMB (kcal)</label>
                      <input
                        type="number"
                        step="1"
                        value={importForm.tmb}
                        onChange={(e) => setImportForm((prev) => ({ ...prev, tmb: e.target.value }))}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Notas (opcional)</label>
                    <textarea
                      rows={3}
                      value={importForm.notas}
                      onChange={(e) => setImportForm((prev) => ({ ...prev, notas: e.target.value }))}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      placeholder="Comentario interno del informe..."
                    />
                  </div>

                  {importPreviewReady && selectedUsuario && (
                    <div className="rounded-xl border-2 border-blue-200 bg-blue-50 p-4 space-y-4">
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-full bg-blue-100 text-blue-700 grid place-items-center shrink-0"><i className="ri-user-follow-line text-xl" /></div>
                        <div>
                          <p className="font-bold text-blue-950">Vista previa antes de importar</p>
                          <p className="text-sm text-blue-800 mt-1">La evaluación quedará asociada a <strong>{selectedUsuario.nombre}</strong> · {selectedUsuario.email}</p>
                          {(importSourceIdentity.nombre || importSourceIdentity.email || importSourceIdentity.usuarioId) && <p className="text-xs text-blue-700 mt-1">Identidad informada por el QR: {[importSourceIdentity.nombre, importSourceIdentity.email, importSourceIdentity.usuarioId].filter(Boolean).join(' · ')}</p>}
                        </div>
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm">
                        <div className="rounded-lg bg-white border border-blue-100 p-3"><span className="block text-xs text-gray-500">Fecha</span><strong>{new Date(`${importForm.fecha_medicion}T12:00:00`).toLocaleDateString('es-CL')}</strong></div>
                        <div className="rounded-lg bg-white border border-blue-100 p-3"><span className="block text-xs text-gray-500">Peso</span><strong>{importForm.peso} kg</strong></div>
                        <div className="rounded-lg bg-white border border-blue-100 p-3"><span className="block text-xs text-gray-500">Grasa</span><strong>{importForm.grasa_corporal}%</strong></div>
                        <div className="rounded-lg bg-white border border-blue-100 p-3"><span className="block text-xs text-gray-500">Masa muscular</span><strong>{importForm.masa_muscular} kg</strong></div>
                      </div>
                      <label className="flex items-start gap-3 rounded-lg bg-white border border-blue-200 p-3 cursor-pointer">
                        <input type="checkbox" checked={importIdentityConfirmed} onChange={(event) => setImportIdentityConfirmed(event.target.checked)} className="mt-0.5 h-4 w-4 accent-blue-600" />
                        <span className="text-sm text-gray-800">Confirmo que revisé la identidad, las unidades y que estos datos corresponden al usuario seleccionado.</span>
                      </label>
                    </div>
                  )}
                </div>

                <div className="flex gap-3 mt-6">
                  <button
                    type="button"
                    onClick={() => {
                      setShowImportModal(false);
                      resetImportForm();
                    }}
                    className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={importingReporte || extractingFitmao}
                    className="flex-1 px-6 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-60 disabled:cursor-not-allowed transition-colors whitespace-nowrap flex items-center justify-center gap-2"
                  >
                    {(importingReporte || extractingFitmao) && <i className="ri-loader-4-line animate-spin"></i>}
                    {extractingFitmao ? 'Leyendo FitMao…' : importPreviewReady ? 'Confirmar e importar' : 'Recuperar y revisar datos'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {correctionReport && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
              <div className="px-6 py-5 border-b border-gray-200 flex items-start justify-between gap-4">
                <div>
                  <h2 className="text-xl font-bold text-gray-900">Corregir evaluación corporal</h2>
                  <p className="text-sm text-gray-500 mt-1">
                    {selectedUsuario?.nombre || '-'} · {new Date(`${correctionReport.fecha_medicion}T12:00:00`).toLocaleDateString('es-CL')}
                  </p>
                </div>
                <button type="button" onClick={() => setCorrectionReport(null)} className="text-gray-500 hover:text-gray-700" aria-label="Cerrar">
                  <i className="ri-close-line text-2xl"></i>
                </button>
              </div>

              <form onSubmit={handleSaveCorrection} className="p-6 space-y-5">
                <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                  El informe original no se reemplaza. El sistema guardará los valores anteriores, los nuevos y el motivo de esta corrección.
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {([
                    ['peso', 'Peso (kg) *'],
                    ['grasa_corporal', 'Grasa corporal (%) *'],
                    ['masa_muscular', 'Masa muscular (kg) *'],
                  ] as const).map(([key, label]) => (
                    <label key={key} className="block text-sm font-medium text-gray-700">
                      {label}
                      <input
                        type="number"
                        step="0.1"
                        required
                        value={correctionForm[key]}
                        onChange={(event) => setCorrectionForm((prev) => ({ ...prev, [key]: event.target.value }))}
                        className="mt-2 w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                      />
                    </label>
                  ))}
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {([
                    ['imc', 'IMC'],
                    ['pbf', 'PBF (%)'],
                    ['grasa_visceral', 'Grasa visceral'],
                    ['tmb', 'TMB (kcal)'],
                  ] as const).map(([key, label]) => (
                    <label key={key} className="block text-sm font-medium text-gray-700">
                      {label}
                      <input
                        type="number"
                        step={key === 'tmb' ? '1' : '0.1'}
                        value={correctionForm[key]}
                        onChange={(event) => setCorrectionForm((prev) => ({ ...prev, [key]: event.target.value }))}
                        className="mt-2 w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                      />
                    </label>
                  ))}
                </div>
                <label className="block text-sm font-medium text-gray-700">
                  Motivo de la corrección *
                  <textarea
                    rows={3}
                    required
                    minLength={5}
                    value={correctionForm.motivo}
                    onChange={(event) => setCorrectionForm((prev) => ({ ...prev, motivo: event.target.value }))}
                    placeholder="Ej.: el valor de masa muscular fue transcrito incorrectamente."
                    className="mt-2 w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                  />
                </label>

                <div className="border border-gray-200 rounded-xl overflow-hidden">
                  <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 font-semibold text-gray-900">Historial de correcciones</div>
                  {loadingCorrectionHistory ? (
                    <div className="p-4 text-sm text-gray-500"><i className="ri-loader-4-line animate-spin mr-2"></i>Cargando...</div>
                  ) : correctionHistory.length === 0 ? (
                    <div className="p-4 text-sm text-gray-500">Esta evaluación aún no tiene correcciones.</div>
                  ) : (
                    <div className="divide-y divide-gray-100">
                      {correctionHistory.map((item) => {
                        const before = item.valores_antes || {};
                        const after = item.valores_despues || {};
                        return (
                          <div key={item.id} className="p-4 text-sm">
                            <div className="flex flex-wrap items-center justify-between gap-2">
                              <strong className="text-gray-900">{item.motivo}</strong>
                              <span className="text-xs text-gray-500">{new Date(item.created_at).toLocaleString('es-CL')}</span>
                            </div>
                            <p className="text-xs text-gray-600 mt-2">
                              Peso {String(before.peso ?? '-')} → {String(after.peso ?? '-')} kg · Grasa {String(before.grasa_corporal ?? '-')} → {String(after.grasa_corporal ?? '-')}% · Masa muscular {String(before.masa_muscular ?? '-')} → {String(after.masa_muscular ?? '-')} kg
                            </p>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                <div className="flex justify-end gap-3 pt-1">
                  <button type="button" onClick={() => setCorrectionReport(null)} className="px-5 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50">Cancelar</button>
                  <button type="submit" disabled={savingCorrection} className="px-5 py-2.5 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-60 inline-flex items-center gap-2">
                    {savingCorrection && <i className="ri-loader-4-line animate-spin"></i>}
                    Guardar corrección
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {showQrScannerModal && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[60] p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-lg w-full overflow-hidden">
              <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
                <h3 className="text-lg font-bold text-gray-900">Escanear QR FitMao</h3>
                <button
                  type="button"
                  onClick={() => setShowQrScannerModal(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <i className="ri-close-line text-2xl"></i>
                </button>
              </div>
              <div className="p-5 space-y-3">
                <p className="text-sm text-gray-600">
                  Apunta la cámara al código QR de la máquina. Se cargará la URL automáticamente.
                </p>
                <div className="rounded-lg overflow-hidden border border-gray-200 bg-black">
                  <video ref={qrVideoRef} className="w-full h-[280px] object-cover" muted playsInline />
                </div>
                <div className="flex items-center justify-between gap-2">
                  <button
                    type="button"
                    onClick={() => qrImageInputRef.current?.click()}
                    disabled={readingQrImage}
                    className="px-3 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-60"
                  >
                    {readingQrImage ? 'Leyendo imagen...' : 'Subir imagen QR'}
                  </button>
                  <input
                    ref={qrImageInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleQrImagePick}
                  />
                  <span className="text-xs text-gray-500">Fallback sin cámara</span>
                </div>
                {qrScanError && (
                  <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                    {qrScanError}
                  </div>
                )}
                {qrScanSuccess && (
                  <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
                    {qrScanSuccess}
                  </div>
                )}
                {!qrScanError && (
                  <div className="text-xs text-gray-500">
                    {scanningQr ? 'Escaneando...' : 'Iniciando cámara...'}
                  </div>
                )}
              </div>
              <div className="px-5 py-4 border-t border-gray-200 flex justify-end">
                <button
                  type="button"
                  onClick={() => setShowQrScannerModal(false)}
                  className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
                >
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        )}

        {showRutinaAssignModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-xl w-full max-h-[85vh] flex flex-col">
              <div className="p-6 border-b border-gray-200 flex items-start justify-between gap-4">
                <div>
                  <h2 className="text-xl font-bold text-gray-900">Asignar rutina</h2>
                  <p className="text-sm text-gray-500 mt-1">
                    Usuario: <span className="font-medium text-gray-800">{selectedUsuario?.nombre || '-'}</span>
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setShowRutinaAssignModal(false)}
                  className="w-9 h-9 rounded-lg text-gray-500 hover:bg-gray-100"
                  aria-label="Cerrar"
                >
                  <i className="ri-close-line text-xl"></i>
                </button>
              </div>
              <div className="p-6 min-h-0 flex-1 flex flex-col gap-4">
                <div className="relative">
                  <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
                  <input
                    type="search"
                    value={rutinaAssignSearch}
                    onChange={(event) => setRutinaAssignSearch(event.target.value)}
                    placeholder="Buscar rutina por nombre..."
                    className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  />
                </div>
                <div className="border border-gray-200 rounded-xl overflow-y-auto min-h-48 max-h-96">
                  {loadingRutinasDisponibles ? (
                    <div className="p-8 text-center text-gray-500">
                      <i className="ri-loader-4-line animate-spin mr-2"></i>
                      Cargando rutinas...
                    </div>
                  ) : rutinasDisponiblesFiltradas.length === 0 ? (
                    <div className="p-8 text-center text-gray-500">No hay rutinas disponibles.</div>
                  ) : (
                    <div className="divide-y divide-gray-100">
                      {rutinasDisponiblesFiltradas.map((rutina) => (
                        <label key={rutina.id} className="p-4 flex items-start gap-3 cursor-pointer hover:bg-teal-50">
                          <input
                            type="radio"
                            name="rutina-para-asignar"
                            value={rutina.id}
                            checked={rutinaSeleccionadaId === rutina.id}
                            onChange={() => setRutinaSeleccionadaId(rutina.id)}
                            className="mt-1 w-4 h-4 accent-teal-700"
                          />
                          <span className="min-w-0">
                            <span className="block font-semibold text-gray-900">{rutina.nombre}</span>
                            {rutina.descripcion && <span className="block text-sm text-gray-500 mt-1 line-clamp-2">{rutina.descripcion}</span>}
                          </span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
                <p className="text-xs text-gray-500">
                  Se creará una copia independiente para este usuario. Los cambios posteriores en la rutina base no modificarán su plan.
                </p>
              </div>
              <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setShowRutinaAssignModal(false)}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={() => void handleAssignRutina()}
                  disabled={!rutinaSeleccionadaId || asignandoRutina}
                  className="px-4 py-2 bg-teal-700 text-white rounded-lg hover:bg-teal-800 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {asignandoRutina ? 'Asignando...' : 'Asignar rutina'}
                </button>
              </div>
            </div>
          </div>
        )}

        {showRutinaChangeModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
              <div className="p-6 border-b border-gray-200">
                <h2 className="text-xl font-bold text-gray-900">Cambio de rutina</h2>
                <p className="text-sm text-gray-500 mt-1">
                  Usuario: <span className="font-medium text-gray-800">{selectedUsuario?.nombre || '-'}</span>
                </p>
              </div>
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Fecha programada
                  </label>
                  <input
                    type="date"
                    value={rutinaChangeDate}
                    onChange={(e) => setRutinaChangeDate(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm"
                  />
                  <p className="text-xs text-gray-500 mt-2">
                    Si dejas vacío, se elimina la programación.
                  </p>
                </div>
                <div className="flex gap-3 pt-2">
                  <button
                    type="button"
                    onClick={() => setShowRutinaChangeModal(false)}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    onClick={handleSaveRutinaChange}
                    disabled={savingRutinaChange}
                    className="flex-1 px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
                  >
                    {savingRutinaChange ? 'Guardando...' : 'Guardar'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {showCoachModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
              <div className="p-6 border-b border-gray-200">
                <h2 className="text-xl font-bold text-gray-900">Asignar coach</h2>
                <p className="text-sm text-gray-500 mt-1">
                  Usuario: <span className="font-medium text-gray-800">{selectedUsuario?.nombre || '-'}</span>
                </p>
              </div>
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Coach / Instructor
                  </label>
                  <select
                    value={selectedCoachId}
                    onChange={(e) => setSelectedCoachId(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
                  >
                    <option value="">Sin asignar</option>
                    {coachOptions.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.nombre} ({c.role})
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex gap-3 pt-2">
                  <button
                    type="button"
                    onClick={() => setShowCoachModal(false)}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    onClick={handleSaveCoach}
                    disabled={savingCoach}
                    className="flex-1 px-4 py-2 bg-indigo-500 text-white rounded-lg hover:bg-indigo-600 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
                  >
                    {savingCoach ? 'Guardando...' : 'Guardar'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {showUserSelector && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-200">
                <h2 className="text-2xl font-bold text-gray-900">{isCoach ? 'Clientes del gimnasio' : 'Seleccionar Usuario'}</h2>
                {isCoach && <p className="mt-1 text-sm text-gray-500">Puedes consultar a los clientes de la sucursal y registrar sus evaluaciones iniciales.</p>}
              </div>

              <div className="p-6">
                <div className="mb-4">
                  <div className="relative">
                    <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
                    <input
                      type="text"
                      placeholder="Buscar usuario..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    />
                  </div>
                </div>

                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {filteredUsuarios.map((usuario) => (
                    <div key={usuario.id} className="flex items-center gap-3 p-3 hover:bg-gray-50 rounded-lg transition-colors">
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedUsuario(usuario);
                          setShowUserSelector(false);
                        }}
                        className="flex flex-1 min-w-0 items-center gap-3 text-left"
                      >
                        <span className="w-10 h-10 shrink-0 rounded-full bg-teal-100 flex items-center justify-center">
                          <span className="text-teal-600 font-semibold text-sm">{usuario.avatar}</span>
                        </span>
                        <span className="flex-1 min-w-0">
                          <span className="block font-medium text-gray-900 truncate">{usuario.nombre}</span>
                          <span className="block text-sm text-gray-500 truncate">{usuario.tipo_membresia || 'Sin plan'}</span>
                        </span>
                        <i className="ri-arrow-right-s-line text-gray-400"></i>
                      </button>
                      {isCoach && (
                        <div className="flex shrink-0 gap-2">
                          <button
                            type="button"
                            onClick={() => void openAcomaSuggestionForUsuario(usuario)}
                            disabled={usuario.estado.toLocaleLowerCase('es-CL') !== 'activo' || loadingRecomendacionesIA}
                            className="rounded-lg border border-violet-200 bg-violet-50 px-3 py-2 text-xs font-bold text-violet-700 hover:bg-violet-100 disabled:opacity-50 disabled:cursor-not-allowed"
                            title="Solicitar una propuesta de rutina con ACOMA"
                          >
                            <i className="ri-sparkling-2-line mr-1"></i>
                            Rutina IA
                          </button>
                          <button
                            type="button"
                            onClick={() => openFamilySuggestionForUsuario(usuario)}
                            disabled={usuario.estado.toLocaleLowerCase('es-CL') !== 'activo'}
                            className="rounded-lg border border-teal-200 bg-teal-50 px-3 py-2 text-xs font-bold text-teal-800 hover:bg-teal-100 disabled:opacity-50 disabled:cursor-not-allowed"
                            title="Generar una familia completa en segundo plano"
                          >
                            <i className="ri-node-tree mr-1"></i>
                            Familia IA
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                {filteredUsuarios.length === 0 && (
                  <div className="text-center py-8">
                    <i className="ri-user-line text-4xl text-gray-300"></i>
                    <p className="text-gray-500 mt-2">No se encontraron usuarios</p>
                  </div>
                )}

                <div className="mt-6">
                  <button
                    onClick={() => setShowUserSelector(false)}
                    className="w-full px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap"
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {historialIASeleccionado && (
          <div className="fixed inset-0 z-[115] bg-black/65 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl border border-slate-200 shadow-2xl w-full max-w-6xl max-h-[92vh] overflow-hidden flex flex-col">
              <div className="px-5 py-4 border-b border-slate-200 flex items-start justify-between gap-4">
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="text-lg font-bold text-gray-950">Historial de revisión IA</h3>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${historialIASeleccionado.estado === 'aprobada' ? 'bg-emerald-100 text-emerald-700' : historialIASeleccionado.estado === 'rechazada' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-600'}`}>
                      {historialIASeleccionado.estado === 'aprobada' ? 'Aprobada' : historialIASeleccionado.estado === 'rechazada' ? 'Rechazada' : 'Cancelada'}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 mt-1">{historialIASeleccionado.rutina_propuesta?.nombre || 'Propuesta de rutina'} · {selectedUsuario?.nombre}</p>
                </div>
                <button type="button" onClick={() => setHistorialIASeleccionado(null)} className="w-9 h-9 rounded-lg border border-gray-200 hover:bg-gray-50 flex items-center justify-center" aria-label="Cerrar historial">
                  <i className="ri-close-line text-lg text-gray-700" />
                </button>
              </div>
              <div className="p-5 overflow-y-auto space-y-5">
                <div className="grid md:grid-cols-3 gap-3">
                  <div className="rounded-xl border border-gray-200 p-4">
                    <p className="text-xs uppercase tracking-wide font-bold text-gray-400">Generación</p>
                    <p className="text-sm font-semibold text-gray-800 mt-1">{new Date(historialIASeleccionado.created_at).toLocaleString('es-CL')}</p>
                  </div>
                  <div className="rounded-xl border border-gray-200 p-4">
                    <p className="text-xs uppercase tracking-wide font-bold text-gray-400">Resolución humana</p>
                    <p className="text-sm font-semibold text-gray-800 mt-1">{historialIASeleccionado.revisada_at ? new Date(historialIASeleccionado.revisada_at).toLocaleString('es-CL') : 'Sin fecha registrada'}</p>
                  </div>
                  <div className="rounded-xl border border-gray-200 p-4">
                    <p className="text-xs uppercase tracking-wide font-bold text-gray-400">Ediciones auditadas</p>
                    <p className="text-sm font-semibold text-gray-800 mt-1">{historialIASeleccionado.cantidad_ediciones}</p>
                  </div>
                </div>

                {historialIASeleccionado.comentario_revision && (
                  <div className={`rounded-xl border p-4 ${historialIASeleccionado.estado === 'rechazada' ? 'border-red-200 bg-red-50' : 'border-emerald-200 bg-emerald-50'}`}>
                    <p className="text-xs uppercase tracking-wide font-bold text-gray-500">Comentario de resolución</p>
                    <p className="text-sm text-gray-800 mt-1">{historialIASeleccionado.comentario_revision}</p>
                  </div>
                )}

                {historialIASeleccionado.acoma_execution_id && (
                  <div className="text-xs text-gray-500 flex flex-wrap gap-2">
                    <span className="font-semibold">Trazabilidad ACOMA:</span>
                    <code className="break-all">{historialIASeleccionado.acoma_execution_id}</code>
                  </div>
                )}

                {loadingRevisionesIA ? (
                  <div className="py-10 text-center text-sm text-gray-500"><i className="ri-loader-4-line animate-spin mr-2" />Cargando comparación…</div>
                ) : revisionesIA.length === 0 ? (
                  <div className="rounded-xl border border-dashed border-gray-300 p-6 text-center">
                    <i className="ri-checkbox-circle-line text-2xl text-emerald-600" />
                    <p className="font-semibold text-gray-800 mt-2">La propuesta se resolvió sin editar su contenido</p>
                    <p className="text-sm text-gray-500 mt-1">No existe una comparación antes/después porque el coach aprobó o rechazó la sugerencia original.</p>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {revisionesIA.map((revision, index) => (
                      <section key={revision.id} className="rounded-2xl border border-gray-200 p-4">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-4">
                          <div>
                            <p className="font-bold text-gray-900">Edición {index + 1}</p>
                            <p className="text-xs text-gray-500 mt-0.5">{revision.admin?.nombre || 'Equipo autorizado'} · {new Date(revision.created_at).toLocaleString('es-CL')}</p>
                          </div>
                          {revision.comentario && <span className="text-xs rounded-lg bg-violet-50 text-violet-800 px-3 py-2">{revision.comentario}</span>}
                        </div>
                        <div className="grid lg:grid-cols-2 gap-4">
                          <RoutineSnapshotPanel title="Antes" snapshot={revision.propuesta_antes} tone="before" />
                          <RoutineSnapshotPanel title="Después de revisión humana" snapshot={revision.propuesta_despues} tone="after" />
                        </div>
                      </section>
                    ))}
                  </div>
                )}
              </div>
              <div className="px-5 py-4 border-t border-gray-200 flex flex-wrap justify-end gap-2">
                {historialIASeleccionado.rutina_propuesta?.id && (
                  <button onClick={() => {
                    const rutinaId = historialIASeleccionado.rutina_propuesta!.id;
                    setHistorialIASeleccionado(null);
                    openRutinaInlineModal(rutinaId, 'detail');
                  }} className="px-4 py-2 border border-violet-200 text-violet-700 rounded-lg text-sm font-semibold hover:bg-violet-50">
                    <i className="ri-eye-line mr-1" />Ver rutina propuesta
                  </button>
                )}
                <button onClick={() => setHistorialIASeleccionado(null)} className="px-4 py-2 bg-slate-900 text-white rounded-lg text-sm font-semibold">Cerrar</button>
              </div>
            </div>
          </div>
        )}

        {rutinaEmbedRequest && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[70] p-4">
            <div className="relative bg-white rounded-xl shadow-xl w-full max-w-[96rem] h-[92vh] overflow-hidden border border-gray-200 flex flex-col">
              <div className="h-14 shrink-0 px-4 border-b border-gray-200 flex items-center justify-between">
                <h3 className="text-base font-semibold text-gray-900">
                  {rutinaEmbedRequest.recommendationId ? 'Revisar propuesta de ACOMA' : rutinaEmbedRequest.action === 'edit' ? 'Editar rutina asignada' : 'Detalle de rutina asignada'}
                </h3>
                <button
                  type="button"
                  onClick={() => setRutinaEmbedRequest(null)}
                  className="w-9 h-9 rounded-lg border border-gray-200 hover:bg-gray-50 flex items-center justify-center"
                  aria-label="Cerrar"
                >
                  <i className="ri-close-line text-lg text-gray-700"></i>
                </button>
              </div>
              <div className="relative flex-1 min-h-0 overflow-hidden">
                <Rutinas
                  embeddedRequest={{
                    ...rutinaEmbedRequest,
                    usuarioId: selectedUsuario?.id,
                  }}
                  onEmbeddedClose={(saved) => {
                    const eraPropuesta = Boolean(rutinaEmbedRequest.recommendationId);
                    setRutinaEmbedRequest(null);
                    if (saved && selectedUsuario) {
                      void fetchRecomendacionesIA(selectedUsuario.id);
                      void fetchRutinasAsignadas(selectedUsuario.id);
                      void fetchFamiliaUsuario(selectedUsuario.id);
                      setReportesNotice(eraPropuesta ? 'Los ajustes del coach quedaron guardados. La propuesta sigue pendiente hasta su aprobación.' : 'La rutina del socio quedó actualizada.');
                    }
                  }}
                />
              </div>
            </div>
          </div>
        )}

      </div>
      {showFamilySuggestionModal && selectedUsuario && (
        <div className="fixed inset-0 z-[112] bg-black/60 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl border border-teal-200 shadow-2xl w-full max-w-2xl max-h-[92vh] overflow-hidden flex flex-col">
            <header className="p-5 border-b border-teal-100 bg-teal-50 flex items-start justify-between gap-4">
              <div>
                <h2 className="text-xl font-bold text-teal-950 flex items-center gap-2"><i className="ri-node-tree" />Generar familia con IA</h2>
                <p className="text-sm text-teal-700 mt-1">Para {selectedUsuario.nombre}. ACOMA trabajará en segundo plano y la familia no se aplicará hasta que el coach la apruebe.</p>
              </div>
              <button disabled={requestingFamilySuggestion} onClick={() => setShowFamilySuggestionModal(false)} className="w-10 h-10 rounded-lg text-gray-500 hover:bg-white disabled:opacity-50" aria-label="Cerrar"><i className="ri-close-line text-2xl" /></button>
            </header>
            <div className="p-5 overflow-y-auto space-y-4">
              <label className="block text-sm font-semibold text-gray-800">Objetivo y criterios de la familia
                <textarea value={familyForm.instrucciones} onChange={(event) => setFamilyForm((current) => ({ ...current, instrucciones: event.target.value }))} maxLength={2000} rows={4} placeholder="Ej.: crear una progresión de fuerza e hipertrofia para tren inferior, cuidando la rodilla derecha y priorizando técnica." className="mt-1.5 w-full px-3 py-2.5 border border-gray-300 rounded-lg font-normal resize-y" />
                <span className="mt-1 block text-xs font-normal text-gray-500">Mínimo 10 caracteres. Incluye restricciones y prioridades que el coach deba revisar.</span>
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
                <label className="text-sm font-semibold text-gray-800">Nivel
                  <select value={familyForm.nivel} onChange={(event) => setFamilyForm((current) => ({ ...current, nivel: event.target.value }))} className="mt-1.5 w-full px-3 py-2.5 border border-gray-300 rounded-lg bg-white font-normal"><option>Principiante</option><option>Intermedio</option><option>Avanzado</option></select>
                </label>
                <label className="text-sm font-semibold text-gray-800">Días/semana
                  <input type="number" min={1} max={7} value={familyForm.frecuencia} onChange={(event) => setFamilyForm((current) => ({ ...current, frecuencia: Number(event.target.value) }))} className="mt-1.5 w-full px-3 py-2.5 border border-gray-300 rounded-lg font-normal" />
                </label>
                <label className="text-sm font-semibold text-gray-800">Minutos
                  <input type="number" min={20} max={180} step={5} value={familyForm.duracion_minutos} onChange={(event) => setFamilyForm((current) => ({ ...current, duracion_minutos: Number(event.target.value) }))} className="mt-1.5 w-full px-3 py-2.5 border border-gray-300 rounded-lg font-normal" />
                </label>
                <label className="text-sm font-semibold text-gray-800">Etapas
                  <input type="number" min={1} max={6} value={familyForm.cantidad_etapas} onChange={(event) => setFamilyForm((current) => ({ ...current, cantidad_etapas: Number(event.target.value) }))} className="mt-1.5 w-full px-3 py-2.5 border border-gray-300 rounded-lg font-normal" />
                </label>
              </div>
              <div className="rounded-xl border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900"><i className="ri-information-line mr-2" />Puedes cerrar esta ventana después de enviarla. El resultado aparecerá automáticamente en <strong>Propuestas IA</strong>.</div>
            </div>
            <footer className="p-5 border-t bg-gray-50 flex justify-end gap-3">
              <button disabled={requestingFamilySuggestion} onClick={() => setShowFamilySuggestionModal(false)} className="px-4 py-2.5 border border-gray-300 rounded-lg text-gray-700 disabled:opacity-50">Cancelar</button>
              <button disabled={requestingFamilySuggestion || familyForm.instrucciones.trim().length < 10 || familyForm.frecuencia < 1 || familyForm.frecuencia > 7 || familyForm.duracion_minutos < 20 || familyForm.duracion_minutos > 180 || familyForm.cantidad_etapas < 1 || familyForm.cantidad_etapas > 6} onClick={() => void requestFamilySuggestion()} className="px-5 py-2.5 bg-teal-800 text-white rounded-lg font-semibold hover:bg-teal-900 disabled:opacity-50 flex items-center gap-2">{requestingFamilySuggestion ? <><i className="ri-loader-4-line animate-spin" />Enviando…</> : <><i className="ri-send-plane-2-line" />Enviar a ACOMA</>}</button>
            </footer>
          </div>
        </div>
      )}
      {showAcomaSuggestionModal && selectedUsuario && (
        <div className="fixed inset-0 z-[110] bg-black/60 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl border border-violet-200 shadow-2xl w-full max-w-2xl max-h-[92vh] overflow-hidden flex flex-col">
            <header className="p-5 border-b border-violet-100 bg-violet-50 flex items-start justify-between gap-4">
              <div>
                <h2 className="text-xl font-bold text-violet-950 flex items-center gap-2"><i className={acomaForm.tipo_sugerencia === 'renovacion' ? 'ri-refresh-line' : 'ri-sparkling-2-line'} />{acomaForm.tipo_sugerencia === 'renovacion' ? 'Renovar rutina con ACOMA' : 'Sugerir rutina con ACOMA'}</h2>
                <p className="text-sm text-violet-700 mt-1">Para {selectedUsuario.nombre}. {acomaForm.tipo_sugerencia === 'renovacion' ? 'ACOMA analizará la rutina activa, adherencia y registros disponibles. ' : ''}La propuesta quedará pendiente y no se aplicará sin aprobación.</p>
              </div>
              <button disabled={generandoRecomendacionIA} onClick={() => setShowAcomaSuggestionModal(false)} className="w-10 h-10 rounded-lg text-gray-500 hover:bg-white disabled:opacity-50" aria-label="Cerrar"><i className="ri-close-line text-2xl" /></button>
            </header>
            <div className="p-5 overflow-y-auto space-y-4">
              <label className="block text-sm font-semibold text-gray-800">Objetivo del socio
                <textarea value={acomaForm.objetivo} onChange={(event) => setAcomaForm((current) => ({ ...current, objetivo: event.target.value }))} maxLength={1000} rows={3} placeholder="Ej.: mejorar fuerza general y adherencia, cuidando la rodilla derecha" className="mt-1.5 w-full px-3 py-2.5 border border-gray-300 rounded-lg font-normal resize-y" />
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <label className="text-sm font-semibold text-gray-800">Nivel
                  <select value={acomaForm.nivel} onChange={(event) => setAcomaForm((current) => ({ ...current, nivel: event.target.value }))} className="mt-1.5 w-full px-3 py-2.5 border border-gray-300 rounded-lg bg-white font-normal"><option value="principiante">Principiante</option><option value="intermedio">Intermedio</option><option value="avanzado">Avanzado</option></select>
                </label>
                <label className="text-sm font-semibold text-gray-800">Días por semana
                  <input type="number" min={1} max={7} value={acomaForm.frecuencia_semanal} onChange={(event) => setAcomaForm((current) => ({ ...current, frecuencia_semanal: Number(event.target.value) }))} className="mt-1.5 w-full px-3 py-2.5 border border-gray-300 rounded-lg font-normal" />
                </label>
                <label className="text-sm font-semibold text-gray-800">Minutos por sesión
                  <input type="number" min={20} max={180} step={5} value={acomaForm.duracion_minutos} onChange={(event) => setAcomaForm((current) => ({ ...current, duracion_minutos: Number(event.target.value) }))} className="mt-1.5 w-full px-3 py-2.5 border border-gray-300 rounded-lg font-normal" />
                </label>
              </div>
              <label className="block text-sm font-semibold text-gray-800">Observaciones del coach <span className="font-normal text-gray-500">(opcional)</span>
                <textarea value={acomaForm.observaciones} onChange={(event) => setAcomaForm((current) => ({ ...current, observaciones: event.target.value }))} maxLength={500} rows={2} placeholder="Preferencias, equipamiento o consideraciones adicionales" className="mt-1.5 w-full px-3 py-2.5 border border-gray-300 rounded-lg font-normal resize-y" />
              </label>
              {evaluacionInicialAcoma ? (
                <div className={`rounded-xl border p-3 text-sm ${evaluacionInicialAcoma.estado === 'requiere_revision' ? 'border-amber-200 bg-amber-50 text-amber-900' : 'border-violet-200 bg-violet-50 text-violet-900'}`}>
                  <p className="font-semibold"><i className="ri-clipboard-line mr-2" />Evaluación inicial que utilizará ACOMA</p>
                  <p className="mt-1">{new Date(`${evaluacionInicialAcoma.fecha_evaluacion}T12:00:00`).toLocaleDateString('es-CL')} · {evaluacionInicialAcoma.estado.replaceAll('_', ' ')}</p>
                </div>
              ) : (
                <div className="rounded-xl border border-gray-200 bg-gray-50 p-3 text-sm text-gray-600"><i className="ri-information-line mr-2" />Este socio no tiene una evaluación inicial finalizada. ACOMA generará la propuesta sin inferir resultados funcionales.</div>
              )}
              {selectedUsuario.declaracion_salud?.estado === 'con_condicion' && <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900"><i className="ri-heart-pulse-line mr-2" />ACOMA considerará las restricciones declaradas. El coach debe verificar la propuesta antes de aprobarla.</div>}
              {acomaForm.tipo_sugerencia === 'renovacion' && <div className="rounded-xl border border-sky-200 bg-sky-50 p-3 text-sm text-sky-900"><i className="ri-history-line mr-2" />La rutina vigente conservará su historial. Solo será reemplazada cuando el coach apruebe esta propuesta.</div>}
            </div>
            <footer className="p-5 border-t bg-gray-50 flex justify-end gap-3">
              <button disabled={generandoRecomendacionIA} onClick={() => setShowAcomaSuggestionModal(false)} className="px-4 py-2.5 border border-gray-300 rounded-lg text-gray-700 disabled:opacity-50">Cancelar</button>
              <button disabled={generandoRecomendacionIA || acomaFormInvalid()} onClick={() => void generarRecomendacionIA()} className="px-5 py-2.5 bg-violet-700 text-white rounded-lg font-semibold hover:bg-violet-800 disabled:opacity-50 flex items-center gap-2">{generandoRecomendacionIA ? <><i className="ri-loader-4-line animate-spin" />Generando propuesta…</> : <><i className={acomaForm.tipo_sugerencia === 'renovacion' ? 'ri-refresh-line' : 'ri-sparkling-2-line'} />{acomaForm.tipo_sugerencia === 'renovacion' ? 'Generar renovación' : 'Generar propuesta'}</>}</button>
            </footer>
          </div>
        </div>
      )}
      {showUpcomingSummary && (
        <div className="fixed inset-0 z-[100] bg-black/60 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl border border-gray-200 shadow-2xl w-full max-w-4xl max-h-[85vh] overflow-hidden flex flex-col">
            <header className="p-5 border-b border-gray-200 flex items-center justify-between gap-4">
              <div>
                <h2 className="text-xl font-bold text-gray-900">Próximas actividades</h2>
                <p className="text-sm text-gray-500">Cambios de rutina y evaluaciones pendientes.</p>
              </div>
              <button onClick={() => setShowUpcomingSummary(false)} className="w-10 h-10 rounded-lg text-gray-500 hover:bg-gray-100" aria-label="Cerrar">
                <i className="ri-close-line text-2xl"></i>
              </button>
            </header>
            <div className="p-5 overflow-y-auto grid grid-cols-1 lg:grid-cols-2 gap-5 items-start">
              <CoachUpcomingCard
                title="Próximos cambios de rutina"
                icon="ri-calendar-event-line"
                tone="amber"
                emptyText="No hay cambios de rutina programados."
                footer="Administrar usuarios"
                onFooter={() => { setShowUpcomingSummary(false); setShowUserSelector(true); }}
              >
                {cambiosRutinaProximos.map(({ usuario, fecha, dias }) => (
                  <button key={usuario.id} onClick={() => { selectUsuarioById(usuario.id); setShowUpcomingSummary(false); }} className="w-full flex items-center gap-3 px-4 py-3 border-t first:border-t-0 hover:bg-gray-50 text-left">
                    <span className="w-9 h-9 shrink-0 rounded-full bg-emerald-50 text-green-800 flex items-center justify-center font-bold text-xs">{usuario.avatar}</span>
                    <span className="flex-1 min-w-0 font-semibold text-sm text-gray-900 truncate">{usuario.nombre}</span>
                    <span className="text-xs text-gray-500 whitespace-nowrap">{fecha.toLocaleDateString('es-CL', { day: '2-digit', month: 'short' })}</span>
                    <span className={`text-xs rounded-full px-2 py-1 whitespace-nowrap ${dias < 0 ? 'bg-red-100 text-red-700' : dias === 0 ? 'bg-rose-100 text-rose-700' : dias <= 3 ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'}`}>{dias < 0 ? `Vencida ${Math.abs(dias)}d` : dias === 0 ? 'Hoy' : `${dias} días`}</span>
                  </button>
                ))}
              </CoachUpcomingCard>
              <CoachUpcomingCard
                title="Próximas evaluaciones"
                icon="ri-calendar-check-line"
                tone="purple"
                emptyText="No hay evaluaciones próximas."
                footer="Abrir agenda de evaluaciones"
                onFooter={() => { setShowUpcomingSummary(false); setShowEvaluacionesAgenda(true); }}
              >
                {evaluacionesProximas.map((cita) => {
                  const fecha = new Date(cita.fecha_inicio);
                  return <button key={cita.id} onClick={() => { selectUsuarioById(cita.usuario_id); setShowUpcomingSummary(false); }} className="w-full flex items-center gap-3 px-4 py-3 border-t first:border-t-0 hover:bg-gray-50 text-left">
                    <span className="w-9 h-9 shrink-0 rounded-full bg-purple-50 text-purple-700 flex items-center justify-center font-bold text-xs">{cita.usuario?.nombre?.charAt(0) || 'U'}</span>
                    <span className="flex-1 min-w-0 font-semibold text-sm text-gray-900 truncate">{cita.usuario?.nombre || 'Usuario'}</span>
                    <span className="text-xs text-gray-500 whitespace-nowrap">{fecha.toLocaleString('es-CL', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
                  </button>;
                })}
              </CoachUpcomingCard>
            </div>
          </div>
        </div>
      )}
      {showEvaluacionesAgenda && sucursalActiva?.id && (
        <EvaluacionesAgendaModal sucursalId={sucursalActiva.id} onClose={() => setShowEvaluacionesAgenda(false)} />
      )}
    </AdminLayout>
  );
}

function CoachUpcomingCard({
  title,
  icon,
  tone,
  emptyText,
  footer,
  onFooter,
  children,
}: {
  title: string;
  icon: string;
  tone: 'amber' | 'purple';
  emptyText: string;
  footer: string;
  onFooter: () => void;
  children: React.ReactNode;
}) {
  const hasItems = Array.isArray(children) ? children.length > 0 : !!children;
  const colors = tone === 'amber'
    ? 'bg-amber-50 text-amber-700'
    : 'bg-purple-50 text-purple-700';
  return (
    <section className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
      <header className="p-4 flex items-center gap-3 border-b">
        <span className={`w-10 h-10 rounded-full flex items-center justify-center ${colors}`}><i className={`${icon} text-xl`}></i></span>
        <h3 className="font-bold text-gray-900 flex-1">{title}</h3>
        <button onClick={onFooter} className="text-xs font-semibold text-gray-900 hover:underline">Ver todos →</button>
      </header>
      {hasItems ? <div>{children}</div> : <p className="p-6 text-sm text-gray-500 text-center">{emptyText}</p>}
      <div className="p-3 border-t"><button onClick={onFooter} className="w-full py-2 rounded-lg border border-gray-300 text-sm font-semibold text-gray-900 hover:bg-gray-50">{footer} →</button></div>
    </section>
  );
}


